import {
  ChamberPerksAppSubscriptionPlanTypesEnum,
  UserType,
} from "../graphql/types";
import { useMemo } from "react";
import { useLocales } from "../locales/LocalizationContext";

// DO NOT use any captial letters in any of the redirect URIs cuz it messes with the exact matches
export enum RedirectUri {
  errorGeneral = "/error",
  login = "/login",
  logout = "/logout",
  suspendedChamberMember = "/suspended",
  newPassword = "/newpassword",
  forgotPassword = "/forgotpassword",
  memberLogin = "/memberlogin",
  cpaMarketingToolkit = "/marketing",
  updateAChamberRoute = "/admin/chambers/update",
  listAllChamberAdmin = "/admin/chambers",
  createAChamber = "/admin/chambers/create",
  listAllPerkPartner = "/admin/perkpartners",
  createAPerkPartner = "/admin/perkpartners/create",
  editperkPartnerDiscountPageChamberAdmin = "/chambers/perkpartnerdiscounts/update",
  manageAMemberChamberAdmin = "/chambers/members",
  updateAMemberChamberAdmin = "/chambers/members/update",
  sendMemberCredentials = "/chambers/members/credentials",
  manageYourChamberChamberAdmin = "/chambers/update",
  createAMemberChamberAdmin = "/chambers/create",
  settingsOfChamberChamberAdmin = "/chambers/support",
  memberDiscountPageChamberAdmin = "/chambers/discounts",
  memberDiscountCreatePageChamberAdmin = "/chambers/discounts/create",
  editDiscountPageChamberAdmin = "/chambers/discounts/update",
  manageDiscountChamberMemberAdmin = "/chambers/members/discounts",
  createDiscountChamberMemberAdmin = "/chambers/members/discounts/create",
  editDiscountChamberMemberAdmin = "/chambers/members/discounts/update",
  settingsChamberMemberAdmin = "/chambers/members/support",
  manageFeaturedMemberBannersChamberMember = "/chambers/member/banners",
  rootAdmin = "/home",
  chamberAdmin = "/chamber/admin/home",
  chamberMemberAdmin = "/chamber/member/home",
  manageCompanyProfileChamberMemberAdmin = "/chamber/member/company/profile",
  memberInvoices = "/chamber/member/invoices",
  memberEvents = "/chamber/member/events",
  home = "/",
  deleteCpaData = "/deletecpadata",
  cpacreateperk = "/cpacreateperk", // This is specifically for the "Create Perk" button in app to route user properly to perk create screen
  perksLink = "/perksLink",
  chamberShopLocal = "/chamber/previousshoplocal",
  activeChamberShopLocal = "/chamber/activeshoplocal",
  createShopLocal = "/shoplocal/create",
  updateShopLocal = "/shoplocal/update",
  chamberMemberShopLocal = "/chamber/member/shoplocal",
  cpaPrivacyPolicy = "/cpaprivacypolicy",
  appStoreSupport = "/appstoresupport",
  rootManageChamberEngage = "/chamberengage",
  rootChamberEngageInvoicing = "/chamberengage/invoicing",
  chamberEngageManageMemberProfileAndPerkBanners = "/chamberengage/memberprofileandperkbanners",
  chamberEngageActivateFeaturedMember = "/chamberengage/featuredmember/activate",
  chamberEngageManageFeaturedMemberActivations = "/chamberengage/featuredmember/manage",
  chamberEngageManageCategoryAdvertisingBanners = "/chamberengage/categoryadvertisingbanners",
  chamberEngageActivateCategoryBanners = "/chamberengage/categorybanners/activate",
  chamberEngageManageCategoryBannersActivations = "/chamberengage/categorybanners/manage",
  chamberEngageAddMemberBanners = "/chamberengage/categorybanners/addmembers",
  chamberEngageManageCategoryAdvertisingBannersChamberMember = "/chamberengage/categoryadvertisingbanners/member",
  chamberReward = "/chamber/previousreward",
  activeChamberReward = "/chamber/activereward",
  createReward = "/reward/create",
  updateReward = "/reward/update",
  settings = "/settings",
  support = "/support",
  cpaMarketingToolkitFreshdesk = "/marketing/freshdesk",
  perkPartnersPortal = "/perkpartners/portal",
  perkPartnersActivateAffinity = "/perkpartners/portal/activate",
  perkPartnersManageActivations = "/perkpartners/portal/manage",
  perkPartnersUpdateAffinity = "/perkpartners/portal/update",
  perkPartnersViewStats = "/perkpartners/portal/stats",
}

const enumStrings = {
  supportContactEmail: "support@chamberperksapp.com",
  subscriptionsContactEmail: "subscriptions@chamberperksapp.com",
  sessionStorageKey: {
    loggedInWithTemporaryPassword: "loggedInWithTemporaryPassword",
  },
  localStorageKey: {
    userHasAccessedExternalKnowledgeBase:
      "userHasAccessedExternalKnowledgeBase",
  },
  modalMessage: {
    deleteConfirm: "Are you sure you want to delete?",
    doneConfirm: "Are you sure you want to mark as done?",
  },
  chamberModal: {
    downloadText: "Download",
    okText: "Ok",
    noText: "No",
    yesText: "Yes",
  },
  errorPageText: {
    title: "We could not load this page",
    text: "Select Go Home and try again. If it keeps happening, select Contact Support and tell us which page you tried to open.",
    homeButtonText: "Go Home",
    contactSupportButtonText: "Contact Support",
  },
  navText: {
    cpaNameText: "CPA",
    rootAdminText: "Root Dashboard",
    chamberAdminText: "Chamber Dashboard",
    boardAdminText: "Board Dashboard",
    chamberMemberAdminText: "Member Dashboard",
    perkPartnerAdminText: "Perk Partner Dashboard",
    perkPartnersPortalComingSoonTitle: "COMING SOON!",
    perkPartnersPortalComingSoonSubtitle:
      "Maximize affinity programs with the Affinity Program Partner Portal",
    perkPartnersPortalComingSoonDescription:
      "Showcase your affinity programs and exclusive offers in a dedicated partner portal, with built-in KPIs to track performance and engagement.",
  },
  categoriesText: {
    categoriesTitle: "Chamber Perks App™ Categories *",
  },
  redeemOfferText: {
    redeemOfferTitle: "Where to Redeem Perk",
    tooltip: {
      title:
        "Select from the 3 options below how users can view and redeem this perk. For more details click the “information icon” beside each option.",
      inPerson:
        "By selecting this option, Chamber Perks App™ users can only redeem this perk in person by showing the redemption screen on their phone at the point of purchase. This is a good option to drive more traffic to your company.",
      online:
        "By selecting this option, you are setting up a perk that can ONLY be redeemed online. After selecting this option, you must enter a discount code and your company e-commerce website URL. This is a good option to grow your e-commerce business.",
      contactCompany:
        "By selecting this option, you are setting up a perk that can ONLY be redeemed by contacting your company. After selecting this option, you must enter a department or contact name, contact email, and contact number. This is a good option for B2B businesses.",
    },
  },
  chamberShopLocal: {
    activeTitle: "View Active Shop Local Contest",
    activeDescription:
      "View prize draw entrants, participating member companies and update active Shop Local contest.",
    inactiveTitle: "View Previous Shop Local Contests",
    inactiveDescription: "",
    toastText: "Contest Created Successfully",
    updateToastText: "Contest Updated Successfully",
    label: {
      prizeDescription: "Contest Prize Description",
      campaignName: "Shop Local Contest Name",
    },
    tooltip: {
      campaignName:
        "Name of Shop Local Contest. This name will not be shown to Chamber Perks App™ users and is only shown in your Chamber Dashboard.",
      prizeDescription:
        "Description of prize that Chamber Perks App™ users can qualify to win by participating in the Shop Local Contest.",
      startDateUtcMilli:
        "Start date of this Shop Local Contest. This contest will automatically start at 12:00am on the selected date.",
      endDateUtcMilli:
        "End date of this Shop Local Contest. This contest will automatically end at 11:59pm on the selected date.",
      participatingMembers:
        'Select the members that are participating in this Shop Local Contest. Each particiapting member has a unique participation code for this contest. When a Chamber Perks App™ user redeems any perk offered by a participating member, the user can enter the member`s code to be entered into the prize draw. Members can also see their participation code from their Member Dashboard on the "Shop Local" page.',
    },
  },
  rewardCampaign: {
    activeTitle: "View Active Reward Campaign",
    activeDescription:
      "View prize draw entrants and update active Reward Campaign.",
    inactiveTitle: "View Previous Reward Campaigns",
    inactiveDescription: "",
    toastText: "Reward Campaign Created Successfully",
    updateToastText: "Reward Campaign Updated Successfully",
    label: {
      prizeDescription: "Reward Campaign Prize Description",
      campaignName: "Reward Campaign Name",
    },
    tooltip: {
      sponsoringMember:
        "Select your member that is sponsoring this Reward Campaign. This member will be the one providing the prize to the winner of this campaign. You can also select yourself as the sponsoring member.",
      campaignName:
        "Name of Reward Campaign. This name will not be shown to Chamber Perks App™ users and is only shown in your Chamber Dashboard.",
      prizeDescription:
        "Description of prize that Chamber Perks App™ users can earn a chance to win by participating in the Reward Campaign.\n\nThis verbiage will be shown as part of a larger description, please see below the text box for more details.",
      startDateUtcMilli:
        "Start date of this Reward Campaign. This reward campaign will automatically start at 12:00am on the selected date.",
      endDateUtcMilli:
        "End date of this Reward Campaign. This reward campaign will automatically end at 11:59pm on the selected date.",
    },
  },
  chamberAdminImportMembersText: {
    importCSVText: "You can import a csv file of members here. Please click",
    hereText: "here",
    toDownloadText:
      "to download a sample to see how you should format your file.",
    importMembersButtonText: "IMPORT MEMBERS",
  },
  chamberAdminListAllChambersText: {
    noChambersText: "No members available please create one",
    downloadAllMemberCredentials: {
      buttonLabel: "Download All Member Login Credentials",
      initialModal: {
        title: "Download All Member Login Credentials",
      },
    },
  },
  provinceDropDownSelect: {
    SelectProvinceText: "Select Province/State",
    dropdownOptions: [
      { full: "Alberta", code: "AB", country: "Canada" },
      { full: "British Columbia", code: "BC", country: "Canada" },
      { full: "Manitoba", code: "MB", country: "Canada" },
      { full: "New Brunswick", code: "NB", country: "Canada" },
      { full: "Newfoundland and Labrador", code: "NL", country: "Canada" },
      { full: "Northwest Territories", code: "NT", country: "Canada" },
      { full: "Nova Scotia", code: "NS", country: "Canada" },
      { full: "Nunavut", code: "NU", country: "Canada" },
      { full: "Ontario", code: "ON", country: "Canada" },
      { full: "Prince Edward Island", code: "PE", country: "Canada" },
      { full: "Quebec", code: "QC", country: "Canada" },
      { full: "Saskatchewan", code: "SK", country: "Canada" },
      { full: "Yukon", code: "YT", country: "Canada" },
      { full: "Alabama", code: "AL", country: "United States" },
      { full: "Alaska", code: "AK", country: "United States" },
      { full: "Arizona", code: "AZ", country: "United States" },
      { full: "Arkansas", code: "AR", country: "United States" },
      { full: "California", code: "CA", country: "United States" },
      { full: "Canal Zone", code: "CZ", country: "United States" },
      { full: "Colorado", code: "CO", country: "United States" },
      { full: "Connecticut", code: "CT", country: "United States" },
      { full: "Delaware", code: "DE", country: "United States" },
      { full: "District of Columbia", code: "DC", country: "United States" },
      { full: "Florida", code: "FL", country: "United States" },
      { full: "Georgia", code: "GA", country: "United States" },
      { full: "Guam", code: "GU", country: "United States" },
      { full: "Hawaii", code: "HI", country: "United States" },
      { full: "Idaho", code: "ID", country: "United States" },
      { full: "Illinois", code: "IL", country: "United States" },
      { full: "Indiana", code: "IN", country: "United States" },
      { full: "Iowa", code: "IA", country: "United States" },
      { full: "Kansas", code: "KS", country: "United States" },
      { full: "Kentucky", code: "KY", country: "United States" },
      { full: "Louisiana", code: "LA", country: "United States" },
      { full: "Maine", code: "ME", country: "United States" },
      { full: "Maryland", code: "MD", country: "United States" },
      { full: "Massachusetts", code: "MA", country: "United States" },
      { full: "Michigan", code: "MI", country: "United States" },
      { full: "Minnesota", code: "MN", country: "United States" },
      { full: "Mississippi", code: "MS", country: "United States" },
      { full: "Missouri", code: "MO", country: "United States" },
      { full: "Montana", code: "MT", country: "United States" },
      { full: "Nebraska", code: "NE", country: "United States" },
      { full: "Nevada", code: "NV", country: "United States" },
      { full: "New Hampshire", code: "NH", country: "United States" },
      { full: "New Jersey", code: "NJ", country: "United States" },
      { full: "New Mexico", code: "NM", country: "United States" },
      { full: "New York", code: "NY", country: "United States" },
      { full: "North Carolina", code: "NC", country: "United States" },
      { full: "North Dakota", code: "ND", country: "United States" },
      { full: "Ohio", code: "OH", country: "United States" },
      { full: "Oklahoma", code: "OK", country: "United States" },
      { full: "Oregon", code: "OR", country: "United States" },
      { full: "Pennsylvania", code: "PA", country: "United States" },
      { full: "Puerto Rico", code: "PR", country: "United States" },
      { full: "Rhode Island", code: "RI", country: "United States" },
      { full: "South Carolina", code: "SC", country: "United States" },
      { full: "South Dakota", code: "SD", country: "United States" },
      { full: "Tennessee", code: "TN", country: "United States" },
      { full: "Texas", code: "TX", country: "United States" },
      { full: "Utah", code: "UT", country: "United States" },
      { full: "Vermont", code: "VT", country: "United States" },
      { full: "Virgin Islands", code: "VI", country: "United States" },
      { full: "Virginia", code: "VA", country: "United States" },
      { full: "Washington", code: "WA", country: "United States" },
      { full: "West Virginia", code: "WV", country: "United States" },
      { full: "Wisconsin", code: "WI", country: "United States" },
      { full: "Wyoming", code: "WY", country: "United States" },
    ],
  },
  removeALogoText: "Delete Logo",
  deleteText: "Delete",
  resetPasswordText: "Reset Password",
  settingsText: {
    rootAdminText: "Root Admin Contact",
    sendFeedbackText: "Send Feedback",
  },
  bannerPicker: {
    minSizeRequirementForBanner:
      "An aspect ratio of 16:9 and a minimum size of 1280x720",
    imageLink:
      "The link that will be opened when this image is tapped in the Chamber Perks App™. Leave this empty if you do not want this image to be linked.",
  },
  loginInormationModal: {
    titles: {
      member: "New Member Created Successfully!",
    },
  },
  createChamberMemberText: {
    tooltip: {
      membershipStatus: {
        title:
          "Select this member's membership status. Use the information icons below for more detail about how each option effects this member perks and profile in the Chamber Perks App™. This status can be changed at any time.",
        active:
          "Choose this option if this member's membership status is active.\n\nThis option will also activate this member in the Chamber Perks App™, which means:\n* This member will be able to access their Member Dashboard to manage their profile and create new perks.\n* This member's profile will show in the Chamber Perks App™.\n* This member's perks will show in the Chamber Perks App™.",
        suspended:
          "Choose this option if this member's membership status is suspended due to unpaid dues or for any other reason.\n\nThis option will also suspend this member in the Chamber Perks App™, which means:\n* This member will not be able to access their Member Dashboard to manage their profile and create new perks.\n* This member's profile will not show in the Chamber Perks App™.\n* This member's perks will not show in the Chamber Perks App™.",
      },
      name: "Member company's name will appear to users on the Chamber Perks App™.",
      adminConsoleContactName:
        "The individual in this company who has the authority and responsibility for the company’s membership and who will receive the credentials for accessing the Chamber Perks App™ Member Dashboard.\n\nThis will not be shown in the Chamber Perks App™ and is only for internal chamber use.",
      adminConsoleLoginEmail:
        "The primary company contact email belongs to the primary company contact within your member company who will receive the member dashboard credentials to log in to their member dashboard. This individual will be able to share these credentials internally with other staff. Alternatively, to ensure a member company utilizes the Chamber Perks App™ fully, chambers should look to identify the following additional contacts listed below.\n\nThis will not be shown in the Chamber Perks App™ and is only for internal chamber use.",
      adminConsoleLoginEmailUpdateBlurb:
        "To change this, click 'Change Email Username'.",
      description:
        "Company information that will be shown on the Chamber Perks App™ to highlight your company to users. This information will also help users find your company through the Chamber Perks App™ search feature, so add as much detail as possible. You can include hours of operation, years in business, awards, and certifications, etc.\n\n(If left blank, this will not show on Chamber Perks App™)",
      contactName:
        "A company contact name that will be shown on the Chamber Perks App™ who will be responsible for company inquiries. This will allow users to connect directly with this person in your company.\n\n(If left blank, this will not show on Chamber Perks App™)",
      contactEmail:
        "The email for the above company contact that will be shown on the Chamber Perks App™, so users can easily connect. \n\n(If left blank, this will not show on Chamber Perks App™)",
      supportLink:
        "URL link of online support resource that will be shown to your member companies when they access the dashboard if they need support.",
      phoneNumber:
        "The main company phone number that will be shown on the Chamber Perks App™ for users to reach your company, or alternatively, this can be the phone number for the above company contact.\n\n(If left blank, this will not show on Chamber Perks App™)",
      website:
        "Company website URL that will be shown on the Chamber Perks App™ so users can connect directly to your company website.\n\n(If left blank, this will not show on Chamber Perks App™)",
      facebook:
        "Company Facebook Business Page URL that will be shown on the Chamber Perks App™ and will link users to your company's Facebook Business Page.\n\nHow to get this URL:\n1. Open your company's Facebook Business Page in a web browser\n2. Copy the URL from the address bar of your web browser\n3. Paste it in this input field (it should look like https://www.facebook.com/chamberperksapp)\n\n(If left blank, this will not show on Chamber Perks App™)",
      instagram:
        "Company Instagram account URL that will be shown on the Chamber Perks App™ and will link users to your company's Instagram account.\n\nHow to get this URL:\n1. Open your company's Instagram account in a web browser\n2. Copy the URL from the address bar of your web browser\n3. Paste it in this input field (it should look like https://www.instagram.com/chamberperksapp)\n\n(If left blank, this will not show on Chamber Perks App™)",
      threads:
        "Company Threads account URL that will be shown on the Chamber Perks App™ and will link users to your company's Threads account.\n\nHow to get this URL:\n1. Open your company's Threads account in a web browser\n2. Copy the URL from the address bar of your web browser\n3. Paste it in this input field (it should look like https://www.threads.net/@chamberperksapp)\n\n(If left blank, this will not show on Chamber Perks App™)",
      linkedin:
        "Company LinkedIn page URL that will be shown on the Chamber Perks App™ and will link users to your company's LinkedIn page.\n\nHow to get this URL:\n1. Open your company's LinkedIn page in a web browser\n2. Copy the URL from the address bar of your web browser\n3. Paste it in this input field (it should look like https://linkedin.com/company/chamberperksapp)\n\n(If left blank, this will not show on Chamber Perks App™)",
      twitter:
        "Company Twitter account URL that will show on the Chamber Perks App™ and will link users to your company's Twitter account.\n\nHow to get this URL:\n1. Open your company's Twitter account in a web browser\n2. Copy the URL from the address bar of your web browser\n3. Paste it in this input field (it should look like https://twitter.com/chamberperksapp)\n\n(If left blank, this will not show on Chamber Perks App™)",
      tiktok:
        "Company TikTok account URL that will be shown on the Chamber Perks App™ and will link users to your company's TikTok account.\n\nHow to get this URL:\n1. Open your company's TikTok account in a web browser\n2. Copy the URL from the address bar of your web browser\n3. Paste it in this input field (it should look like https://www.tiktok.com/chamberperksapp)\n\n(If left blank, this will not show on Chamber Perks App™)",
      youtube:
        "Company Youtube channel URL that will be shown on the Chamber Perks App™ and will link users to your company's Youtube channel.\n\nHow to get this URL:\n1. Open your company's Youtube channel in a web browser\n2. Copy the URL from the address bar of your web browser\n3. Paste it in this input field (it should look like https://www.youtube.com/@chamberperksapp)\n\n(If left blank, this will not show on Chamber Perks App™)",
      streetAddress:
        "Company street address for your company’s location that will be shown on the Chamber Perks App™. If any field is left blank, your company’s location will not appear on the map feature on the Chamber Perks App™.",
      province:
        "Company province/state for your company’s location that will be shown on the Chamber Perks App™. If any field is left blank, your company’s location will not appear on the map feature on the Chamber Perks App™.",
      city: "Company city for your company’s location that will be shown on the Chamber Perks App™. If any field is left blank, your company’s location will not appear on the map feature on the Chamber Perks App™.",
      postalCode:
        "Company postal/zip code for your company’s location that will be shown on the Chamber Perks App™. If any field is left blank, your company’s location will not appear on the map feature on the Chamber Perks App™.",
      categories:
        "Select from the dropdown up to 2 of the 15 categories listed that best represent your company. Your company will be shown under the selected categories on the Chamber Perks App™.",
      logo: "Ideally a square image. Formatted as png or jpg. Max 5mb.\n\nCompany logo that will be shown on the Chamber Perks App™. If not provided, the Chamber Perks App™ will use the first letter of your company’s name as the logo.",
      directoryScreenBanner: ({
        minSizeRequirementForBanner,
      }: {
        minSizeRequirementForBanner: string;
      }) =>
        `These banners will be shown on the main directory screen in Chamber Perks App™. This screen can be opened by tapping Directory on the app's bottom navigation bar.\n\nFor best quality of both videos and images, ${minSizeRequirementForBanner.charAt(0).toLowerCase() +
        minSizeRequirementForBanner.slice(1)
        } are recommended.\n\nThe position of each banner in Chamber Perks App™ will be the same as shown here.\n\nClick the right and left arrows under each banner to change its positioning.`,
      profileScreenBanner: ({
        chamberName,
        minSizeRequirementForBanner,
        isChamber,
      }: {
        chamberName: string;
        minSizeRequirementForBanner: string;
        isChamber: boolean;
      }) =>
        `These banners will be shown on the ${isChamber ? "Chamber" : "Board"
        } profile screen in the Chamber Perks App™. This screen can be opened by tapping ${chamberName}'s logo at the top of the app's home screen.\n\nFor best quality of both videos and images, ${minSizeRequirementForBanner.charAt(0).toLowerCase() +
        minSizeRequirementForBanner.slice(1)
        } are recommended.\n\nThe position of each banner in Chamber Perks App™ will be the same as shown here.\n\nClick the right and left arrows under each banner to change its positioning.`,
    },
    loginWithEmailText:
      "* This will also be used as the Member Dashboard email username for your company's admin",
  },
  disocuntText: {
    perkDisabledFromBadAvailability:
      "A notification will not be sent for this perk because the current time does not match the availability day(s) and/or month(s) selected above.",
    tooltip: {
      title:
        "Create a short, catchy phrase to attract users to view and redeem this perk. More details can be specified below in description.",
      description:
        "Add specific details about this perk. This information will help users find this perk through the Chamber Perks App™ search feature, so add as much detail as possible. Additional detail options are provided below.\n\n(If left blank, this will not show on Chamber Perks App™)",
      dayToggle:
        "Enabling this option allows you to restrict this perk to only show on certain days on the Chamber Perks App™. This is a good option for controlling when the perk can be viewed and redeemed.",
      monthToggle:
        "Enabling this option allows you to restrict this perk to only show during certain months on the Chamber Perks App™. This is a good option for controlling when the perk can be viewed and redeemed.\n\nIf you select only one month, you will have the ability to set a start and expiry date for this perk during that month.",
      startAndExpiryDate:
        "This perk will only show after 12am on the start date and automatically be deleted at 11:59pm on the expiry date. Only providing a start date is not a valid input.",
      notify: (chamberName: string, created: boolean) =>
        `Enabling this feature will notify all Chamber Perks App™ users who have signed up under the ${chamberName} that your company is offering a new perk. New perk notification will only be sent when a new perk is created.`,
    },
    availableToText: "Perk Redeemable By",
    redemptionOptionText: "Perk Availability",
    redemptionOptionTooltipText: {
      title:
        "Select how often this perk can be redeemed. Use the information icons below for more detail about each option. Remember, you can set up a unique perk for each option!",
      oneTime:
        "A one time perk can be redeemed only once by a Chamber Perks App™ user. This type of perk is good for attracting first-time customers or special sales events.",
      reoccurring:
        "A reoccurring perk can be redeemed multiple times by a Chamber Perks App™ user. This type of perk is good for building repeat customers and increasing customer loyalty and sales.",
    },
    offersAvailableonCertainDaysText: "Only Offer Perk on Certain Days",
    onlyShowDuringCertainMonthsText: "Only Offer Perk During Certain Months",
    notifyText: "Send New Perk Notification",
    privateEmails: {
      title: "How to Set Up",
      mainDesc: `Enter the email addresses your employees used to set up their personal Chamber Perks App™ user account in the text box below labelled "Employee Email List".`,
    },
    memberText: "Member Perk",
    discountUnavailableText: "No perks available please create one",
    viewsText: "Views: ",
    redemptionText: "Redemptions: ",
    offeredToText: "Redeemable By: ",
    createButtonText: "Create",
    offeredToPopup:
      "Select from the 4 options below who can view and redeem this perk on the Chamber Perks App™. For more details click the “information icon” beside each option. Remember you can always set up more than one perk.",
    generalPublicPopup:
      "By selecting this option, this perk can be viewed and redeemed by any users, using the app. This is a good way to increase your company’s exposure.",
    localChamberMembersPopup: (CHAMBER_NAME_HERE: string) =>
      `By selecting this option, you limit this perk to only be viewed and redeemed by ${CHAMBER_NAME_HERE} member companies and their employees.`,
    allChamberMembersPopup:
      "By selecting this option, this perk can only be viewed and redeemed by Chamber Perks App™ users employed by any of the participating Chambers of Commerce and Boards of Trade’s members using the app. This is a good way to increase your business exposure. (To see a list, tap the switch icon shown top right while on the home tab)",
    privatePopup:
      "By selecting this option, you are setting up a private employee perk that can only be viewed and redeemed by your employees who you allow. The employee must download the Chamber Perks App™, set up an account, and provide you with the email address they used to set up the account. More details are provided on How to Set Up once you select this option.",
    radioGroupText: {
      allChamberMembers: "All Members",
      localChamberMembers: "Local Members Only",
      generalPublic: "Anyone",
      private: "Your Company Employees Only",
    },
  },
  showInAppText: "Show in App",
  manageAChamberMemberText: {
    createDiscountText: "Create Perk",
  },
  rootAdminText: {
    amountOfDownloadsOnIOS: "Amount of Downloads on iOS",
    amountOfDownloadsOnAndroid: "Amount of Downloads on Android",
    membersInCPAText: "Total Chambers/Boards Subscribed",
    exportChamberLeads: "Export Missing City Leads",
    exportChamberMemberLeads: "Export Missing Employer Leads",
    chamberBoardLead: "City Name",
    memberCompanyLead: "Employer Name",
    creationTimeGmt: "Submission Time (GMT)",
    creationTimeLocalTZ: "Submission Time",
    viewStatBoxes: {
      chamberName: "Chamber/Board",
      numberOfUserAccounts: "Number of User Accounts",
      exportChamberLeads: "Missing City Leads",
      exportChamberMemberLeads: "Missing Employer Leads",
      membersInCPAText: "Total Chambers/Boards Subscribed",
      totalMemberCompanies: "Total Number of Member Companies",
      totalUsersSignedUp: "Total Number of User Accounts on Chamber Perks App™",
      totalPerks: "Total Number of Live Perks",
      numberOfLivePerksOfferedTo: "Number of Live Perks Redeemable By",
      chamberDetails: "Chamber/Board Details",
      perkPerMemberCategory: "Live Perks Per Member Category",
      perkPerMemberCategorySubTitle:
        "Number of live perks may seem higher than expected because one perk can belong to 2 categories",
      categoryName: "Category",
      numberOfPerks: "Live Perks",
      percentOfPerks: "% Live Perks",
      chamberPerksPerCategory: "Chamber/Board Perks Per Category",
    },
  },
  rootAdminChamberText: {
    createPerkToSeeChart: "Create a perk to see this chart",
    usersSignedUp: (chamberName: string) =>
      `Chamber Perks App™ User Accounts Under ${chamberName}`,
    usersSignedupWithText: "Total Number of Members on the Chamber Perks App™",
    numberOfBusinessesOfferingPerks: "Number of Members Offering Perks",
    numberOfTotalPerks: (chamberName: string) =>
      `Total Number of Perks Under ${chamberName}`,
    numberOfLivePerksOfferedTo: "Breakdown of Perks Offered",
    amountOfDiscountsRedeemedText: "Total number of perk redemptions",
    perksOfferedByYourMemebrs: "Perks Offered by\nYour Members",
  },
  chamberMemberAdminRootText: {
    uniqueViewsOnYourListingText: "Unique Views on Your Listing",
  },
  forgotPasswordText: {
    pleaseEnterEmailText:
      "Please enter your email username, and a code will be sent to reset your password. If you are not the owner of this email, please contact the owner to retrieve the code.",
    goBacktoLoginText: "Go Back to Login",
  },
  loginText: {
    forgotPasswordText: "Forgot Password",
  },
  newPasswordText: {
    pleaseEnterNewPasswordText: "Please enter a new password for your account",
    changePasswordText: "Change Password",
  },
  resetPassword: {
    pleaseVerifyEmailText: (email: string) =>
      `Please enter the verification code sent to ${email}, along with your new password`,
    changePasswordText: "Change Password",
    resetPasswordSuccessText:
      "Your Password has successfully been reset, please click below to return to the login page",
    returnToLoginText: "Return to Login",
  },

  createChamberText: {
    supportContactemailText:
      "* This will also be used as the Chamber Dashboard email username for the chamber's admin",
    tooltip: {
      name: "This is how your chamber/board’s name will appear to users on the Chamber Perks App™.",
      description:
        "Chamber/Board information that will be shown on the Chamber Perks App™ to highlight your chamber/board to users.",
      supportContactName:
        "A chamber/board contact name that will be shown on the Chamber Perks App™ who will be responsible for chamber/board inquiries. This will allow users to connect directly with this person in your chamber/board.",
      supportContactEmail:
        "The email for the above chamber/board contact that will be shown on the Chamber Perks App™, so users can easily connect.",
      supportLink:
        "URL link of your online support resource that will be shown to your member companies when they access the dashboard if they need support.",
      phoneNumber:
        "The main chamber/board phone number that will be shown on the Chamber Perks App™ for users to reach your chamber/board, or alternatively, this can be the phone number for the above chamber/board contact.",
      website:
        "Chamber/Board website URL that will be shown on the Chamber Perks App™ so users can connect directly to your chamber/board website.",
      facebook:
        "Chamber/Board Facebook Business Page URL that will be shown on the Chamber Perks App™ and will link users to your chamber/board's Facebook Business Page.\n\nHow to get this URL:\n1. Open your chamber/board's Facebook Business Page in a web browser\n2. Copy the URL from the address bar of your web browser\n3. Paste it in this input field (it should look like https://www.facebook.com/chamberperksapp)\n\n(If left blank, this will not show on Chamber Perks App™)",
      instagram:
        "Chamber/Board Instagram account URL that will be shown on the Chamber Perks App™ and will link users to your chamber/board's Instagram account.\n\nHow to get this URL:\n1. Open your chamber/board's Instagram account in a web browser\n2. Copy the URL from the address bar of your web browser\n3. Paste it in this input field (it should look like https://www.instagram.com/chamberperksapp)\n\n(If left blank, this will not show on Chamber Perks App™)",
      threads:
        "Chamber/Board Threads account URL that will be shown on the Chamber Perks App™ and will link users to your chamber/board's Threads account.\n\nHow to get this URL:\n1. Open your chamber/board's Threads account in a web browser\n2. Copy the URL from the address bar of your web browser\n3. Paste it in this input field (it should look like https://www.threads.net/@chamberperksapp)\n\n(If left blank, this will not show on Chamber Perks App™)",
      linkedin:
        "Chamber/Board LinkedIn page URL that will be shown on the Chamber Perks App™ and will link users to your chamber/board's LinkedIn page.\n\nHow to get this URL:\n1. Open your chamber/board's LinkedIn page in a web browser\n2. Copy the URL from the address bar of your web browser\n3. Paste it in this input field (it should look like https://linkedin.com/chamber/company/chamberperksapp)\n\n(If left blank, this will not show on Chamber Perks App™)",
      twitter:
        "Chamber/Board Twitter account URL that will show on the Chamber Perks App™ and will link users to your chamber/board's Twitter account.\n\nHow to get this URL:\n1. Open your chamber/board's Twitter account in a web browser\n2. Copy the URL from the address bar of your web browser\n3. Paste it in this input field (it should look like https://twitter.com/chamberperksapp)\n\n(If left blank, this will not show on Chamber Perks App™)",
      tiktok:
        "Chamber/Board TikTok account URL that will be shown on the Chamber Perks App™ and will link users to your chamber/board's TikTok account.\n\nHow to get this URL:\n1. Open your chamber/board's TikTok account in a web browser\n2. Copy the URL from the address bar of your web browser\n3. Paste it in this input field (it should look like https://www.tiktok.com/chamberperksapp)\n\n(If left blank, this will not show on Chamber Perks App™)",
      youtube:
        "Chamber/Board Youtube channel URL that will be shown on the Chamber Perks App™ and will link users to your chamber/board's Youtube channel.\n\nHow to get this URL:\n1. Open your chamber/board's Youtube channel in a web browser\n2. Copy the URL from the address bar of your web browser\n3. Paste it in this input field (it should look like https://www.youtube.com/@chamberperksapp)\n\n(If left blank, this will not show on Chamber Perks App™)",
      streetAddress:
        "Chamber/Board street address for your chamber/board’s location that will be shown on the Chamber Perks App™.",
      province:
        "Chamber/Board province/state for your chamber/board’s location that will be shown on the Chamber Perks App™.",
      city: "Chamber/Board city for your chamber/board’s location that will be shown on the Chamber Perks App™.",
      postalCode:
        "Chamber/Board postal/zip code for your chamber/board’s location that will be shown on the Chamber Perks App™.",
      logo: "Ideally a square image. Formatted as png or jpg. Max 5mb.\n\nChamber/Board logo that will be shown on the Chamber Perks App™. If not provided, the Chamber Perks App™ will use the first letter of your chamber/board’s name as the logo.",
      sponsorLogo: ({ isChamber }: { isChamber: boolean }) =>
        `Ideally a square image. Formatted as png or jpg. Max 5mb.\n\nThis is your sponsor's logo that will be shown in the following locations:\n* The navigation menu on your Dashboard and in each of your member's Dashboard.\n* The Directory screen on the Chamber Perks App™.\n* The ${isChamber ? "Chamber" : "Board"
        } Profile screen on the Chamber Perks App™.`,
      sponsorLogoLink:
        "The link that will be opened when your sponsor's logo is clicked in your Dashboard and in each of your member's Dashboard, or tapped in the Chamber Perks App™. Leave this empty if you do not want the sponsor's logo to be linked.",
      jobPostingLink:
        "Chamber/Board job board URL that will show on the Chamber Perks App™ (If left blank, this will not show on Chamber Perks App™)",
      eventsLink:
        "Chamber/Board events calendar URL that will show on the Chamber Perks App™ (If left blank, this will not show on Chamber Perks App™)",
      newsletterLink:
        "Chamber/Board newsletter URL that will show on the Chamber Perks App™ (If left blank, this will not show on Chamber Perks App™)",
      surveyLink:
        "Chamber/Board survey URL that will show on the Chamber Perks App™ (If left blank, this will not show on Chamber Perks App™)",
      advocacyWinsLink:
        "Chamber/Board advocacy wins and information URL that will show on the Chamber Perks App™ (If left blank, this will not show on Chamber Perks App™)",
      memberSignupLink:
        "Chamber/Board new member signup URL that will show on the Chamber Perks App™ (If left blank, this will not show on Chamber Perks App™)",
      premiumChamberMarketer:
        "Premium chamber marketer dropbox URL (If left blank, the chamber will only be shown the basic chamber marketer in their dashboard)",
    },
  },

  loadingText: "LOADING...",
  offersPerkText: "Offers Perk",
  chamberDeletedBadgeText: "Deleted",
  chamberCpaBadgeText: "CPA",
  chamberCmpBadgeText: "CMP",
  toastPerkText: "Perk Created",
  toastPerkUpdatedText: "Perk Updated",
  perkTitleText: "Perk Title is required",
  addressSubText:
    "If this is left empty, location will not show on map in Chamber Perks App™",
  adminConsoleLoginEmailSubText: "* Set while creating and cannot be changed",
  chamberEngage: {
    navBar: {
      categoryAdvertising: {
        popupInfo: {
          active: ({
            chamberName,
            supportContactEmail,
          }: {
            chamberName: string;
            supportContactEmail: string;
          }) =>
            `Category advertising is active! Start adding image or video banners to elevate your company's appearance and attract more business. Contact ${chamberName} at ${supportContactEmail} to learn more!`,
          inactive: ({
            chamberName,
            supportContactEmail,
          }: {
            chamberName: string;
            supportContactEmail: string;
          }) =>
            `Category advertising is inactive. If you're interested in advertising in a category on the Chamber Perks App™ and uploading image or video banners to elevate your company's appearance and attract more business, contact ${chamberName} at ${supportContactEmail} to learn more!`,
        },
      },
    },
  },
};

export const useEnumStrings = () => {
  const { t } = useLocales();

  return useMemo(
    () => {
      const strings = {
        supportContactEmail: t("supportContactEmail"),
        subscriptionsContactEmail: t("subscriptionsContactEmail"),
        navNewStuff: t("navNewStuff"),
        navLearningMarketing: t("navLearningMarketing"),
        NonDuesRevenuePlatform: t("NonDuesRevenuePlatform"),
        chamber: t("chamber"),
        board: t("board"),
        for: t("for"),
        watchVideoToLearnHow: t("watchVideoToLearnHow"),
        daysOfWeek: {
          SUNDAY: t("daysOfWeek.SUNDAY"),
          MONDAY: t("daysOfWeek.MONDAY"),
          TUESDAY: t("daysOfWeek.TUESDAY"),
          WEDNESDAY: t("daysOfWeek.WEDNESDAY"),
          THURSDAY: t("daysOfWeek.THURSDAY"),
          FRIDAY: t("daysOfWeek.FRIDAY"),
          SATURDAY: t("daysOfWeek.SATURDAY"),
        },
        monthsOfYear: {
          JANUARY: t("monthsOfYear.JANUARY"),
          FEBRUARY: t("monthsOfYear.FEBRUARY"),
          MARCH: t("monthsOfYear.MARCH"),
          APRIL: t("monthsOfYear.APRIL"),
          MAY: t("monthsOfYear.MAY"),
          JUNE: t("monthsOfYear.JUNE"),
          JULY: t("monthsOfYear.JULY"),
          AUGUST: t("monthsOfYear.AUGUST"),
          SEPTEMBER: t("monthsOfYear.SEPTEMBER"),
          OCTOBER: t("monthsOfYear.OCTOBER"),
          NOVEMBER: t("monthsOfYear.NOVEMBER"),
          DECEMBER: t("monthsOfYear.DECEMBER"),
        },
        monthsOfYearAbbreviated: {
          jan: t("monthsOfYearAbbreviated.jan"),
          feb: t("monthsOfYearAbbreviated.feb"),
          mar: t("monthsOfYearAbbreviated.mar"),
          apr: t("monthsOfYearAbbreviated.apr"),
          may: t("monthsOfYearAbbreviated.may"),
          jun: t("monthsOfYearAbbreviated.jun"),
          jul: t("monthsOfYearAbbreviated.jul"),
          aug: t("monthsOfYearAbbreviated.aug"),
          sep: t("monthsOfYearAbbreviated.sep"),
          oct: t("monthsOfYearAbbreviated.oct"),
          nov: t("monthsOfYearAbbreviated.nov"),
          dec: t("monthsOfYearAbbreviated.dec"),
        },
        localStorageKey: {
          userHasAccessedExternalKnowledgeBase:
            "userHasAccessedExternalKnowledgeBase",
        },
        sessionStorageKey: {
          loggedInWithTemporaryPassword: t(
            "sessionStorageKey.loggedInWithTemporaryPassword"
          ),
        },
        modalMessage: {
          deleteConfirm: t("modalMessage.deleteConfirm"),
          doneConfirm: t("modalMessage.doneConfirm"),
        },
        chamberModal: {
          downloadText: t("chamberModal.downloadText"),
          okText: t("chamberModal.okText"),
          noText: t("chamberModal.noText"),
          yesText: t("chamberModal.yesText"),
        },
        userFacingErrors: {
          network: t("userFacingErrors.network"),
          actionFailedMember: t("userFacingErrors.actionFailedMember"),
          actionFailedStaff: t("userFacingErrors.actionFailedStaff"),
          saveFailedMember: t("userFacingErrors.saveFailedMember"),
          saveFailedStaff: t("userFacingErrors.saveFailedStaff"),
          memberSaveFailedStaff: t("userFacingErrors.memberSaveFailedStaff"),
          deleteChamberFailedStaff: t("userFacingErrors.deleteChamberFailedStaff"),
          loginEmailInUse: t("userFacingErrors.loginEmailInUse"),
          loginEmailInUseMember: (chamberName: string) => t("userFacingErrors.loginEmailInUseMember", { chamberName }),
          loginEmailChangeFailed: t("userFacingErrors.loginEmailChangeFailed"),
          yourChamber: t("userFacingErrors.yourChamber"),
          close: t("userFacingErrors.close"),
          profileSaveFailedMember: (chamberName: string) => t("userFacingErrors.profileSaveFailedMember", { chamberName }),
          ownLoginEmailMember: (chamberName: string) => t("userFacingErrors.ownLoginEmailMember", { chamberName }),
          perkCreateFailedMember: (button: string, chamberName: string) =>
            t("userFacingErrors.perkCreateFailedMember", { button, chamberName }),
          perkCreateFailedStaff: (button: string) => t("userFacingErrors.perkCreateFailedStaff", { button }),
          perkUpdateFailedMember: (button: string, chamberName: string) =>
            t("userFacingErrors.perkUpdateFailedMember", { button, chamberName }),
          perkUpdateFailedStaff: (button: string) => t("userFacingErrors.perkUpdateFailedStaff", { button }),
          tryAgainStaff: t("userFacingErrors.tryAgainStaff"),
          credentialsCreateFailedStaff: t("userFacingErrors.credentialsCreateFailedStaff"),
          bannersSaveFailedMember: t("userFacingErrors.bannersSaveFailedMember"),
          dashboardTurnedOff: t("userFacingErrors.dashboardTurnedOff"),
          dashboardTurnOnEmail: (chamberName: string) =>
            t("userFacingErrors.dashboardTurnOnEmail", { chamberName }),
          dashboardTurnOn: (chamberName: string) => t("userFacingErrors.dashboardTurnOn", { chamberName }),
          pageNotAllowed: (chamberName: string) => t("userFacingErrors.pageNotAllowed", { chamberName }),
          invoicesOnlyLogin: (chamberName: string) => t("userFacingErrors.invoicesOnlyLogin", { chamberName }),
          perksNotInPlan: t("userFacingErrors.perksNotInPlan"),
          nothingToShow: t("userFacingErrors.nothingToShow"),
          loadingPleaseWait: t("userFacingErrors.loadingPleaseWait"),
        },
        errorPageText: {
          title: t("errorPageText.title"),
          text: t("errorPageText.text"),
          homeButtonText: t("errorPageText.homeButtonText"),
          contactSupportButtonText: t("errorPageText.contactSupportButtonText"),
        },
        navText: {
          cpaNameText: t("navText.cpaNameText"),
          rootAdminText: t("navText.rootAdminText"),
          chamberAdminText: t("navText.chamberAdminText"),
          boardAdminText: t("navText.boardAdminText"),
          chamberMemberAdminText: t("navText.chamberMemberAdminText"),
          perkPartnerAdminText: t("navText.perkPartnerAdminText"),
          perkPartnersPortalComingSoonTitle: t(
            "navText.perkPartnersPortalComingSoonTitle"
          ),
          perkPartnersPortalComingSoonSubtitle: t(
            "navText.perkPartnersPortalComingSoonSubtitle"
          ),
          perkPartnersPortalComingSoonDescription: t(
            "navText.perkPartnersPortalComingSoonDescription"
          ),
        },
        categoriesText: {
          categoriesTitle: t("categoriesText.categoriesTitle"),
        },
        redeemOfferText: {
          redeemOfferTitle: t("redeemOfferText.redeemOfferTitle"),
          tooltip: {
            title: t("redeemOfferText.tooltip.title"),
            inPerson: t("redeemOfferText.tooltip.inPerson"),
            online: t("redeemOfferText.tooltip.online"),
            contactCompany: t("redeemOfferText.tooltip.contactCompany"),
          },
        },
        chamberShopLocal: {
          activeTitle: t("chamberShopLocal.activeTitle"),
          activeDescription: t("chamberShopLocal.activeDescription"),
          inactiveTitle: t("chamberShopLocal.inactiveTitle"),
          inactiveDescription: t("chamberShopLocal.inactiveDescription"),
          toastText: t("chamberShopLocal.toastText"),
          updateToastText: t("chamberShopLocal.updateToastText"),
          label: {
            prizeDescription: t("chamberShopLocal.label.prizeDescription"),
            campaignName: t("chamberShopLocal.label.campaignName"),
            campaignDates: t("chamberShopLocal.label.campaignDates"),
            startDate: t("chamberShopLocal.label.startDate"),
            endDate: t("chamberShopLocal.label.endDate"),
            participatingMembers: t("chamberShopLocal.label.participatingMembers"),
          },
          tooltip: {
            campaignName: t("chamberShopLocal.tooltip.campaignName"),
            prizeDescription: t("chamberShopLocal.tooltip.prizeDescription"),
            startDateUtcMilli: t("chamberShopLocal.tooltip.startDateUtcMilli"),
            endDateUtcMilli: t("chamberShopLocal.tooltip.endDateUtcMilli"),
            participatingMembers: t(
              "chamberShopLocal.tooltip.participatingMembers"
            ),
          },
        },
        rewardCampaign: {
          activeTitle: t("rewardCampaign.activeTitle"),
          activeDescription: t("rewardCampaign.activeDescription"),
          inactiveTitle: t("rewardCampaign.inactiveTitle"),
          inactiveDescription: t("rewardCampaign.inactiveDescription"),
          toastText: t("rewardCampaign.toastText"),
          updateToastText: t("rewardCampaign.updateToastText"),
          unableToUpdate: t("rewardCampaign.unableToUpdate"),
          UpdateError: t("rewardCampaign.UpdateError"),
          label: {
            prizeDescription: t("rewardCampaign.label.prizeDescription"),
            campaignName: t("rewardCampaign.label.campaignName"),
            campaignDates: t("rewardCampaign.label.campaignDates"),
            startDate: t("rewardCampaign.label.startDate"),
            endDate: t("rewardCampaign.label.endDate"),
          },
          tooltip: {
            sponsoringMember: t("rewardCampaign.tooltip.sponsoringMember"),
            campaignName: t("rewardCampaign.tooltip.campaignName"),
            prizeDescription: t("rewardCampaign.tooltip.prizeDescription"),
            startDateUtcMilli: t("rewardCampaign.tooltip.startDateUtcMilli"),
            endDateUtcMilli: t("rewardCampaign.tooltip.endDateUtcMilli"),
          },
          close: t("rewardCampaign.close"),
        },
        chamberAdminImportMembersText: {
          importCSVText: t("chamberAdminImportMembersText.importCSVText"),
          hereText: t("chamberAdminImportMembersText.hereText"),
          toDownloadText: t("chamberAdminImportMembersText.toDownloadText"),
          importMembersButtonText: t(
            "chamberAdminImportMembersText.importMembersButtonText"
          ),
        },
        chamberAdminListAllChambersText: {
          noChambersText: t("chamberAdminListAllChambersText.noChambersText"),
          downloadAllMemberCredentials: {
            buttonLabel: t(
              "chamberAdminListAllChambersText.downloadAllMemberCredentials.buttonLabel"
            ),
            initialModal: {
              title: t(
                "chamberAdminListAllChambersText.downloadAllMemberCredentials.initialModal.title"
              ),
            },
          },
        },
        provinceDropDownSelect: {
          SelectProvinceText: t("provinceDropDownSelect.SelectProvinceText"),
          dropdownOptions: [
            { full: "Alberta", code: "AB", country: "Canada" },
            { full: "British Columbia", code: "BC", country: "Canada" },
            { full: "Manitoba", code: "MB", country: "Canada" },
            { full: "New Brunswick", code: "NB", country: "Canada" },
            { full: "Newfoundland and Labrador", code: "NL", country: "Canada" },
            { full: "Northwest Territories", code: "NT", country: "Canada" },
            { full: "Nova Scotia", code: "NS", country: "Canada" },
            { full: "Nunavut", code: "NU", country: "Canada" },
            { full: "Ontario", code: "ON", country: "Canada" },
            { full: "Prince Edward Island", code: "PE", country: "Canada" },
            { full: "Quebec", code: "QC", country: "Canada" },
            { full: "Saskatchewan", code: "SK", country: "Canada" },
            { full: "Yukon", code: "YT", country: "Canada" },
            { full: "Alabama", code: "AL", country: "United States" },
            { full: "Alaska", code: "AK", country: "United States" },
            { full: "Arizona", code: "AZ", country: "United States" },
            { full: "Arkansas", code: "AR", country: "United States" },
            { full: "California", code: "CA", country: "United States" },
            { full: "Canal Zone", code: "CZ", country: "United States" },
            { full: "Colorado", code: "CO", country: "United States" },
            { full: "Connecticut", code: "CT", country: "United States" },
            { full: "Delaware", code: "DE", country: "United States" },
            {
              full: "District of Columbia",
              code: "DC",
              country: "United States",
            },
            { full: "Florida", code: "FL", country: "United States" },
            { full: "Georgia", code: "GA", country: "United States" },
            { full: "Guam", code: "GU", country: "United States" },
            { full: "Hawaii", code: "HI", country: "United States" },
            { full: "Idaho", code: "ID", country: "United States" },
            { full: "Illinois", code: "IL", country: "United States" },
            { full: "Indiana", code: "IN", country: "United States" },
            { full: "Iowa", code: "IA", country: "United States" },
            { full: "Kansas", code: "KS", country: "United States" },
            { full: "Kentucky", code: "KY", country: "United States" },
            { full: "Louisiana", code: "LA", country: "United States" },
            { full: "Maine", code: "ME", country: "United States" },
            { full: "Maryland", code: "MD", country: "United States" },
            { full: "Massachusetts", code: "MA", country: "United States" },
            { full: "Michigan", code: "MI", country: "United States" },
            { full: "Minnesota", code: "MN", country: "United States" },
            { full: "Mississippi", code: "MS", country: "United States" },
            { full: "Missouri", code: "MO", country: "United States" },
            { full: "Montana", code: "MT", country: "United States" },
            { full: "Nebraska", code: "NE", country: "United States" },
            { full: "Nevada", code: "NV", country: "United States" },
            { full: "New Hampshire", code: "NH", country: "United States" },
            { full: "New Jersey", code: "NJ", country: "United States" },
            { full: "New Mexico", code: "NM", country: "United States" },
            { full: "New York", code: "NY", country: "United States" },
            { full: "North Carolina", code: "NC", country: "United States" },
            { full: "North Dakota", code: "ND", country: "United States" },
            { full: "Ohio", code: "OH", country: "United States" },
            { full: "Oklahoma", code: "OK", country: "United States" },
            { full: "Oregon", code: "OR", country: "United States" },
            { full: "Pennsylvania", code: "PA", country: "United States" },
            { full: "Puerto Rico", code: "PR", country: "United States" },
            { full: "Rhode Island", code: "RI", country: "United States" },
            { full: "South Carolina", code: "SC", country: "United States" },
            { full: "South Dakota", code: "SD", country: "United States" },
            { full: "Tennessee", code: "TN", country: "United States" },
            { full: "Texas", code: "TX", country: "United States" },
            { full: "Utah", code: "UT", country: "United States" },
            { full: "Vermont", code: "VT", country: "United States" },
            { full: "Virgin Islands", code: "VI", country: "United States" },
            { full: "Virginia", code: "VA", country: "United States" },
            { full: "Washington", code: "WA", country: "United States" },
            { full: "West Virginia", code: "WV", country: "United States" },
            { full: "Wisconsin", code: "WI", country: "United States" },
            { full: "Wyoming", code: "WY", country: "United States" },
          ],
        },
        removeALogoText: t("removeALogoText"),
        deleteText: t("deleteText"),
        searchText: t("searchText"),
        resetPasswordText: t("resetPasswordText"),
        settingsText: {
          rootAdminText: t("settingsText.rootAdminText"),
          sendFeedbackText: t("settingsText.sendFeedbackText"),
        },
        bannerPicker: {
          banner: t("bannerPicker.banner"),
          minSizeRequirementForBanner: t(
            "bannerPicker.minSizeRequirementForBanner"
          ),
          imageLink: t("bannerPicker.imageLink"),
        },
        loginInormationModal: {
          titles: {
            member: t("loginInormationModal.titles.member"),
          },
        },
        createChamberMemberText: {
          tooltip: {
            membershipStatus: {
              title: t("createChamberMemberText.tooltip.membershipStatus.title"),
              active: t(
                "createChamberMemberText.tooltip.membershipStatus.active"
              ),
              suspended: t(
                "createChamberMemberText.tooltip.membershipStatus.suspended"
              ),
            },
            name: t("createChamberMemberText.tooltip.name"),
            adminConsoleContactName: t(
              "createChamberMemberText.tooltip.adminConsoleContactName"
            ),
            adminConsoleLoginEmail: t(
              "createChamberMemberText.tooltip.adminConsoleLoginEmail"
            ),
            adminConsoleLoginEmailUpdateBlurb: t(
              "createChamberMemberText.tooltip.adminConsoleLoginEmailUpdateBlurb"
            ),
            description: t("createChamberMemberText.tooltip.description"),
            contactName: t("createChamberMemberText.tooltip.contactName"),
            contactEmail: t("createChamberMemberText.tooltip.contactEmail"),
            supportLink: t("createChamberMemberText.tooltip.supportLink"),
            phoneNumber: t("createChamberMemberText.tooltip.phoneNumber"),
            website: t("createChamberMemberText.tooltip.website"),
            facebook: t("createChamberMemberText.tooltip.facebook"),
            instagram: t("createChamberMemberText.tooltip.instagram"),
            threads: t("createChamberMemberText.tooltip.threads"),
            linkedin: t("createChamberMemberText.tooltip.linkedin"),
            twitter: t("createChamberMemberText.tooltip.twitter"),
            tiktok: t("createChamberMemberText.tooltip.tiktok"),
            youtube: t("createChamberMemberText.tooltip.youtube"),
            googleReviewLink: t(
              "createChamberMemberText.tooltip.googleReviewLink"
            ),
            streetAddress: t("createChamberMemberText.tooltip.streetAddress"),
            province: t("createChamberMemberText.tooltip.province"),
            city: t("createChamberMemberText.tooltip.city"),
            postalCode: t("createChamberMemberText.tooltip.postalCode"),
            categories: t("createChamberMemberText.tooltip.categories"),
            logo: t("createChamberMemberText.tooltip.logo"),
            directoryScreenBanner: ({
              minSizeRequirementForBanner,
            }: {
              minSizeRequirementForBanner: string;
            }) =>
              `${t(
                "createChamberMemberText.tooltip.directoryScreenBannerStart"
              )} ${minSizeRequirementForBanner} ${t(
                "createChamberMemberText.tooltip.directoryScreenBannerEnd"
              )}`,
            profileScreenBanner: ({
              chamberName,
              minSizeRequirementForBanner,
              isChamber,
            }: {
              chamberName: string;
              minSizeRequirementForBanner: string;
              isChamber: boolean;
            }) =>
              `${t("createChamberMemberText.tooltip.profileScreenBannerStart")} ${isChamber
                ? t("createChamberMemberText.tooltip.chamber")
                : t("createChamberMemberText.tooltip.board")
              } ${t(
                "createChamberMemberText.tooltip.profileScreenBannerMiddle"
              )} ${minSizeRequirementForBanner} ${t(
                "createChamberMemberText.tooltip.directoryScreenBannerEnd"
              )}`,
          },
          loginWithEmailText: t("createChamberMemberText.loginWithEmailText"),
        },
        discountText: {
          perkDisabledFromBadAvailability: t(
            "discountText.perkDisabledFromBadAvailability"
          ),
          tooltip: {
            title: t("discountText.tooltip.title"),
            description: t("discountText.tooltip.description"),
            dayToggle: t("discountText.tooltip.dayToggle"),
            monthToggle: t("discountText.tooltip.monthToggle"),
            startAndExpiryDate: t("discountText.tooltip.startAndExpiryDate"),
            notify: (chamberName: string, created: boolean) =>
              `${t("discountText.tooltip.notifyStart")} ${chamberName} ${t(
                "discountText.tooltip.notifyEnd"
              )}`,
            notifyStart: t("discountText.tooltip.notifyStart"),
            notifyEnd: t("discountText.tooltip.notifyEnd"),
          },
          availableToText: t("discountText.availableToText"),
          redemptionOptionText: t("discountText.redemptionOptionText"),
          redemptionOptionTooltipText: {
            title: t("discountText.redemptionOptionTooltipText.title"),
            oneTime: t("discountText.redemptionOptionTooltipText.oneTime"),
            reoccurring: t(
              "discountText.redemptionOptionTooltipText.reoccurring"
            ),
          },
          offersAvailableonCertainDaysText: t(
            "discountText.offersAvailableonCertainDaysText"
          ),
          onlyShowDuringCertainMonthsText: t(
            "discountText.onlyShowDuringCertainMonthsText"
          ),
          notifyText: t("discountText.notifyText"),
          privateEmails: {
            title: t("discountText.privateEmails.title"),
            mainDesc: t("discountText.privateEmails.mainDesc"),
          },
          memberText: t("discountText.memberText"),
          discountUnavailableText: t("discountText.discountUnavailableText"),
          viewsText: t("discountText.viewsText"),
          redemptionText: t("discountText.redemptionText"),
          offeredToText: t("discountText.offeredToText"),
          createButtonText: t("discountText.createButtonText"),
          offeredToPopup: t("discountText.offeredToPopup"),
          generalPublicPopup: t("discountText.generalPublicPopup"),
          localChamberMembersPopup: (CHAMBER_NAME_HERE: string) =>
            `${t(
              "discountText.localChamberMembersPopup"
            )} ${CHAMBER_NAME_HERE} ${t(
              "discountText.localChamberMembersPopupEnd"
            )}`,
          allChamberMembersPopup: t("discountText.allChamberMembersPopup"),
          privatePopup: t("discountText.privatePopup"),
          radioGroupText: {
            allChamberMembers: t("discountText.radioGroupText.allChamberMembers"),
            localChamberMembers: t(
              "discountText.radioGroupText.localChamberMembers"
            ),
            generalPublic: t("discountText.radioGroupText.generalPublic"),
            private: t("discountText.radioGroupText.private"),
          },
        },
        createPerk: {
          title: t("createPerk.title"),
          pageTitle: t("createPerk.pageTitle"),
          forText: t("createPerk.forText"),
          pageSubtitle: t("createPerk.pageSubtitle"),
          perkType: t("createPerk.perkType"),
          specificDetails: t("createPerk.specificDetails"),
          perkTitle: t("createPerk.perkTitle"),
          perkDescription: t("createPerk.perkDescription"),
          previewButton: t("createPerk.previewButton"),
          submitButton: t("createPerk.submitButton"),
          onlineCode: t("createPerk.onlineCode"),
          websiteUrl: t("createPerk.websiteUrl"),
          contactName: t("createPerk.contactName"),
          contactEmail: t("createPerk.contactEmail"),
          contactPhone: t("createPerk.contactPhone"),
          selectDays: t("createPerk.selectDays"),
          enableDays: t("createPerk.enableDays"),
          enableExpiry: t("createPerk.enableExpiry"),
          whereToFind: t("createPerk.whereToFind"),
          howToRedeem: t("createPerk.howToRedeem"),
          notificationDesc: t("createPerk.notificationDesc"),
          perkTitleHelperText: t("createPerk.perkTitleHelperText"),
          perkTitlePlaceholder: t("createPerk.perkTitlePlaceholder"),
          idaTitleHelper: t("createPerk.idaTitleHelper"),
          idaTitleRegenerateHelper: t("createPerk.idaTitleRegenerateHelper"),
          idaGenerate: t("createPerk.idaGenerate"),
          idaRegenerate: t("createPerk.idaRegenerate"),
          idaPerkTitleLabel: t("createPerk.idaPerkTitleLabel"),
          idaPerkDescriptionLabel: t("createPerk.idaPerkDescriptionLabel"),
          perkDescriptionHelperText: t("createPerk.perkDescriptionHelperText"),
          perkDescriptionPlaceholder: t("createPerk.perkDescriptionPlaceholder"),
          characters: t("createPerk.characters"),
          idaDescriptionHelper: t("createPerk.idaDescriptionHelper"),
          idaDescriptionRegenerateHelper: t("createPerk.idaDescriptionRegenerateHelper"),
          redeemableByHelperText: t("createPerk.redeemableByHelperText"),
          anyoneRecommended: t("createPerk.anyoneRecommended"),
          perkRedemptionAvailabilityTitle: t("createPerk.perkRedemptionAvailabilityTitle"),
          perkRedemptionAvailabilityHelperText: t("createPerk.perkRedemptionAvailabilityHelperText"),
          recurringPerkRedemption: t("createPerk.recurringPerkRedemption"),
          recurringPerkDesc: t("createPerk.recurringPerkDesc"),
          oneTimePerkRedemption: t("createPerk.oneTimePerkRedemption"),
          oneTimePerkDesc: t("createPerk.oneTimePerkDesc"),
          certainDaysHelperText: t("createPerk.certainDaysHelperText"),
          schedulePerkDisplayDate: t("createPerk.schedulePerkDisplayDate"),
          schedulePerkHelperText: t("createPerk.schedulePerkHelperText"),
          startDate: t("createPerk.startDate"),
          endDate: t("createPerk.endDate"),
          clickToSetDate: t("createPerk.clickToSetDate"),
          whereToRedeemPerk: t("createPerk.whereToRedeemPerk"),
          whereToRedeemHelperText: t("createPerk.whereToRedeemHelperText"),
          inPerson: t("createPerk.inPerson"),
          inPersonDesc: t("createPerk.inPersonDesc"),
          onlineLabel: t("createPerk.onlineLabel"),
          onlineDesc: t("createPerk.onlineDesc"),
          contactCompanyLabel: t("createPerk.contactCompanyLabel"),
          contactCompanyDesc: t("createPerk.contactCompanyDesc"),
          forOnlinePerkRedemptions: t("createPerk.forOnlinePerkRedemptions"),
          discountCodeLabel: t("createPerk.discountCodeLabel"),
          enterDiscountCode: t("createPerk.enterDiscountCode"),
          companyEcommerceUrl: t("createPerk.companyEcommerceUrl"),
          enterWebsiteUrl: t("createPerk.enterWebsiteUrl"),
          forContactCompanyRedemption: t("createPerk.forContactCompanyRedemption"),
          departmentOrContactName: t("createPerk.departmentOrContactName"),
          enterDepartmentOrContactName: t("createPerk.enterDepartmentOrContactName"),
          companyContactEmailLabel: t("createPerk.companyContactEmailLabel"),
          enterCompanyContactEmail: t("createPerk.enterCompanyContactEmail"),
          contactNumberLabel: t("createPerk.contactNumberLabel"),
          enterContactNumber: t("createPerk.enterContactNumber"),
          sendNewPerkNotification: t("createPerk.sendNewPerkNotification"),
          sendNewPerkNotificationHelperText: t("createPerk.sendNewPerkNotificationHelperText"),
          companiesEmployeeOnly: t("createPerk.companiesEmployeeOnly"),
          option1: t("createPerk.option1"),
          option2: t("createPerk.option2"),
          option1Text1: t("createPerk.option1Text1"),
          option1Text2: t("createPerk.option1Text2"),
          option2Text1: t("createPerk.option2Text1"),
          option2Text2: t("createPerk.option2Text2"),
          downloadImportCsvSample: t("createPerk.downloadImportCsvSample"),
          enterEmployeesEmail: t("createPerk.enterEmployeesEmail"),
          emailsWillAppear: t("createPerk.emailsWillAppear"),
          importantNote: t("createPerk.importantNote"),
          importantNoteRemove: t("createPerk.importantNoteRemove"),
          titleCharRemaining: t("createPerk.titleCharRemaining"),
          descCharRemaining: t("createPerk.descCharRemaining"),
          createMemberPerkButton: t("createPerk.createMemberPerkButton"),
          anyoneDesc: t("createPerk.anyoneDesc"),
          localMembersDesc: t("createPerk.localMembersDesc"),
          allMembersDesc: t("createPerk.allMembersDesc"),
          employeesOnlyDesc: t("createPerk.employeesOnlyDesc"),
          idaTitleModalTitle: t("createPerk.idaTitleModalTitle"),
          idaTitleModalSubtitle: t("createPerk.idaTitleModalSubtitle"),
          idaTitleModalPlaceholder: t("createPerk.idaTitleModalPlaceholder"),
          idaTitleOverwriteTitle: t("createPerk.idaTitleOverwriteTitle"),
          idaTitleOverwriteDesc: t("createPerk.idaTitleOverwriteDesc"),
          idaTitleGeneratedLabel: t("createPerk.idaTitleGeneratedLabel"),
          idaTitleSuccessTitle: t("createPerk.idaTitleSuccessTitle"),
          idaTitleSuccessTitle2: t("createPerk.idaTitleSuccessTitle2"),
          idaDescModalTitle: t("createPerk.idaDescModalTitle"),
          idaDescModalSubtitle: t("createPerk.idaDescModalSubtitle"),
          idaDescModalPlaceholder: t("createPerk.idaDescModalPlaceholder"),
          idaDescOverwriteTitle: t("createPerk.idaDescOverwriteTitle"),
          idaDescOverwriteDesc: t("createPerk.idaDescOverwriteDesc"),
          idaDescGeneratedLabel: t("createPerk.idaDescGeneratedLabel"),
          idaDescSuccessTitle: t("createPerk.idaDescSuccessTitle"),
          idaDescSuccessTitle2: t("createPerk.idaDescSuccessTitle2"),
          idaUpdateSuccessTitle: t("createPerk.idaUpdateSuccessTitle"),
          idaUpdateSuccessTitle2: t("createPerk.idaUpdateSuccessTitle2"),
        },
        showInAppText: t("showInAppText"),
        manageAChamberMemberText: {
          createDiscountText: t("manageAChamberMemberText.createDiscountText"),
        },
        rootAdminText: {
          amountOfDownloadsOnIOS: t("rootAdminText.amountOfDownloadsOnIOS"),
          amountOfDownloadsOnAndroid: t(
            "rootAdminText.amountOfDownloadsOnAndroid"
          ),
          membersInCPAText: t("rootAdminText.membersInCPAText"),
          exportChamberLeads: t("rootAdminText.exportChamberLeads"),
          exportChamberMemberLeads: t("rootAdminText.exportChamberMemberLeads"),
          chamberBoardLead: t("rootAdminText.chamberBoardLead"),
          memberCompanyLead: t("rootAdminText.memberCompanyLead"),
          creationTimeGmt: t("rootAdminText.creationTimeGmt"),
          creationTimeLocalTZ: t("rootAdminText.creationTimeLocalTZ"),
          viewStatBoxes: {
            chamberName: t("rootAdminText.viewStatBoxes.chamberName"),
            numberOfUserAccounts: t(
              "rootAdminText.viewStatBoxes.numberOfUserAccounts"
            ),
            exportChamberLeads: t(
              "rootAdminText.viewStatBoxes.exportChamberLeads"
            ),
            exportChamberMemberLeads: t(
              "rootAdminText.viewStatBoxes.exportChamberMemberLeads"
            ),
            membersInCPAText: t("rootAdminText.viewStatBoxes.membersInCPAText"),
            totalMemberCompanies: t(
              "rootAdminText.viewStatBoxes.totalMemberCompanies"
            ),
            totalUsersSignedUp: t(
              "rootAdminText.viewStatBoxes.totalUsersSignedUp"
            ),
            totalPerks: t("rootAdminText.viewStatBoxes.totalPerks"),
            numberOfLivePerksOfferedTo: t(
              "rootAdminText.viewStatBoxes.numberOfLivePerksOfferedTo"
            ),
            chamberDetails: t("rootAdminText.viewStatBoxes.chamberDetails"),
            perkPerMemberCategory: t(
              "rootAdminText.viewStatBoxes.perkPerMemberCategory"
            ),
            perkPerMemberCategorySubTitle: t(
              "rootAdminText.viewStatBoxes.perkPerMemberCategorySubTitle"
            ),
            categoryName: t("rootAdminText.viewStatBoxes.categoryName"),
            numberOfPerks: t("rootAdminText.viewStatBoxes.numberOfPerks"),
            percentOfPerks: t("rootAdminText.viewStatBoxes.percentOfPerks"),
            chamberPerksPerCategory: t(
              "rootAdminText.viewStatBoxes.chamberPerksPerCategory"
            ),
          },
        },

        loadingText: t("loadingText"),
        offersPerkText: t("offersPerkText"),
        chamberDeletedBadgeText: t("chamberDeletedBadgeText"),
        chamberCpaBadgeText: t("chamberCpaBadgeText"),
        chamberCmpBadgeText: t("chamberCmpBadgeText"),
        toastPerkText: t("toastPerkText"),
        toastPerkUpdatedText: t("toastPerkUpdatedText"),
        perkTitleText: t("perkTitleText"),
        addressSubText: t("addressSubText"),
        adminConsoleLoginEmailSubText: t("adminConsoleLoginEmailSubText"),
        chamberEngage: {
          navBar: {
            categoryAdvertising: {
              popupInfo: {
                active: ({ chamberName }: { chamberName: string }) =>
                  `${t(
                    "chamberEngage.navBar.categoryAdvertising.popupInfo.activeStart"
                  )} ${t(
                    "chamberEngage.navBar.categoryAdvertising.popupInfo.activeMiddle"
                  )} ${t(
                    "chamberEngage.navBar.categoryAdvertising.popupInfo.activeEnd"
                  ).replace("{chamberName}", chamberName)}`,
                inactive: ({ chamberName }: { chamberName: string }) =>
                  `${t(
                    "chamberEngage.navBar.categoryAdvertising.popupInfo.inactiveStart"
                  )} ${t(
                    "chamberEngage.navBar.categoryAdvertising.popupInfo.inactiveMiddle"
                  )} ${chamberName} ${t(
                    "chamberEngage.navBar.categoryAdvertising.popupInfo.inactiveEnd"
                  )}`,
              },
            },
            featuredMember: {
              active: {
                pillText: t(
                  "chamberEngage.navBar.featuredMember.active.pillText"
                ),
                popupText: t(
                  "chamberEngage.navBar.featuredMember.active.popupText"
                ),
              },
              inactive: {
                pillText: t(
                  "chamberEngage.navBar.featuredMember.inactive.pillText"
                ),
                popupText: t(
                  "chamberEngage.navBar.featuredMember.inactive.popupText"
                ),
              },
            },
            categoryBanners: {
              active: {
                pillText: t(
                  "chamberEngage.navBar.categoryBanners.active.pillText"
                ),
              },
              inactive: {
                pillText: t(
                  "chamberEngage.navBar.categoryBanners.inactive.pillText"
                ),
              },
            },
          },

          invoice: {
            monthAndYear: t("chamberEngage.invoice.monthAndYear"),
            totalActivationFees: t("chamberEngage.invoice.totalActivationFees"),
            allInvoicesSent: t("chamberEngage.invoice.allInvoicesSent"),
            chamAndBoard: t("chamberEngage.invoice.chamAndBoard"),
            totalRevenue: t("chamberEngage.invoice.totalRevenue"),
            FeaturedMembers: t("chamberEngage.invoice.FeaturedMembers"),
            invoiceSendDate: t("chamberEngage.invoice.invoiceSendDate"),
            activation: t("chamberEngage.invoice.activation"),
            activationFee: t("chamberEngage.invoice.activationFee"),
            updateRevenue: t("chamberEngage.invoice.updateRevenue"),
            soldByCpa: t("chamberEngage.invoice.soldByCpa"),
            soldByChamber: t("chamberEngage.invoice.soldByChamber"),
            cancel: t("chamberEngage.invoice.cancel"),
            update: t("chamberEngage.invoice.update"),
            invoiceUpdated: t("chamberEngage.invoice.invoiceUpdated"),
            failedToUpdate: t("chamberEngage.invoice.failedToUpdate"),
            activated: t("chamberEngage.invoice.activated"),
            activationExcluded: t("chamberEngage.invoice.activationExcluded"),
            updatedSuccessfully: t("chamberEngage.invoice.updatedSuccessfully"),
            failed: t("chamberEngage.invoice.failed"),
            memberName: t("chamberEngage.invoice.memberName"),
            activationType: t("chamberEngage.invoice.activationType"),
            revenue: t("chamberEngage.invoice.revenue"),
            exAndIn: t("chamberEngage.invoice.exAndIn"),
            featuredMember: t("chamberEngage.invoice.featuredMember"),
            categoryAdvertising: t("chamberEngage.invoice.categoryAdvertising"),
            unknownCategory: t("chamberEngage.invoice.unknownCategory"),
            revenueDetails: t("chamberEngage.invoice.revenueDetails"),
            revenueInform: t("chamberEngage.invoice.revenueInform"),
            exclude: t("chamberEngage.invoice.exclude"),
            include: t("chamberEngage.invoice.include"),
            confirmAction: t("chamberEngage.invoice.confirmAction"),
            messageOne: t("chamberEngage.invoice.messageOne"),
            messageTwo: t("chamberEngage.invoice.messageTwo"),
            confirmDateChange: t("chamberEngage.invoice.confirmDateChange"),
            confirmDateClear: t("chamberEngage.invoice.confirmDateClear"),
            dateConfirmationMessage: t(
              "chamberEngage.invoice.dateConfirmationMessage"
            ),
            dateClearMessage: t("chamberEngage.invoice.dateClearMessage"),
            confirmFeeUpdate: t("chamberEngage.invoice.confirmFeeUpdate"),
            updateMessage: t("chamberEngage.invoice.updateMessage"),
            loadingChamber: t("chamberEngage.invoice.loadingChamber"),
          },
        },

        createChamberText: {
          hideChamberPerksApp: t("createChamberText.hideChamberPerksApp"),
          supportContactemailText: t("createChamberText.supportContactemailText"),
          tooltip: {
            name: t("createChamberText.tooltip.name"),
            description: t("createChamberText.tooltip.description"),
            supportContactName: t("createChamberText.tooltip.supportContactName"),
            supportContactEmail: t(
              "createChamberText.tooltip.supportContactEmail"
            ),
            supportLink: t("createChamberText.tooltip.supportLink"),
            phoneNumber: t("createChamberText.tooltip.phoneNumber"),
            website: t("createChamberText.tooltip.website"),
            facebook: t("createChamberText.tooltip.facebook"),
            instagram: t("createChamberText.tooltip.instagram"),
            threads: t("createChamberText.tooltip.threads"),
            linkedin: t("createChamberText.tooltip.linkedin"),
            twitter: t("createChamberText.tooltip.twitter"),
            tiktok: t("createChamberText.tooltip.tiktok"),
            youtube: t("createChamberText.tooltip.youtube"),
            streetAddress: t("createChamberText.tooltip.streetAddress"),
            province: t("createChamberText.tooltip.province"),
            city: t("createChamberText.tooltip.city"),
            postalCode: t("createChamberText.tooltip.postalCode"),
            logo: t("createChamberText.tooltip.logo"),
            sponsorLogo: t("createChamberText.tooltip.sponsorLogo"),
            sponsorLogoLink: t("createChamberText.tooltip.sponsorLogoLink"),
            jobPostingLink: t("createChamberText.tooltip.jobPostingLink"),
            eventsLink: t("createChamberText.tooltip.eventsLink"),
            newsletterLink: t("createChamberText.tooltip.newsletterLink"),
            surveyLink: t("createChamberText.tooltip.surveyLink"),
            advocacyWinsLink: t("createChamberText.tooltip.advocacyWinsLink"),
            callForNominationsLink: t(
              "createChamberText.tooltip.callForNominationsLink"
            ),
            memberSignupLink: t("createChamberText.tooltip.memberSignupLink"),
            premiumChamberMarketer: t(
              "createChamberText.tooltip.premiumChamberMarketer"
            ),
          },
          chamberName: t("createChamberText.chamberName"),
          chamberDescription: t("createChamberText.chamberDescription"),
          numberOfMembers: t("createChamberText.numberOfMembers"),
          website: t("createChamberText.website"),
          emailUsername: t("createChamberText.emailUsername"),
          emailUsernameCSVLabel: t("createChamberText.emailUsernameCSVLabel"),
          temporaryPassword: t("createChamberText.temporaryPassword"),
          supportContactEmail: t("createChamberText.supportContactEmail"),
          supportContactName: t("createChamberText.supportContactName"),
          phoneNumber: t("createChamberText.phoneNumber"),
          streetAddress: t("createChamberText.streetAddress"),
          city: t("createChamberText.city"),
          postalCode: t("createChamberText.postalCode"),
          facebookUrl: t("createChamberText.facebookUrl"),
          instagramUrl: t("createChamberText.instagramUrl"),
          linkedinUrl: t("createChamberText.linkedinUrl"),
          twitterUrl: t("createChamberText.twitterUrl"),
          threadsUrl: t("createChamberText.threadsUrl"),
          tiktokUrl: t("createChamberText.tiktokUrl"),
          youtubeUrl: t("createChamberText.youtubeUrl"),
          memberSignupLink: t("createChamberText.memberSignupLink"),
          surveyLink: t("createChamberText.surveyLink"),
          supportLink: t("createChamberText.supportLink"),
          newsletterLink: t("createChamberText.newsletterLink"),
          created: t("createChamberText.created"),
          error: t("createChamberText.error"),
          jobPostingLink: t("createChamberText.jobPostingLink"),
          eventsLink: t("createChamberText.eventsLink"),
          callForNominationsLink: t("createChamberText.callForNominationsLink"),
        },

        rootAdminChamberText: {
          createPerkToSeeChart: t("rootAdminChamber.createPerkToSeeChart"),
          usersSignedUp: t("rootAdminChamber.usersSignedUp"),
          usersSignedupWith: t("rootAdminChamber.usersSignedupWith"),
          numberOfBusinessesOfferingPerks: t(
            "rootAdminChamber.numberOfBusinessesOfferingPerks"
          ),
          numberOfTotalPerks: t("rootAdminChamber.numberOfTotalPerks"),
          numberOfLivePerksOfferedTo: t(
            "rootAdminChamberText.numberOfLivePerksOfferedTo"
          ),
          amountOfDiscountsRedeemed: t(
            "rootAdminChamber.amountOfDiscountsRedeemed"
          ),
          perksOfferedByYourMembers: t(
            "rootAdminChamber.perksOfferedByYourMembers"
          ),
          perksOfferedByYourMemebrs: t(
            "rootAdminChamberText.perksOfferedByYourMemebrs"
          ),

          perkBreakdown: t("rootAdminChamberText.perkBreakdown"),
          membersOfferingPerks: t("rootAdminChamberText.membersOfferingPerks"),
          yourMembersFeaturedOnChamberPerksApp: t(
            "rootAdminChamberText.yourMembersFeaturedOnChamberPerksApp"
          ),
          totalPerkViews: t("rootAdminChamberText.totalPerkViews"),
          chamberPerkBreakdown: t("rootAdminChamberText.chamberPerkBreakdown"),
          perksAvailableOnApp: t("rootAdminChamberText.perksAvailableOnApp"),
          totalMemberCompaniesFeaturedOnChamberPerksApp: t(
            "rootAdminChamberText.totalMemberCompaniesFeaturedOnChamberPerksApp"
          ),
          membersOfferingPerksOnApp: t(
            "rootAdminChamberText.membersOfferingPerksOnApp"
          ),
          outOf: t("rootAdminChamberText.outOf"),
          outOfMembers: t("rootAdminChamberText.outOfMembers"),
          totalPerkViewsOnApp: t("rootAdminChamberText.totalPerkViewsOnApp"),
          perkCategoryBreakdown: t("rootAdminChamberText.perkCategoryBreakdown"),
          mostPopularPerks: t("rootAdminChamberText.mostPopularPerks"),
          newestPerks: t("rootAdminChamberText.newestPerks"),
          perkTableCompanyName: t("rootAdminChamberText.perkTableCompanyName"),
          perkTablePerkTitle: t("rootAdminChamberText.perkTablePerkTitle"),
          perkTableOfferedTo: t("rootAdminChamberText.perkTableOfferedTo"),
          perkTableViews: t("rootAdminChamberText.perkTableViews"),
          noPerksAvailable: t("rootAdminChamberText.noPerksAvailable"),
          companyLeads: t("rootAdminChamberText.companyLeads"),
          mediumBox: true,
          boxSubTitle: t("rootAdminChamberText.boxSubTitle"),
          totalRevenueCategoryBanners: t(
            "rootAdminChamberText.totalRevenueCategoryBanners"
          ),
          totalRevenueProfileBanners: t(
            "rootAdminChamberText.totalRevenueProfileBanners"
          ),
          membersWithActivatedBanners: t(
            "rootAdminChamberText.membersWithActivatedBanners"
          ),
          membersPerCategoryWithActivatedBanners: t(
            "rootAdminChamberText.membersPerCategoryWithActivatedBanners"
          ),
        },

        chamberMemberAdminRoot: {
          uniqueViewsOnYourListing: t(
            "chamberMemberAdminRoot.uniqueViewsOnYourListing"
          ),
        },

        forgotPassword: {
          pleaseEnterEmail: t("forgotPassword.pleaseEnterEmail"),
          goBacktoLogin: t("forgotPassword.goBacktoLogin"),
        },

        login: {
          forgotPassword: t("login.forgotPassword"),
        },

        newPassword: {
          pleaseEnterNewPassword: t("newPassword.pleaseEnterNewPassword"),
          changePassword: t("newPassword.changePassword"),
        },

        // resetPassword: {
        //   pleaseVerifyEmail: t("resetPassword.pleaseVerifyEmail"),
        //   changePassword: t("resetPassword.changePassword"),
        //   resetPasswordSuccess: t("resetPassword.resetPasswordSuccess"),
        //   returnToLogin: t("resetPassword.returnToLogin"),
        // },

        createChamber: {
          supportContactemail: t("createChamber.supportContactemail"),
          tooltip: {
            name: t("createChamber.tooltip.name"),
            description: t("createChamber.tooltip.description"),
            supportContactName: t("createChamber.tooltip.supportContactName"),
            supportContactEmail: t("createChamber.tooltip.supportContactEmail"),
            supportLink: t("createChamber.tooltip.supportLink"),
            phoneNumber: t("createChamber.tooltip.phoneNumber"),
            website: t("createChamber.tooltip.website"),
            facebook: t("createChamber.tooltip.facebook"),
            instagram: t("createChamber.tooltip.instagram"),
            threads: t("createChamber.tooltip.threads"),
            linkedin: t("createChamber.tooltip.linkedin"),
            twitter: t("createChamber.tooltip.twitter"),
            tiktok: t("createChamber.tooltip.tiktok"),
            youtube: t("createChamber.tooltip.youtube"),
            streetAddress: t("createChamber.tooltip.streetAddress"),
            province: t("createChamber.tooltip.province"),
            city: t("createChamber.tooltip.city"),
            postalCode: t("createChamber.tooltip.postalCode"),
            logo: t("createChamber.tooltip.logo"),
            sponsorLogo: t("createChamber.tooltip.sponsorLogo"),
            sponsorLogoLink: t("createChamber.tooltip.sponsorLogoLink"),
            jobPostingLink: t("createChamber.tooltip.jobPostingLink"),
            eventsLink: t("createChamber.tooltip.eventsLink"),
            newsletterLink: t("createChamber.tooltip.newsletterLink"),
            surveyLink: t("createChamber.tooltip.surveyLink"),
            memberSignupLink: t("createChamber.tooltip.memberSignupLink"),
            premiumChamberMarketer: t(
              "createChamber.tooltip.premiumChamberMarketer"
            ),
          },
        },
        shopLocal: {
          shopLocalCampaign: {
            notParticipating: t("shopLocal.shopLocalCampaign.notParticipating"),
            isParticipating: t("shopLocal.shopLocalCampaign.isParticipating"),
            prizeDescription: t("shopLocal.shopLocalCampaign.prizeDescription"),
            startDate: t("shopLocal.shopLocalCampaign.startDate"),
            endDate: t("shopLocal.shopLocalCampaign.endDate"),
            chamberPerksCode: t("shopLocal.shopLocalCampaign.chamberPerksCode"),
            activeCompanyParticipating: t(
              "shopLocal.shopLocalCampaign.activeCompanyParticipating"
            ),
            upcomingCompanyParticipating: t(
              "shopLocal.shopLocalCampaign.upcomingCompanyParticipating"
            ),
            customErrors: t("shopLocal.shopLocalCampaign.customErrors"),
            toast: t("shopLocal.shopLocalCampaign.toast"),
          },
          chooseParticipatingMembers: {
            memberCompanySelected: t(
              "shopLocal.chooseParticipatingMembers.memberCompanySelected"
            ),
            SelectAll: t("shopLocal.chooseParticipatingMembers.SelectAll"),
            selectMemberCompanies: t(
              "shopLocal.chooseParticipatingMembers.selectMemberCompanies"
            ),
            ParticipationCodeExplanationText1: t(
              "shopLocal.chooseParticipatingMembers.ParticipationCodeExplanationText1"
            ),
            ParticipationCodeExplanationText2: t(
              "shopLocal.chooseParticipatingMembers.ParticipationCodeExplanationText2"
            ),
            ParticipationCodeExplanationText3: t(
              "shopLocal.chooseParticipatingMembers.ParticipationCodeExplanationText3"
            ),
            ParticipationCodeExplanationText4: t(
              "shopLocal.chooseParticipatingMembers.ParticipationCodeExplanationText4"
            ),
            ParticipationCodeExplanationText5: t(
              "shopLocal.chooseParticipatingMembers.ParticipationCodeExplanationText5"
            ),
            allItemsSelected: t(
              "shopLocal.chooseParticipatingMembers.allItemsSelected"
            ),
            charRemaining: t(
              "shopLocal.chooseParticipatingMembers.charRemaining"
            ),
          },
          createShopLocalCampaign: {
            createShopLocalCampaign: t(
              "shopLocal.createShopLocalCampaign.createShopLocalCampaign"
            ),
            prizeDescription: t(
              "shopLocal.createShopLocalCampaign.prizeDescription"
            ),
            campaignName: t("shopLocal.createShopLocalCampaign.campaignName"),
            sponsoringMember: t(
              "shopLocal.createShopLocalCampaign.sponsoringMember"
            ),
            startDate: t("shopLocal.createShopLocalCampaign.startDate"),
            endDate: t("shopLocal.createShopLocalCampaign.endDate"),
            participatingMembers: t(
              "shopLocal.createShopLocalCampaign.participatingMembers"
            ),
            prizeDrawParticipants: t(
              "shopLocal.createShopLocalCampaign.prizeDrawParticipants"
            ),
            status: t("shopLocal.createShopLocalCampaign.status"),
            participatonCode: t(
              "shopLocal.createShopLocalCampaign.participatonCode"
            ),
            usersInPrizeDraw: t(
              "shopLocal.createShopLocalCampaign.usersInPrizeDraw"
            ),
            enteredPrizeDrawRedeem: t(
              "shopLocal.createShopLocalCampaign.enteredPrizeDrawRedeem"
            ),
            noneSoFar: t("shopLocal.createShopLocalCampaign.noneSoFar"),
            redeemPerksFrom1: t(
              "shopLocal.createShopLocalCampaign.redeemPerksFrom1"
            ),
            redeemPerksFrom2: t(
              "shopLocal.createShopLocalCampaign.redeemPerksFrom2"
            ),
            participatingMemberCompanies: t(
              "shopLocal.createShopLocalCampaign.participatingMemberCompanies"
            ),
            prizeDrawCode: t("shopLocal.createShopLocalCampaign.prizeDrawCode"),
            updateMemberList: t(
              "shopLocal.createShopLocalCampaign.updateMemberList"
            ),
            close: t("shopLocal.createShopLocalCampaign.close"),
            edit: t("shopLocal.createShopLocalCampaign.edit"),
            chamberPerksAppUsers: t(
              "shopLocal.createShopLocalCampaign.chamberPerksAppUsers"
            ),
            participatingPrizeDraw: t(
              "shopLocal.createShopLocalCampaign.participatingPrizeDraw"
            ),
            pickRandomWinner: t(
              "shopLocal.createShopLocalCampaign.pickRandomWinner"
            ),
            randomWinnerIs: t("shopLocal.createShopLocalCampaign.randomWinnerIs"),
            noWinnerYet: t("shopLocal.createShopLocalCampaign.noWinnerYet"),
            openList: t("shopLocal.createShopLocalCampaign.openList"),
            openListAndPickWinner: t(
              "shopLocal.createShopLocalCampaign.openListAndPickWinner"
            ),
            end: t("shopLocal.createShopLocalCampaign.end"),
            charRemaining: t("shopLocal.createShopLocalCampaign.charRemaining"),
            enterText: t("shopLocal.createShopLocalCampaign.enterText"),
            create: t("shopLocal.createShopLocalCampaign.create"),
            sendNotificationLabel: (chamberName: string) =>
              t("shopLocal.createShopLocalCampaign.sendNotificationLabel", {
                chamberName,
              }),
            sendNotificationChamberFallback: t(
              "shopLocal.createShopLocalCampaign.sendNotificationChamberFallback"
            ),
            sendNotificationHelper: t(
              "shopLocal.createShopLocalCampaign.sendNotificationHelper"
            ),
            sendNotificationStartDateRequired: t(
              "shopLocal.createShopLocalCampaign.sendNotificationStartDateRequired"
            ),
            sendNotificationFutureStartDisabled: t(
              "shopLocal.createShopLocalCampaign.sendNotificationFutureStartDisabled"
            ),
            prizeDescriptionCompletePhraseHelper: t(
              "shopLocal.createShopLocalCampaign.prizeDescriptionCompletePhraseHelper"
            ),
            appUserEmailPreviewPlaceholder: t(
              "shopLocal.createShopLocalCampaign.appUserEmailPreviewPlaceholder"
            ),
            participatingMemberEligibilityWarning: t(
              "shopLocal.createShopLocalCampaign.participatingMemberEligibilityWarning"
            ),
            notificationOutcomeNotRequested: t(
              "shopLocal.createShopLocalCampaign.notificationOutcomeNotRequested"
            ),
            notificationOutcomeSent: (successCount: number) =>
              t("shopLocal.createShopLocalCampaign.notificationOutcomeSent", {
                successCount,
              }),
            notificationOutcomePartiallySent: (
              successCount: number,
              targetCount: number,
              failureCount: number
            ) =>
              t(
                "shopLocal.createShopLocalCampaign.notificationOutcomePartiallySent",
                { successCount, targetCount, failureCount }
              ),
            notificationOutcomeNoEligibleRecipients: t(
              "shopLocal.createShopLocalCampaign.notificationOutcomeNoEligibleRecipients"
            ),
            notificationOutcomeFailed: t(
              "shopLocal.createShopLocalCampaign.notificationOutcomeFailed"
            ),
            notificationOutcomeCampaignNotActive: t(
              "shopLocal.createShopLocalCampaign.notificationOutcomeCampaignNotActive"
            ),
            notificationOutcomeConfigurationError: t(
              "shopLocal.createShopLocalCampaign.notificationOutcomeConfigurationError"
            ),
          },
          listallChamberShopLocalCampaigns: {
            noActiveContests: t(
              "shopLocal.listallChamberShopLocalCampaigns.noActiveContests"
            ),
            noPreviousContests: t(
              "shopLocal.listallChamberShopLocalCampaigns.noPreviousContests"
            ),
          },
          shopLocalDatePicker: {
            contestStartDate: t("shopLocal.shopLocalDatePicker.contestStartDate"),
            contestEndDate: t("shopLocal.shopLocalDatePicker.contestEndDate"),
          },
          fullPageShopLocalCampaign: {
            contestName: t("shopLocal.fullPageShopLocalCampaign.contestName"),
            prizeDescription: t(
              "shopLocal.fullPageShopLocalCampaign.prizeDescription"
            ),
            startDate: t("shopLocal.fullPageShopLocalCampaign.startDate"),
            endDate: t("shopLocal.fullPageShopLocalCampaign.endDate"),
            participatingMemberCompanies: t(
              "shopLocal.fullPageShopLocalCampaign.participatingMemberCompanies"
            ),
            prizeDrawParticipants: t(
              "shopLocal.fullPageShopLocalCampaign.prizeDrawParticipants"
            ),
            contestStatus: t("shopLocal.fullPageShopLocalCampaign.contestStatus"),
            noActiveContests: t(
              "shopLocal.fullPageShopLocalCampaign.noActiveContests"
            ),
            noPreviousContests: t(
              "shopLocal.fullPageShopLocalCampaign.noPreviousContests"
            ),
            participatingMemberCompaniesTitle: t(
              "shopLocal.fullPageShopLocalCampaign.participatingMemberCompaniesTitle"
            ),
            prizeDrawParticipationExplanation: t(
              "shopLocal.fullPageShopLocalCampaign.prizeDrawParticipationExplanation"
            ),
            updateMemberList: t(
              "shopLocal.fullPageShopLocalCampaign.updateMemberList"
            ),
            close: t("shopLocal.fullPageShopLocalCampaign.close"),
            chamberPerksAppUsersTitle: t(
              "shopLocal.fullPageShopLocalCampaign.chamberPerksAppUsersTitle"
            ),
            participatingInPrizeDraw: t(
              "shopLocal.fullPageShopLocalCampaign.participatingInPrizeDraw"
            ),
            pickRandomWinner: t(
              "shopLocal.fullPageShopLocalCampaign.pickRandomWinner"
            ),
            randomWinnerIs: t(
              "shopLocal.fullPageShopLocalCampaign.randomWinnerIs"
            ),
            entrantEmail: t("shopLocal.fullPageShopLocalCampaign.entrantEmail"),
            enteredPrizeDrawByRedeeming: t(
              "shopLocal.fullPageShopLocalCampaign.enteredPrizeDrawByRedeeming"
            ),
            noneSoFar: t("shopLocal.fullPageShopLocalCampaign.noneSoFar"),
            contestNotFound: t(
              "shopLocal.fullPageShopLocalCampaign.contestNotFound"
            ),
            sortedDescending: t(
              "shopLocal.fullPageShopLocalCampaign.sortedDescending"
            ),
            sortedAscending: t(
              "shopLocal.fullPageShopLocalCampaign.sortedAscending"
            ),
            openList: t("shopLocal.fullPageShopLocalCampaign.openList"),
            openListAndPickWinner: t(
              "shopLocal.fullPageShopLocalCampaign.openListAndPickWinner"
            ),
            endCampaignModalTitle: t(
              "shopLocal.fullPageShopLocalCampaign.endCampaignModalTitle"
            ),
            endCampaignModalText1: t(
              "shopLocal.fullPageShopLocalCampaign.endCampaignModalText1"
            ),
            endCampaignModalText2: t(
              "shopLocal.fullPageShopLocalCampaign.endCampaignModalText2"
            ),
            endCampaignConfirm: t(
              "shopLocal.fullPageShopLocalCampaign.endCampaignConfirm"
            ),
            goBack: t("shopLocal.fullPageShopLocalCampaign.goBack"),
            endCampaign: t("shopLocal.fullPageShopLocalCampaign.endCampaign"),
            edit: t("shopLocal.fullPageShopLocalCampaign.edit"),
            upcoming: t("shopLocal.fullPageShopLocalCampaign.upcoming"),
            active: t("shopLocal.fullPageShopLocalCampaign.active"),
            ended: t("shopLocal.fullPageShopLocalCampaign.ended"),
            loading: t("shopLocal.fullPageShopLocalCampaign.loading"),
            end: t("shopLocal.fullPageShopLocalCampaign.end"),
            participatingCode: t(
              "shopLocal.fullPageShopLocalCampaign.participatingCode"
            ),
            usersInPrizeDraw: t(
              "shopLocal.fullPageShopLocalCampaign.usersInPrizeDraw"
            ),
            memberCompanyName: t(
              "shopLocal.fullPageShopLocalCampaign.memberCompanyName"
            ),
            downloadCsv: t("shopLocal.fullPageShopLocalCampaign.downloadCsv"),
            searchPlaceholder: t(
              "shopLocal.fullPageShopLocalCampaign.searchPlaceholder"
            ),
          },
          updateChamberShopLocalCampaign: {
            campaignNameReq: t(
              "shopLocal.updateChamberShopLocalCampaign.campaignNameReq"
            ),
            prizeDescReq: t(
              "shopLocal.updateChamberShopLocalCampaign.prizeDescReq"
            ),
            participatingMembersReq: t(
              "shopLocal.updateChamberShopLocalCampaign.participatingMembersReq"
            ),
            atLeastOneMember: t(
              "shopLocal.updateChamberShopLocalCampaign.atLeastOneMember"
            ),
            startDateReq: t(
              "shopLocal.updateChamberShopLocalCampaign.startDateReq"
            ),
            endDateReq: t("shopLocal.updateChamberShopLocalCampaign.endDateReq"),
            endDateAfterStart: t(
              "shopLocal.updateChamberShopLocalCampaign.endDateAfterStart"
            ),
            contestUpdated: t(
              "shopLocal.updateChamberShopLocalCampaign.contestUpdated"
            ),
            anErrorOccurred: t(
              "shopLocal.updateChamberShopLocalCampaign.anErrorOccurred"
            ),
            updateToastText: t(
              "shopLocal.updateChamberShopLocalCampaign.updateToastText"
            ),
            close: t("shopLocal.updateChamberShopLocalCampaign.close"),
            endCampaign: t(
              "shopLocal.updateChamberShopLocalCampaign.endCampaign"
            ),
            invalidEntry: t(
              "shopLocal.updateChamberShopLocalCampaign.invalidEntry"
            ),
            contestEnded: t(
              "shopLocal.updateChamberShopLocalCampaign.contestEnded"
            ),
            enterTextAbove: t(
              "shopLocal.updateChamberShopLocalCampaign.enterTextAbove"
            ),
            contestPreviewMsg: t(
              "shopLocal.updateChamberShopLocalCampaign.contestPreviewMsg"
            ),
            memberParticipationMsg: t(
              "shopLocal.updateChamberShopLocalCampaign.memberParticipationMsg"
            ),
            qualifyChanceToWin: t(
              "shopLocal.updateChamberShopLocalCampaign.qualifyChanceToWin"
            ),
            endCampaignModalTitle: t(
              "shopLocal.updateChamberShopLocalCampaign.endCampaignModalTitle"
            ),
            endCampaignModalText1: t(
              "shopLocal.updateChamberShopLocalCampaign.endCampaignModalText1"
            ),
            endCampaignModalText2: t(
              "shopLocal.updateChamberShopLocalCampaign.endCampaignModalText2"
            ),
            endCampaignConfirm: t(
              "shopLocal.updateChamberShopLocalCampaign.endCampaignConfirm"
            ),
            goBack: t("shopLocal.updateChamberShopLocalCampaign.goBack"),
            contestUpdatedSuccessfully: t(
              "shopLocal.updateChamberShopLocalCampaign.contestUpdatedSuccessfully"
            ),
            invalidErrorText: t(
              "shopLocal.updateChamberShopLocalCampaign.invalidErrorText"
            ),
            update: t("shopLocal.updateChamberShopLocalCampaign.update"),
            contestEndedConfirm: t(
              "shopLocal.updateChamberShopLocalCampaign.contestEndedConfirm"
            ),
            redirectToastText: t(
              "shopLocal.updateChamberShopLocalCampaign.redirectToastText"
            ),
            loading: t("shopLocal.updateChamberShopLocalCampaign.loading"),
            characterLimit: t(
              "shopLocal.updateChamberShopLocalCampaign.characterLimit"
            ),
            endDateError: t(
              "shopLocal.updateChamberShopLocalCampaign.endDateError"
            ),
            watchTutorial: t(
              "shopLocal.updateChamberShopLocalCampaign.watchTutorial"
            ),
            viewInstructions: t(
              "shopLocal.updateChamberShopLocalCampaign.viewInstructions"
            ),
            setupGuide: t(
              "shopLocal.updateChamberShopLocalCampaign.setupGuide"
            ),
            contestNameInternalNote: t(
              "shopLocal.updateChamberShopLocalCampaign.contestNameInternalNote"
            ),
            prizeDescriptionNote: t(
              "shopLocal.updateChamberShopLocalCampaign.prizeDescriptionNote"
            ),
            previewPrizeDescription: t(
              "shopLocal.updateChamberShopLocalCampaign.previewPrizeDescription"
            ),
            contestDuration: t(
              "shopLocal.updateChamberShopLocalCampaign.contestDuration"
            ),
            contestDurationNote: t(
              "shopLocal.updateChamberShopLocalCampaign.contestDurationNote"
            ),
            contestTimingNote1: t(
              "shopLocal.updateChamberShopLocalCampaign.contestTimingNote1"
            ),
            contestTimingBegin: t(
              "shopLocal.updateChamberShopLocalCampaign.contestTimingBegin"
            ),
            contestTimingNote2: t(
              "shopLocal.updateChamberShopLocalCampaign.contestTimingNote2"
            ),
            contestTimingEnd: t(
              "shopLocal.updateChamberShopLocalCampaign.contestTimingEnd"
            ),
            contestTimingNote3: t(
              "shopLocal.updateChamberShopLocalCampaign.contestTimingNote3"
            ),
            contestStartDate: t(
              "shopLocal.updateChamberShopLocalCampaign.contestStartDate"
            ),
            contestEndDate: t(
              "shopLocal.updateChamberShopLocalCampaign.contestEndDate"
            ),
            participatingMembersIntro1: t(
              "shopLocal.updateChamberShopLocalCampaign.participatingMembersIntro1"
            ),
            participatingMembersIntro2: t(
              "shopLocal.updateChamberShopLocalCampaign.participatingMembersIntro2"
            ),
            participatingMembersIntro3: t(
              "shopLocal.updateChamberShopLocalCampaign.participatingMembersIntro3"
            ),
            contestFormat: t(
              "shopLocal.updateChamberShopLocalCampaign.contestFormat"
            ),
            contestFormatLine1: t(
              "shopLocal.updateChamberShopLocalCampaign.contestFormatLine1"
            ),
            contestFormatLine2Prefix: t(
              "shopLocal.updateChamberShopLocalCampaign.contestFormatLine2Prefix"
            ),
            contestFormatLine3Prefix: t(
              "shopLocal.updateChamberShopLocalCampaign.contestFormatLine3Prefix"
            ),
            contestFormatLine3Active: t(
              "shopLocal.updateChamberShopLocalCampaign.contestFormatLine3Active"
            ),
            contestFormatLine3Suffix: t(
              "shopLocal.updateChamberShopLocalCampaign.contestFormatLine3Suffix"
            ),
            contestFormatLine4Prefix: t(
              "shopLocal.updateChamberShopLocalCampaign.contestFormatLine4Prefix"
            ),
            contestFormatLine4Member: t(
              "shopLocal.updateChamberShopLocalCampaign.contestFormatLine4Member"
            ),
            contestFormatLine4Suffix: t(
              "shopLocal.updateChamberShopLocalCampaign.contestFormatLine4Suffix"
            ),
            contestFormatLine5: t(
              "shopLocal.updateChamberShopLocalCampaign.contestFormatLine5"
            ),
            endContest: t(
              "shopLocal.updateChamberShopLocalCampaign.endContest"
            ),
            updateButton: t(
              "shopLocal.updateChamberShopLocalCampaign.updateButton"
            ),
            updateSuccessTitle: t(
              "shopLocal.updateChamberShopLocalCampaign.updateSuccessTitle"
            ),
            notificationUpdateDoesNotResend: t(
              "shopLocal.updateChamberShopLocalCampaign.notificationUpdateDoesNotResend"
            ),
            prizeDrawParticipantsTitle: t(
              "shopLocal.updateChamberShopLocalCampaign.prizeDrawParticipantsTitle"
            ),
            prizeDrawParticipantsDesc: t(
              "shopLocal.updateChamberShopLocalCampaign.prizeDrawParticipantsDesc"
            ),
            participantEmailColumn: t(
              "shopLocal.updateChamberShopLocalCampaign.participantEmailColumn"
            ),
            entryEarnedFromMemberColumn: t(
              "shopLocal.updateChamberShopLocalCampaign.entryEarnedFromMemberColumn"
            ),
            downloadList: t(
              "shopLocal.updateChamberShopLocalCampaign.downloadList"
            ),
            pickRandomWinnerButton: t(
              "shopLocal.updateChamberShopLocalCampaign.pickRandomWinnerButton"
            ),
            randomWinnerTitle: t(
              "shopLocal.updateChamberShopLocalCampaign.randomWinnerTitle"
            ),
            randomWinnerSubtitle: t(
              "shopLocal.updateChamberShopLocalCampaign.randomWinnerSubtitle"
            ),
          },
        },
      };

      const groups = {
        [UserType.PerkPartnerDiscountAdmin]: {
          [RedirectUri.editperkPartnerDiscountPageChamberAdmin]: {
            route: RedirectUri.editperkPartnerDiscountPageChamberAdmin,
            iconName: "CPAMarketingToolkit",
            title: t(
              "groups.UserType.PerkPartnerDiscountAdmin.RedirectUri.editperkPartnerDiscountPageChamberAdmin.title"
            ),
            showInNavBar: true,
            toolTipDescription: t(
              "groups.UserType.PerkPartnerDiscountAdmin.RedirectUri.editperkPartnerDiscountPageChamberAdmin.toolTipDescription"
            ),
            description: t(
              "groups.UserType.PerkPartnerDiscountAdmin.RedirectUri.editperkPartnerDiscountPageChamberAdmin.description"
            ),
          },
          [RedirectUri.cpacreateperk]: {
            route: RedirectUri.cpacreateperk,
            iconName: null,
            title: "",
            showInNavBar: false,
            toolTipDescription: "",
            description: "",
          },
        },
        [UserType.RootAdmin]: {
          [RedirectUri.rootAdmin]: {
            route: RedirectUri.rootAdmin,
            iconName: "CustomStatsIcon",
            title: t("groups.UserType.RootAdmin.RedirectUri.rootAdmin.title"),
            showInNavBar: true,
            toolTipDescription: t(
              "groups.UserType.RootAdmin.RedirectUri.rootAdmin.toolTipDescription"
            ),
            description: "",
          },
          [RedirectUri.listAllChamberAdmin]: {
            route: RedirectUri.listAllChamberAdmin,
            iconName: "CustomManageMembersPerks",
            title: t(
              "groups.UserType.RootAdmin.RedirectUri.listAllChamberAdmin.title"
            ),
            showInNavBar: true,
            toolTipDescription: t(
              "groups.UserType.RootAdmin.RedirectUri.listAllChamberAdmin.toolTipDescription"
            ),
            description: "",
          },
          [RedirectUri.updateAChamberRoute]: {
            route: RedirectUri.updateAChamberRoute,
            iconName: null,
            title: t(
              "groups.UserType.RootAdmin.RedirectUri.updateAChamberRoute.title"
            ),
            showInNavbar: false,
            toolTipDescription: t(
              "groups.UserType.RootAdmin.RedirectUri.updateAChamberRoute.toolTipDescription"
            ),
            description: "",
          },
          [RedirectUri.createAChamber]: {
            route: RedirectUri.createAChamber,
            iconName: "CustomAddIcon",
            title: t(
              "groups.UserType.RootAdmin.RedirectUri.createAChamber.title"
            ),
            showInNavBar: true,
            toolTipDescription: t(
              "groups.UserType.RootAdmin.RedirectUri.createAChamber.toolTipDescription"
            ),
            description: "",
          },
          [RedirectUri.listAllPerkPartner]: {
            route: RedirectUri.listAllPerkPartner,
            iconName: "CPAMarketingToolkit",
            title: t(
              "groups.UserType.RootAdmin.RedirectUri.listAllPerkPartner.title"
            ),
            showInNavBar: true,
            toolTipDescription: t(
              "groups.UserType.RootAdmin.RedirectUri.listAllPerkPartner.toolTipDescription"
            ),
            description: t(
              "groups.UserType.RootAdmin.RedirectUri.listAllPerkPartner.description"
            ),
          },
          [RedirectUri.createAPerkPartner]: {
            route: RedirectUri.createAPerkPartner,
            iconName: "CustomAddIcon",
            title: t(
              "groups.UserType.RootAdmin.RedirectUri.createAPerkPartner.title"
            ),
            showInNavBar: true,
            toolTipDescription: t(
              "groups.UserType.RootAdmin.RedirectUri.createAPerkPartner.toolTipDescription"
            ),
            description: "",
          },
          [RedirectUri.editperkPartnerDiscountPageChamberAdmin]: {
            route: RedirectUri.editperkPartnerDiscountPageChamberAdmin,
            iconName: null,
            title: t(
              "groups.UserType.RootAdmin.RedirectUri.editperkPartnerDiscountPageChamberAdmin.title"
            ),
            showInNavBar: false,
            toolTipDescription: t(
              "groups.UserType.RootAdmin.RedirectUri.editperkPartnerDiscountPageChamberAdmin.toolTipDescription"
            ),
            description: "",
          },
          [RedirectUri.perkPartnersViewStats]: {
            route: RedirectUri.perkPartnersViewStats,
            iconName: null,
            title: t(
              "groups.UserType.ChamberAdmin.RedirectUri.perkPartnersViewStats.navTitle"
            ),
            showInNavBar: false,
            toolTipDescription: "",
            description: "",
          },
          [RedirectUri.rootManageChamberEngage]: {
            route: RedirectUri.rootManageChamberEngage,
            iconName: "CustomChamberEngageIcon",
            title: t(
              "groups.UserType.RootAdmin.RedirectUri.rootManageChamberEngage.title"
            ),
            useIconAsNavBarButtonTitle: true,
            showInNavBar: true,
            isNew: true,
            description: t(
              "groups.UserType.RootAdmin.RedirectUri.rootManageChamberEngage.description"
            ),
            nestedRoutes: [
              {
                route: RedirectUri.rootManageChamberEngage,
                title: "Manage For All Chambers and Boards",
                showInNavBar: true,
                refreshOnClick: true,
              },
              {
                route: RedirectUri.rootChamberEngageInvoicing,
                title: "Manage Invoicing",
                showInNavBar: true,
                refreshOnClick: true,
              },
            ],
          },
          [RedirectUri.rootChamberEngageInvoicing]: {
            route: RedirectUri.rootChamberEngageInvoicing,
            iconName: null,
            title: "Manage Chamber Engage™ Invoicing",
            description: "",
            showInNavBar: false,
          },
          [RedirectUri.chamberEngageManageCategoryAdvertisingBanners]: {
            route: RedirectUri.chamberEngageManageCategoryAdvertisingBanners,
            iconName: null,
            title: t(
              "groups.UserType.RootAdmin.RedirectUri.chamberEngageManageCategoryAdvertisingBanners.title"
            ),
            showInNavBar: false,
            description: t(
              "groups.UserType.RootAdmin.RedirectUri.chamberEngageManageCategoryAdvertisingBanners.description"
            ),
          },
          [RedirectUri.chamberEngageManageMemberProfileAndPerkBanners]: {
            route: RedirectUri.chamberEngageManageMemberProfileAndPerkBanners,
            iconName: null,
            title:
              "Manage Chamber Engage™ Featured Member Profile & Perks Banners",
            showInNavBar: false,
            description:
              "Enable featured member profile and perk display and video banners for members.",
          },
          [RedirectUri.cpacreateperk]: {
            route: RedirectUri.cpacreateperk,
            iconName: null,
            title: "",
            showInNavBar: false,
            toolTipDescription: "",
            description: "",
          },
        },
        [UserType.ChamberAdmin]: {
          [RedirectUri.chamberAdmin]: {
            route: RedirectUri.chamberAdmin,
            iconName: "CustomStatsIcon",
            title: t(
              "groups.UserType.ChamberAdmin.RedirectUri.chamberAdmin.title"
            ),
            showInNavBar: true,
            isNew: true,
            toolTipDescription: t(
              "groups.UserType.ChamberAdmin.RedirectUri.chamberAdmin.toolTipDescription"
            ),
            description: t(
              "groups.UserType.ChamberAdmin.RedirectUri.chamberAdmin.description"
            ),
          },
          [RedirectUri.manageYourChamberChamberAdmin]: {
            route: RedirectUri.manageYourChamberChamberAdmin,
            iconName: "CustomManageChamberProfile",
            title: "Manage Your Chamber",
            showInNavBar: true,
            badgeType: "newUpdate",
            toolTipDescription: t(
              "groups.UserType.ChamberAdmin.RedirectUri.manageYourChamberChamberAdmin.toolTipDescription"
            ),
            description: t(
              "groups.UserType.ChamberAdmin.RedirectUri.manageYourChamberChamberAdmin.description"
            ),
          },
          [RedirectUri.createAMemberChamberAdmin]: {
            route: RedirectUri.createAMemberChamberAdmin,
            iconName: "PersonAddOutlinedIcon",
            title: t(
              "groups.UserType.ChamberAdmin.RedirectUri.createAMemberChamberAdmin.title"
            ),
            showInNavBar: false,
            toolTipDescription: t(
              "groups.UserType.ChamberAdmin.RedirectUri.createAMemberChamberAdmin.toolTipDescription"
            ),
            description: t(
              "groups.UserType.ChamberAdmin.RedirectUri.createAMemberChamberAdmin.description"
            ),
          },
          [RedirectUri.manageAMemberChamberAdmin]: {
            route: RedirectUri.manageAMemberChamberAdmin,
            iconName: "PeopleOutlinedIcon",
            title: "Members",
            showInNavBar: true,
            badgeType: "newUpdate",
            toolTipDescription: t(
              "groups.UserType.ChamberAdmin.RedirectUri.manageAMemberChamberAdmin.toolTipDescription"
            ),
            description: t(
              "groups.UserType.ChamberAdmin.RedirectUri.manageAMemberChamberAdmin.description"
            ),
            nestedRoutes: [
              {
                route: RedirectUri.createAMemberChamberAdmin,
                iconName: "PersonAddOutlinedIcon",
                title: t(
                  "groups.UserType.ChamberAdmin.RedirectUri.createAMemberChamberAdmin.title"
                ),
                showInNavBar: true,
              },
              {
                route: RedirectUri.manageAMemberChamberAdmin,
                iconName: "GroupOutlinedIcon",
                title: t(
                  "groups.UserType.ChamberAdmin.RedirectUri.manageAMemberChamberAdmin.title"
                ),
                showInNavBar: true,
              },
            ],
          },
          [RedirectUri.sendMemberCredentials]: {
            route: RedirectUri.sendMemberCredentials,
            iconName: "SendMemberCredentials",
            title: "Send Member Credentials",
            showInNavBar: true,
            isNew: true,
            toolTipDescription: "Send login credentials to your chamber members",
            description: "Send login credentials to chamber members via email",
          },
          [RedirectUri.memberDiscountCreatePageChamberAdmin]: {
            route: RedirectUri.memberDiscountCreatePageChamberAdmin,
            iconName: "AddCircleOutlineIcon",
            title: t(
              "groups.UserType.ChamberAdmin.RedirectUri.memberDiscountCreatePageChamberAdmin.title"
            ),
            showInNavBar: false,
            toolTipDescription: t(
              "groups.UserType.ChamberAdmin.RedirectUri.memberDiscountCreatePageChamberAdmin.toolTipDescription"
            ),
            description: t(
              "groups.UserType.ChamberAdmin.RedirectUri.memberDiscountCreatePageChamberAdmin.description"
            ),
          },
          [RedirectUri.memberDiscountPageChamberAdmin]: {
            route: RedirectUri.memberDiscountPageChamberAdmin,
            iconName: "CardGiftcardOutlinedIcon",
            title: "Perks",
            showInNavBar: true,
            badgeType: "newUpdate",
            toolTipDescription: t(
              "groups.UserType.ChamberAdmin.RedirectUri.memberDiscountPageChamberAdmin.toolTipDescription"
            ),
            description: t(
              "groups.UserType.ChamberAdmin.RedirectUri.memberDiscountPageChamberAdmin.description"
            ),
            nestedRoutes: [
              {
                route: RedirectUri.memberDiscountCreatePageChamberAdmin,
                iconName: "AddCircleOutlineIcon",
                title: t(
                  "groups.UserType.ChamberAdmin.RedirectUri.memberDiscountCreatePageChamberAdmin.title"
                ),
                showInNavBar: true,
              },
              {
                route: RedirectUri.memberDiscountPageChamberAdmin,
                iconName: "ListAltOutlinedIcon",
                title: t(
                  "groups.UserType.ChamberAdmin.RedirectUri.memberDiscountPageChamberAdmin.title"
                ),
                showInNavBar: true,
              },
            ],
          },
          [RedirectUri.perksLink]: {
            route: RedirectUri.perksLink,
            iconName: "PerkLinkIcon",
            title: t(
              "groups.UserType.ChamberAdmin.RedirectUri.perksLink.title"
            ),
            showInNavBar: true,
            badgeType: "newUpdate",
            toolTipDescription: "",
            description: "",
          },
          [RedirectUri.chamberReward]: {
            route: RedirectUri.chamberReward,
            iconName: "RewardIcon",
            title: "Rewards Campaign",
            showInNavBar: true,
            badgeType: "newUpdate",
            toolTipDescription: "",
            description: "",
            onlyActiveForSubscriptionPlanTypes: [
              ChamberPerksAppSubscriptionPlanTypesEnum.Standard,
            ],
            disabledForSubscriptionPopupText: t(
              "groups.UserType.ChamberAdmin.RedirectUri.chamberReward.disabledForSubscriptionPopupText"
            ),
            nestedRoutes: [
              {
                route: RedirectUri.createReward,
                iconName: null,
                title: t(
                  "groups.UserType.ChamberAdmin.RedirectUri.createReward.title"
                ),
                showInNavBar: true,
              },
              {
                route: RedirectUri.activeChamberReward,
                iconName: null,
                title: t(
                  "groups.UserType.ChamberAdmin.RedirectUri.activeChamberReward.title"
                ),
                showInNavBar: true,
              },
              {
                route: RedirectUri.chamberReward,
                iconName: null,
                title: t("rewardCampaign.inactiveTitle"),
                showInNavBar: true,
              },
            ],
          },
          [RedirectUri.createReward]: {
            route: RedirectUri.createReward,
            iconName: null,
            title: t(
              "groups.UserType.ChamberAdmin.RedirectUri.createReward.title"
            ),
            showInNavBar: false,
            toolTipDescription: "lorem ipsum",
            description: t(
              "groups.UserType.ChamberAdmin.RedirectUri.createReward.description"
            ),
          },
          [RedirectUri.updateReward]: {
            route: RedirectUri.updateReward,
            iconName: null,
            title: t(
              "groups.UserType.ChamberAdmin.RedirectUri.updateReward.title"
            ),
            showInNavBar: false,
            toolTipDescription: "lorem ipsum",
            description: t(
              "groups.UserType.ChamberAdmin.RedirectUri.updateReward.description"
            ),
          },
          [RedirectUri.activeChamberReward]: {
            route: RedirectUri.activeChamberReward,
            iconName: null,
            title: t(
              "groups.UserType.ChamberAdmin.RedirectUri.activeChamberReward.title"
            ),
            showInNavBar: false,
            toolTipDescription: "lorem ipsum",
            description: t(
              "groups.UserType.ChamberAdmin.RedirectUri.activeChamberReward.description"
            ),
          },
          [RedirectUri.chamberShopLocal]: {
            route: RedirectUri.chamberShopLocal,
            iconName: "ShopLocalIcon",
            title: t(
              "groups.UserType.ChamberMemberAdmin.RedirectUri.chamberMemberShopLocal.title"
            ),
            showInNavBar: true,
            badgeType: "newUpdate",
            toolTipDescription: "",
            description: "",
            nestedRoutes: [
              {
                route: RedirectUri.createShopLocal,
                iconName: null,
                title: "Create New Shop Local Contest",
                showInNavBar: true,
              },
              {
                route: RedirectUri.activeChamberShopLocal,
                iconName: null,
                title: "View Active Shop Local Contest",
                showInNavBar: true,
              },
              {
                route: RedirectUri.chamberShopLocal,
                iconName: null,
                title: "View Previous Shop Local Contests",
                showInNavBar: true,
              },
            ],
          },
          [RedirectUri.createShopLocal]: {
            route: RedirectUri.createShopLocal,
            iconName: "AddCircleOutlineIcon",
            title: t(
              "groups.UserType.ChamberAdmin.RedirectUri.createShopLocal.title"
            ),
            showInNavBar: false,
            toolTipDescription: "lorem ipsum",
            description: t(
              "groups.UserType.ChamberAdmin.RedirectUri.createShopLocal.description"
            ),
          },
          [RedirectUri.updateShopLocal]: {
            route: RedirectUri.updateShopLocal,
            iconName: null,
            title: t(
              "groups.UserType.ChamberAdmin.RedirectUri.updateShopLocal.title"
            ),
            showInNavBar: false,
            toolTipDescription: "lorem ipsum",
            description: "",
          },
          [RedirectUri.activeChamberShopLocal]: {
            route: RedirectUri.activeChamberShopLocal,
            iconName: null,
            title: t(
              "groups.UserType.ChamberAdmin.RedirectUri.activeChamberShopLocal.title"
            ),
            showInNavBar: false,
            toolTipDescription: "lorem ipsum",
            description: t(
              "groups.UserType.ChamberAdmin.RedirectUri.activeChamberShopLocal.description"
            ),
          },
          [RedirectUri.chamberEngageManageMemberProfileAndPerkBanners]: {
            route: RedirectUri.chamberEngageManageMemberProfileAndPerkBanners,
            iconName: "CustomChamberEngageIcon",
            title: "CHAMBER ENGAGE™",
            useIconAsNavBarButtonTitle: true,
            showInNavBar: true,
            onlyShowOnActiveChamberEngage: true,
            badgeType: "newUpdate",
            description: t(
              "groups.UserType.ChamberAdmin.RedirectUri.chamberEngageManageMemberProfileAndPerkBanners.description"
            ),
            nestedRoutes: [
              {
                route: RedirectUri.chamberEngageManageMemberProfileAndPerkBanners,
                title: t(
                  "groups.UserType.ChamberAdmin.RedirectUri.chamberEngageManageMemberProfileAndPerkBanners.navTitle"
                ),
                showInNavBar: true,
                iconName: null,
                nestedRoutes: [
                  {
                    route: RedirectUri.chamberEngageActivateFeaturedMember,
                    title: t(
                      "groups.UserType.ChamberAdmin.RedirectUri.chamberEngageActivateFeaturedMember.navTitle"
                    ),
                    showInNavBar: true,
                    iconName: "",
                    refreshOnClick: true,
                  },
                  {
                    route: RedirectUri.chamberEngageAddMemberBanners,
                    title: t(
                      "groups.UserType.ChamberAdmin.RedirectUri.chamberEngageAddMemberBanners.navTitle"
                    ),
                    showInNavBar: true,
                    iconName: "",
                    refreshOnClick: true,
                  },
                  {
                    route: RedirectUri.chamberEngageManageFeaturedMemberActivations,
                    title: t(
                      "groups.UserType.ChamberAdmin.RedirectUri.chamberEngageManageFeaturedMemberActivations.navTitle"
                    ),
                    showInNavBar: true,
                    iconName: "",
                    refreshOnClick: true,
                  },
                ],
              },
              {
                route: RedirectUri.chamberEngageManageCategoryAdvertisingBanners,
                title: t(
                  "groups.UserType.ChamberAdmin.RedirectUri.chamberEngageManageCategoryAdvertisingBanners.navTitle"
                ),
                showInNavBar: true,
                iconName: null,
                nestedRoutes: [
                  {
                    route: RedirectUri.chamberEngageActivateCategoryBanners,
                    title: t(
                      "groups.UserType.ChamberAdmin.RedirectUri.chamberEngageActivateCategoryBanners.navTitle"
                    ),
                    showInNavBar: true,
                    iconName: "",
                    refreshOnClick: true,
                  },
                  {
                    route: RedirectUri.chamberEngageManageCategoryBannersActivations,
                    title: t(
                      "groups.UserType.ChamberAdmin.RedirectUri.chamberEngageManageCategoryBannersActivations.navTitle"
                    ),
                    showInNavBar: true,
                    iconName: "",
                    refreshOnClick: true,
                  },
                ],
              },
            ],
          },
          [RedirectUri.chamberEngageManageCategoryAdvertisingBanners]: {
            route: RedirectUri.chamberEngageManageCategoryAdvertisingBanners,
            iconName: null,
            title: t(
              "groups.UserType.RootAdmin.RedirectUri.chamberEngageManageCategoryAdvertisingBanners.title"
            ),
            showInNavBar: false,
            description: t(
              "groups.UserType.RootAdmin.RedirectUri.chamberEngageManageCategoryAdvertisingBanners.description"
            ),
          },
          [RedirectUri.chamberEngageActivateFeaturedMember]: {
            route: RedirectUri.chamberEngageActivateFeaturedMember,
            iconName: null,
            title: "Activate Enhanced Member Profile & Perks Banners",
            showInNavBar: false,
            description: "Activate featured member profile and perk display for members.",
          },
          [RedirectUri.chamberEngageManageFeaturedMemberActivations]: {
            route: RedirectUri.chamberEngageManageFeaturedMemberActivations,
            iconName: null,
            title: "Manage Featured Member Activations",
            showInNavBar: false,
            description: "View and manage active featured member profile and perk banner activations.",
          },
          [RedirectUri.chamberEngageActivateCategoryBanners]: {
            route: RedirectUri.chamberEngageActivateCategoryBanners,
            iconName: null,
            title: "Activate Category Advertising Banners",
            showInNavBar: false,
            description: "Activate category advertising banners for members.",
          },
          [RedirectUri.chamberEngageManageCategoryBannersActivations]: {
            route: RedirectUri.chamberEngageManageCategoryBannersActivations,
            iconName: null,
            title: "Manage Category Banner Activations",
            showInNavBar: false,
            description: "View and manage active category advertising banner activations.",
          },
          [RedirectUri.chamberEngageAddMemberBanners]: {
            route: RedirectUri.chamberEngageAddMemberBanners,
            iconName: null,
            title: "Add Member Banners",
            showInNavBar: false,
            description: "Add member profile and perk banners for members.",
          },
          [RedirectUri.perkPartnersPortal]: {
            route: RedirectUri.perkPartnersPortal,
            iconName: null,
            title: t(
              "groups.UserType.ChamberAdmin.RedirectUri.perkPartnersPortal.navTitle"
            ),
            showInNavBar: true,
            isNew: true,
            badgeType: "new",
            toolTipDescription: "",
            description: "",
            nestedRoutes: [
              {
                route: RedirectUri.perkPartnersActivateAffinity,
                iconName: null,
                title: t(
                  "groups.UserType.ChamberAdmin.RedirectUri.perkPartnersActivateAffinity.navTitle"
                ),
                showInNavBar: true,
              },
              {
                route: RedirectUri.perkPartnersManageActivations,
                iconName: null,
                title: t(
                  "groups.UserType.ChamberAdmin.RedirectUri.perkPartnersManageActivations.navTitle"
                ),
                showInNavBar: true,
              },
            ],
          },
          [RedirectUri.perkPartnersActivateAffinity]: {
            route: RedirectUri.perkPartnersActivateAffinity,
            iconName: null,
            title: t(
              "groups.UserType.ChamberAdmin.RedirectUri.perkPartnersActivateAffinity.navTitle"
            ),
            showInNavBar: false,
            toolTipDescription: "",
            description: "",
          },
          [RedirectUri.perkPartnersManageActivations]: {
            route: RedirectUri.perkPartnersManageActivations,
            iconName: null,
            title: t(
              "groups.UserType.ChamberAdmin.RedirectUri.perkPartnersManageActivations.navTitle"
            ),
            showInNavBar: false,
            toolTipDescription: "",
            description: "",
          },
          [RedirectUri.perkPartnersUpdateAffinity]: {
            route: RedirectUri.perkPartnersUpdateAffinity,
            iconName: null,
            title: t(
              "groups.UserType.ChamberAdmin.RedirectUri.perkPartnersUpdateAffinity.navTitle"
            ),
            showInNavBar: false,
            toolTipDescription: "",
            description: "",
          },
          [RedirectUri.perkPartnersViewStats]: {
            route: RedirectUri.perkPartnersViewStats,
            iconName: null,
            title: t(
              "groups.UserType.ChamberAdmin.RedirectUri.perkPartnersViewStats.navTitle"
            ),
            showInNavBar: false,
            toolTipDescription: "",
            description: "",
          },
          [RedirectUri.cpaMarketingToolkitFreshdesk]: {
            route: RedirectUri.cpaMarketingToolkitFreshdesk,
            iconName: "CustomChamberMarketerFreshdeskLogo",
            title: t(
              "groups.UserType.ChamberAdmin.RedirectUri.cpaMarketingToolkit.title"
            ),
            useIconAsNavBarButtonTitle: true,
            showInNavBar: true,
            isNew: true,
            overrideLink: "https://chamberperksapp.freshdesk.com/support/home",
          },
          [RedirectUri.support]: {
            route: RedirectUri.support,
            iconName: "CustomSupport",
            title: t("groups.UserType.ChamberAdmin.RedirectUri.support.title"),
            showInNavBar: false,
            forceOpenInNewTab: true,
            overrideLink:
              "https://chamberperksapp.freshdesk.com/support/tickets/new",
          },
          [RedirectUri.editDiscountPageChamberAdmin]: {
            route: RedirectUri.editDiscountPageChamberAdmin,
            iconName: null,
            title: t(
              "groups.UserType.ChamberAdmin.RedirectUri.editDiscountPageChamberAdmin.title"
            ),
            showInNavBar: false,
            toolTipDescription: "lorem ipsum",
            description: t(
              "groups.UserType.ChamberAdmin.RedirectUri.editDiscountPageChamberAdmin.description"
            ),
          },
          [RedirectUri.updateAMemberChamberAdmin]: {
            route: RedirectUri.updateAMemberChamberAdmin,
            iconName: null,
            title: t(
              "groups.UserType.ChamberAdmin.RedirectUri.updateAMemberChamberAdmin.title"
            ),
            showInNavBar: false,
            toolTipDescription: "lorem ipsum",
            description: "",
          },
          [RedirectUri.cpacreateperk]: {
            route: RedirectUri.cpacreateperk,
            iconName: null,
            title: "",
            showInNavBar: false,
            toolTipDescription: "",
            description: "",
          },
        },
        [UserType.ChamberMemberAdmin]: { //check 
          [RedirectUri.manageCompanyProfileChamberMemberAdmin]: {
            route: RedirectUri.manageCompanyProfileChamberMemberAdmin,
            iconName: "CustomManageCompanyProfile",
            title: t(
              "groups.UserType.ChamberMemberAdmin.RedirectUri.updateAMemberChamberAdmin.title"
            ),
            showEnhancedBannersPill: true,
            showInNavBar: true,
            toolTipDescription: t(
              "groups.UserType.ChamberMemberAdmin.RedirectUri.updateAMemberChamberAdmin.toolTipDescription"
            ),
            description: t(
              "groups.UserType.ChamberMemberAdmin.RedirectUri.updateAMemberChamberAdmin.description"
            ),
          },
          [RedirectUri.updateAMemberChamberAdmin]: {
            route: RedirectUri.updateAMemberChamberAdmin,
            iconName: "CustomManageCompanyProfile",
            title: t(
              "groups.UserType.ChamberMemberAdmin.RedirectUri.updateAMemberChamberAdmin.title"
            ),
            showEnhancedBannersPill: true,
            showInNavBar: false,
            toolTipDescription: t(
              "groups.UserType.ChamberMemberAdmin.RedirectUri.updateAMemberChamberAdmin.toolTipDescription"
            ),
            description: t(
              "groups.UserType.ChamberMemberAdmin.RedirectUri.updateAMemberChamberAdmin.description"
            ),
          },
          [RedirectUri.createDiscountChamberMemberAdmin]: {
            route: RedirectUri.createDiscountChamberMemberAdmin,
            iconName: "CustomAddIcon",
            title: t(
              "groups.UserType.ChamberMemberAdmin.RedirectUri.createDiscountChamberMemberAdmin.title"
            ),
            showEnhancedBannersPill: true,
            showInNavBar: true,
            toolTipDescription: t(
              "groups.UserType.ChamberMemberAdmin.RedirectUri.createDiscountChamberMemberAdmin.toolTipDescription"
            ),
            description: t(
              "groups.UserType.ChamberMemberAdmin.RedirectUri.createDiscountChamberMemberAdmin.description"
            ),
          },
          [RedirectUri.manageDiscountChamberMemberAdmin]: {
            route: RedirectUri.manageDiscountChamberMemberAdmin,
            iconName: "CustomManageCompanyPerks",
            title: t(
              "groups.UserType.ChamberMemberAdmin.RedirectUri.manageDiscountChamberMemberAdmin.title"
            ),
            showInNavBar: true,
            toolTipDescription: t(
              "groups.UserType.ChamberMemberAdmin.RedirectUri.manageDiscountChamberMemberAdmin.toolTipDescription"
            ),
            description: t(
              "groups.UserType.ChamberMemberAdmin.RedirectUri.manageDiscountChamberMemberAdmin.description"
            ),
          },
          [RedirectUri.editDiscountChamberMemberAdmin]: {
            route: RedirectUri.editDiscountChamberMemberAdmin,
            iconName: null,
            title: t(
              "groups.UserType.ChamberMemberAdmin.RedirectUri.editDiscountChamberMemberAdmin.title"
            ),
            showInNavBar: null,
            toolTipDescription: t(
              "groups.UserType.ChamberMemberAdmin.RedirectUri.editDiscountChamberMemberAdmin.toolTipDescription"
            ),
            description: t(
              "groups.UserType.ChamberMemberAdmin.RedirectUri.editDiscountChamberMemberAdmin.description"
            ),
          },
          [RedirectUri.chamberEngageManageCategoryAdvertisingBannersChamberMember]:
          {
            route:
              RedirectUri.chamberEngageManageCategoryAdvertisingBannersChamberMember,
            iconName:
              "ChamberEngageManageCategoryAdvertisingBannersChamberMemberIcon",
            title: t(
              "groups.UserType.ChamberMemberAdmin.RedirectUri.chamberEngageManageCategoryAdvertisingBannersChamberMember.title"
            ),
            showEnhancedBannersPill: true,
            showInNavBar: true,
            toolTipDescription: t(
              "groups.UserType.ChamberMemberAdmin.RedirectUri.chamberEngageManageCategoryAdvertisingBannersChamberMember.toolTipDescription"
            ),
            description: t(
              "groups.UserType.ChamberMemberAdmin.RedirectUri.chamberEngageManageCategoryAdvertisingBannersChamberMember.description"
            ),
            refreshOnClick: true,
          },
          [RedirectUri.memberInvoices]: {
            route: RedirectUri.memberInvoices,
            iconName: "Description",
            title: "Invoices & Payments",
            showInNavBar: true,
            toolTipDescription: "View and pay your invoices",
            description: "",
          },
          [RedirectUri.memberEvents]: {
            route: RedirectUri.memberEvents,
            iconName: "Description",
            title: "Register for Events",
            showInNavBar: false,
            toolTipDescription: "Register for events and update attendee details",
            description: "",
          },
          [RedirectUri.manageFeaturedMemberBannersChamberMember]: {
            route: RedirectUri.manageFeaturedMemberBannersChamberMember,
            iconName: "ManageFeaturedMemberBannersIcon",
            title: "Manage Featured Member Banners",
            showInNavBar: false,
            toolTipDescription: "Manage Featured Member Banners",
            description: "Manage Featured Member Banners",
          },
          [RedirectUri.chamberMemberShopLocal]: {
            route: RedirectUri.chamberMemberShopLocal,
            iconName: "ShopLocalIcon",
            title: t(
              "groups.UserType.ChamberMemberAdmin.RedirectUri.chamberMemberShopLocal.title"
            ),
            showInNavBar: true,
            toolTipDescription: t(
              "groups.UserType.ChamberMemberAdmin.RedirectUri.chamberMemberShopLocal.toolTipDescription"
            ),
            description: t(
              "groups.UserType.ChamberMemberAdmin.RedirectUri.chamberMemberShopLocal.description"
            ),
          },
          [RedirectUri.cpaMarketingToolkit]: {
            route: RedirectUri.cpaMarketingToolkit,
            iconName: "CustomChamberMarketerFreshdeskLogo",
            title: t(
              "groups.UserType.ChamberMemberAdmin.RedirectUri.cpaMarketingToolkit.title"
            ),
            useIconAsNavBarButtonTitle: true,
            showInNavBar: false,
            onlyHideOnSandbox: true,
            isNew: true,
            toolTipDescription: t(
              "groups.UserType.ChamberMemberAdmin.RedirectUri.cpaMarketingToolkit.toolTipDescription"
            ),
            description: t(
              "groups.UserType.ChamberMemberAdmin.RedirectUri.cpaMarketingToolkit.description"
            ),
          },
          [RedirectUri.settingsChamberMemberAdmin]: {
            route: RedirectUri.settingsChamberMemberAdmin,
            iconName: "CustomSupport",
            title: t(
              "groups.UserType.ChamberMemberAdmin.RedirectUri.settingsChamberMemberAdmin.title"
            ),
            showInNavBar: true,
            onlyHideOnSandbox: true,
            toolTipDescription: t(
              "groups.UserType.ChamberMemberAdmin.RedirectUri.settingsChamberMemberAdmin.toolTipDescription"
            ),
            description: t(
              "groups.UserType.ChamberMemberAdmin.RedirectUri.settingsChamberMemberAdmin.description"
            ),
          },
          [RedirectUri.cpaMarketingToolkitFreshdesk]: {
            route: RedirectUri.cpaMarketingToolkitFreshdesk,
            iconName: "CustomChamberMarketerFreshdeskLogo",
            title: t(
              "groups.UserType.ChamberAdmin.RedirectUri.cpaMarketingToolkit.title"
            ),
            useIconAsNavBarButtonTitle: true,
            showInNavBar: false,
            onlyShowOnSandbox: true,
            isNew: true,
            overrideLink: "https://chamberperksapp.freshdesk.com/support/home",
          },
          [RedirectUri.support]: {
            route: RedirectUri.support,
            iconName: "CustomSupport",
            title: `${t(
              "groups.UserType.ChamberAdmin.RedirectUri.support.title"
            )} (Freshdesk)`,
            showInNavBar: false,
            onlyShowOnSandbox: true,
            forceOpenInNewTab: true,
            overrideLink:
              "https://chamberperksapp.freshdesk.com/support/tickets/new",
          },

          [RedirectUri.cpacreateperk]: {
            route: RedirectUri.cpacreateperk,
            iconName: null,
            title: "",
            showInNavBar: false,
            toolTipDescription: "",
            description: "",
          },
        },
      };

      return {
        ...strings,
        groups,
        navigation: groups,

        settings: {
          settingTitle: t("settings.settingTitle"),
          chooseLanguage: t("settings.chooseLanguage"),
          update: t("settings.update"),
          changeMemberloginGuide: t("settings.changeMemberloginGuide"),
          supportContact: t("settings.supportContact"),
          contact: t("settings.contact"),
          loading: t("settings.loading"),
        },
        sidebarText: {
          sponsorTitle: t("sidebarText.sponsorTitle"),
          settingTitle: t("sidebarText.settingTitle"),
          needHelp: t("sidebarText.needHelp"),
          createTicketFromHere: t("sidebarText.createTicketFromHere"),
          logoutTitle: t("sidebarText.logoutTitle"),
        },
        profile: {
          changeLogoTitle: t("profile.changeLogoTitle"),
          sponsorLogoTitle: t("profile.sponsorLogoTitle"),
          profileBannerTitle: t("profile.profileBannerTitle"),
          uploadLogo: t("profile.uploadLogo"),
        },
        chamberMemberTableLabel: {
          name: t("chamberMemberTableLabel.name"),
          numberOfPerksOffered: t("chamberMemberTableLabel.numberOfPerksOffered"),
          numberOfUsers: t("chamberMemberTableLabel.numberOfUsers"),
          hasSignedUp: t("chamberMemberTableLabel.hasSignedUp"),
          totalApp: t("chamberMemberTableLabel.totalApp"),
          searchPlaceholder: t("chamberMemberTableLabel.searchPlaceholder"),
          setMemberShip: t("chamberMemberTableLabel.setMemberShip"),
          lastSent: t("chamberMemberTableLabel.lastSent"),
          hasLoggedIn: t("chamberMemberTableLabel.hasLoggedIn"),
          neverLoggedIn: t("chamberMemberTableLabel.neverLoggedIn"),
          sendCredentials: t("chamberMemberTableLabel.sendCredentials"),
          resendCredentials: t("chamberMemberTableLabel.resendCredentials"),
        },
        ChamberAdminListAllChamberMembers: {
          exportMembers: t("ChamberAdminListAllChamberMembers.exportMembers"),
          howMembersLogIntoTheirMemberDashboard: t(
            "ChamberAdminListAllChamberMembers.howMembersLogIntoTheirMemberDashboard"
          ),
        },
        ChamberAdminMangeMember: {
          exportMemberLabel: t("ChamberAdminMangeMember.exportMemberLabel"),
          chamberMemberName: t("ChamberAdminMangeMember.chamberMemberName"),
          chamberMemberNameDesc: t("ChamberAdminMangeMember.chamberMemberNameDesc"),
          title: t("ChamberAdminMangeMember.title"),
          titleDesc: t("ChamberAdminMangeMember.titleDesc"),
          offeredToText: t("ChamberAdminMangeMember.offeredToText"),
          offeredToTextDesc: t("ChamberAdminMangeMember.offeredToTextDesc"),
          redemptionMethod: t("ChamberAdminMangeMember.redemptionMethod"),
          redemptionMethodDesc: t("ChamberAdminMangeMember.redemptionMethodDesc"),
          views: t("ChamberAdminMangeMember.views"),
          viewsDesc: t("ChamberAdminMangeMember.viewsDesc"),
          searchPlaceholder: t("ChamberAdminMangeMember.searchPlaceholder"),
          description: t("ChamberAdminMangeMember.description"),
        },
        ChamberAdminManage: {
          chamberBoardName: t("ChamberAdminManage.chamberBoardName"),
          chamberBoardDescription: t(
            "ChamberAdminManage.chamberBoardDescription"
          ),
          chamberWebsite: t("ChamberAdminManage.chamberWebsite"),
          chamberSupportContactName: t(
            "ChamberAdminManage.chamberSupportContactName"
          ),
          chamberSupportContactEmail: t(
            "ChamberAdminManage.chamberSupportContactEmail"
          ),
          chamberSupportPageURL: t("ChamberAdminManage.chamberSupportPageURL"),
          chamberPhoneNumber: t("ChamberAdminManage.chamberPhoneNumber"),
          chamberStreetAddress: t("ChamberAdminManage.chamberStreetAddress"),
          chamberCity: t("ChamberAdminManage.chamberCity"),
          chamberPostalZipCode: t("ChamberAdminManage.chamberPostalZipCode"),
          chamberFacebookBusinessPageURL: t(
            "ChamberAdminManage.chamberFacebookBusinessPageURL"
          ),
          chamberInstagramAccountURL: t(
            "ChamberAdminManage.chamberInstagramAccountURL"
          ),
          chamberThreadsAccountURL: t(
            "ChamberAdminManage.chamberThreadsAccountURL"
          ),
          chamberLinkedInPageURL: t("ChamberAdminManage.chamberLinkedInPageURL"),
          chamberTwitterAccountURL: t(
            "ChamberAdminManage.chamberTwitterAccountURL"
          ),
          chamberTikTokAccountURL: t(
            "ChamberAdminManage.chamberTikTokAccountURL"
          ),
          chamberYoutubeChannelURL: t(
            "ChamberAdminManage.chamberYoutubeChannelURL"
          ),
          chamberJobPostingLink: t("ChamberAdminManage.chamberJobPostingLink"),
          chamberEventsLink: t("ChamberAdminManage.chamberEventsLink"),
          chamberNewsletterLink: t("ChamberAdminManage.chamberNewsletterLink"),
          chamberSurveyLink: t("ChamberAdminManage.chamberSurveyLink"),
          chamberCallForNominationsLink: t(
            "ChamberAdminManage.chamberCallForNominationsLink"
          ),
          chamberNewMemberSignupLink: t(
            "ChamberAdminManage.chamberNewMemberSignupLink"
          ),
          chamberSponsorLogoLink: t("ChamberAdminManage.chamberSponsorLogoLink"),
          chamberProfileBanners: t("ChamberAdminManage.chamberProfileBanners"),
          chamberMainDirectoryBanners: t(
            "ChamberAdminManage.chamberMainDirectoryBanners"
          ),
          chamberInfoDesc: t("ChamberAdminManage.chamberInfoDesc"),
          profileUpdatedSuccessfully: t("ChamberAdminManage.profileUpdatedSuccessfully"),
          chamberDescriptionLabel: t("ChamberAdminManage.chamberDescriptionLabel"),
          chamberDescriptionSubLabel: t("ChamberAdminManage.chamberDescriptionSubLabel"),
          chamberDescriptionHelper: t("ChamberAdminManage.chamberDescriptionHelper"),
          idaChamberTitle: t("ChamberAdminManage.idaChamberTitle"),
          idaChamberSubtitle: t("ChamberAdminManage.idaChamberSubtitle"),
          myChamberLinksDesc: t("ChamberAdminManage.myChamberLinksDesc"),
          myChamberSection: {
            title: t("ChamberAdminManage.myChamberSection.title"),
            subtitle: t("ChamberAdminManage.myChamberSection.subtitle"),
          },
          directoryBannersSubscriptionPlanLimitationModal: {
            title: t(
              "ChamberAdminManage.directoryBannersSubscriptionPlanLimitationModal.title"
            ),
            description: t(
              "ChamberAdminManage.directoryBannersSubscriptionPlanLimitationModal.description"
            ),
            okButton: t(
              "ChamberAdminManage.directoryBannersSubscriptionPlanLimitationModal.okButton"
            ),
          },
          sections: {
            chamberInformationTitle: t("ChamberAdminManage.sections.chamberInformationTitle"),
            chamberInformationDescPart1: t("ChamberAdminManage.sections.chamberInformationDescPart1"),
            chamberInformationDescPart2: t("ChamberAdminManage.sections.chamberInformationDescPart2"),
            adminContactName: t("ChamberAdminManage.sections.adminContactName"),
            adminContactNameHelper: t("ChamberAdminManage.sections.adminContactNameHelper"),
            adminContactNamePlaceholder: t("ChamberAdminManage.sections.adminContactNamePlaceholder"),
            adminContactEmail: t("ChamberAdminManage.sections.adminContactEmail"),
            adminContactEmailHelper: t("ChamberAdminManage.sections.adminContactEmailHelper"),
            adminContactEmailPlaceholder: t("ChamberAdminManage.sections.adminContactEmailPlaceholder"),
            billingContactName: t("ChamberAdminManage.sections.billingContactName"),
            billingContactNamePlaceholder: t("ChamberAdminManage.sections.billingContactNamePlaceholder"),
            billingContactEmail: t("ChamberAdminManage.sections.billingContactEmail"),
            billingContactEmailPlaceholder: t("ChamberAdminManage.sections.billingContactEmailPlaceholder"),
            signingAuthorityContactName: t("ChamberAdminManage.sections.signingAuthorityContactName"),
            signingAuthorityContactNamePlaceholder: t("ChamberAdminManage.sections.signingAuthorityContactNamePlaceholder"),
            signingAuthorityContactEmail: t("ChamberAdminManage.sections.signingAuthorityContactEmail"),
            signingAuthorityContactEmailPlaceholder: t("ChamberAdminManage.sections.signingAuthorityContactEmailPlaceholder"),
            chamberProfileOnCpa: t("ChamberAdminManage.sections.chamberProfileOnCpa"),
            chamberProfileOnCpaDesc: t("ChamberAdminManage.sections.chamberProfileOnCpaDesc"),
            adminContactNameRequired: t("ChamberAdminManage.sections.adminContactNameRequired"),
            billingContactNameRequired: t("ChamberAdminManage.sections.billingContactNameRequired"),
            billingContactEmailRequired: t("ChamberAdminManage.sections.billingContactEmailRequired"),
            signingAuthorityContactNameRequired: t("ChamberAdminManage.sections.signingAuthorityContactNameRequired"),
            signingAuthorityContactEmailRequired: t("ChamberAdminManage.sections.signingAuthorityContactEmailRequired"),
            validEmailRequired: t("ChamberAdminManage.sections.validEmailRequired"),
          },
          chamberStreetAddressOrPOBox: t("ChamberAdminManage.chamberStreetAddressOrPOBox"),
          chamberProvinceState: t("ChamberAdminManage.chamberProvinceState"),
          chamberEventsLinkURL: t("ChamberAdminManage.chamberEventsLinkURL"),
          chamberNewsletterLinkURL: t("ChamberAdminManage.chamberNewsletterLinkURL"),
          chamberJobPostingLinkURL: t("ChamberAdminManage.chamberJobPostingLinkURL"),
          chamberSurveyLinkURL: t("ChamberAdminManage.chamberSurveyLinkURL"),
          chamberAdvocacyWinsLinkURL: t("ChamberAdminManage.chamberAdvocacyWinsLinkURL"),
          chamberNewMemberSignupLinkURL: t("ChamberAdminManage.chamberNewMemberSignupLinkURL"),
          chamberCallForNominationsLinkURL: t("ChamberAdminManage.chamberCallForNominationsLinkURL"),
          deleteLogo: t("ChamberAdminManage.deleteLogo"),
          deleteSponsorLogo: t("ChamberAdminManage.deleteSponsorLogo"),
          placeholders: {
            enterChamberName: t("ChamberAdminManage.placeholders.enterChamberName"),
            enterWebsiteUrl: t("ChamberAdminManage.placeholders.enterWebsiteUrl"),
            enterDetailsAboutYourChamber: t("ChamberAdminManage.placeholders.enterDetailsAboutYourChamber"),
            enterCompanyContactPhone: t("ChamberAdminManage.placeholders.enterCompanyContactPhone"),
            enterGoogleReviewUrl: t("ChamberAdminManage.placeholders.enterGoogleReviewUrl"),
            enterCompanyContactEmail: t("ChamberAdminManage.placeholders.enterCompanyContactEmail"),
            enterChamberSupportPageUrl: t("ChamberAdminManage.placeholders.enterChamberSupportPageUrl"),
            enterStreetAddress: t("ChamberAdminManage.placeholders.enterStreetAddress"),
            enterCity: t("ChamberAdminManage.placeholders.enterCity"),
            enterPostalCode: t("ChamberAdminManage.placeholders.enterPostalCode"),
            enterFacebookUrl: t("ChamberAdminManage.placeholders.enterFacebookUrl"),
            enterInstagramUrl: t("ChamberAdminManage.placeholders.enterInstagramUrl"),
            enterThreadsUrl: t("ChamberAdminManage.placeholders.enterThreadsUrl"),
            enterLinkedinUrl: t("ChamberAdminManage.placeholders.enterLinkedinUrl"),
            enterTwitterUrl: t("ChamberAdminManage.placeholders.enterTwitterUrl"),
            enterTiktokUrl: t("ChamberAdminManage.placeholders.enterTiktokUrl"),
            enterYoutubeUrl: t("ChamberAdminManage.placeholders.enterYoutubeUrl"),
            enterEventsLinkUrl: t("ChamberAdminManage.placeholders.enterEventsLinkUrl"),
            enterJobPostingLinkUrl: t("ChamberAdminManage.placeholders.enterJobPostingLinkUrl"),
            enterNewsletterLinkUrl: t("ChamberAdminManage.placeholders.enterNewsletterLinkUrl"),
            enterSurveyLinkUrl: t("ChamberAdminManage.placeholders.enterSurveyLinkUrl"),
            enterAdvocacyWinsLinkUrl: t("ChamberAdminManage.placeholders.enterAdvocacyWinsLinkUrl"),
            enterNewMemberSignupLinkUrl: t("ChamberAdminManage.placeholders.enterNewMemberSignupLinkUrl"),
            enterCallForNominationsLinkUrl: t("ChamberAdminManage.placeholders.enterCallForNominationsLinkUrl"),
            enterSponsorLogoWebsiteUrl: t("ChamberAdminManage.placeholders.enterSponsorLogoWebsiteUrl"),
          },
        },
        CreateChamberMember: {
          memberEmailDashboard: t("CreateChamberMember.memberEmailDashboard"),
          memberPassword: t("CreateChamberMember.memberPassword"),
          companyName: t("CreateChamberMember.companyName"),
          memberDescription: t("CreateChamberMember.memberDescription"),
          memberEmail: t("CreateChamberMember.memberEmail"),
          companyContactName: t("CreateChamberMember.companyContactName"),
          companyContactEmail: t("CreateChamberMember.companyContactEmail"),
          companyPhoneNumber: t("CreateChamberMember.companyPhoneNumber"),
          newTempPassword: t("CreateChamberMember.newTempPassword"),
          wantToDelete: t("CreateChamberMember.wantToDelete"),
          howToDownloadChamberPerksApp: t(
            "CreateChamberMember.howToDownloadChamberPerksApp"
          ),
          salesManagerContact: {
            contactName: t("CreateChamberMember.salesManagerContact.contactName"),
            contactEmail: t(
              "CreateChamberMember.salesManagerContact.contactEmail"
            ),
            contactPhoneNumber: t(
              "CreateChamberMember.salesManagerContact.contactPhoneNumber"
            ),
          },
          update: t("CreateChamberMember.update"),
          updateMember: t("CreateChamberMember.updateMember"),
          csvDownload: t("CreateChamberMember.csvDownload"),
          EmployeeHRContact: {
            contactName: t("CreateChamberMember.EmployeeHRContact.contactName"),
            contactEmail: t("CreateChamberMember.EmployeeHRContact.contactEmail"),
            contactPhoneNumber: t(
              "CreateChamberMember.EmployeeHRContact.contactPhoneNumber"
            ),
          },
          MarketingContact: {
            contactName: t("CreateChamberMember.MarketingContact.contactName"),
            contactEmail: t("CreateChamberMember.MarketingContact.contactEmail"),
            contactPhoneNumber: t(
              "CreateChamberMember.MarketingContact.contactPhoneNumber"
            ),
          },
          StreetAddress: t("CreateChamberMember.StreetAddress"),
          Province: t("CreateChamberMember.Province"),
          City: t("CreateChamberMember.City"),
          PostalCode: t("CreateChamberMember.PostalCode"),
          CompanyDescription: t("CreateChamberMember.CompanyDescription"),
          CharactersRemaining: t("CreateChamberMember.CharactersRemaining"),
          CompanyWebsite: t("CreateChamberMember.CompanyWebsite"),
          CompanyFacebook: t("CreateChamberMember.CompanyFacebook"),
          CompanyInstagram: t("CreateChamberMember.CompanyInstagram"),
          CompanyThreads: t("CreateChamberMember.CompanyThreads"),
          CompanyLinkedIn: t("CreateChamberMember.CompanyLinkedIn"),
          CompanyTwitter: t("CreateChamberMember.CompanyTwitter"),
          CompanyTikTok: t("CreateChamberMember.CompanyTikTok"),
          CompanyYoutube: t("CreateChamberMember.CompanyYoutube"),
          CompanyGoogleReviewLink: t(
            "CreateChamberMember.CompanyGoogleReviewLink"
          ),
          MembershipStatus: t("CreateChamberMember.MembershipStatus"),
          Active: t("CreateChamberMember.Active"),
          Suspended: t("CreateChamberMember.Suspended"),
          Create: t("CreateChamberMember.Create"),
        },

        ChamberMemberSelector: {
          selectMemberCompanyName: t("ChamberMemberSelector.selectMemberCompanyName"),
          memberCompanyName: t("ChamberMemberSelector.memberCompanyName"),
          memberCompanyIsRequired: t(
            "ChamberMemberSelector.memberCompanyIsRequired"
          ),
          sponsorCompanyName: t("ChamberMemberSelector.sponsorCompanyName"),
          perkTitle: t("ChamberMemberSelector.perkTitle"),
          perkTitleIsRequired: t("ChamberMemberSelector.perkTitleIsRequired"),
          charactersRemaining: t("ChamberMemberSelector.charactersRemaining"),
          perkDescription: t("ChamberMemberSelector.perkDescription"),
          perkRedeemableBy: t("ChamberMemberSelector.perkRedeemableBy"),
          pleaseSelectAnOption: t("ChamberMemberSelector.pleaseSelectAnOption"),
          anyone: t("ChamberMemberSelector.anyone"),
          allMembers: t("ChamberMemberSelector.allMembers"),
          localMembersOnly: t("ChamberMemberSelector.localMembersOnly"),
          yourCompanyEmployeesOnly: t(
            "ChamberMemberSelector.yourCompanyEmployeesOnly"
          ),
          reoccurringPerk: t("ChamberMemberSelector.reoccurringPerk"),
          oneTimePerk: t("ChamberMemberSelector.oneTimePerk"),
          inPerson: t("ChamberMemberSelector.inPerson"),
          online: t("ChamberMemberSelector.online"),
          contactCompany: t("ChamberMemberSelector.contactCompany"),
          pleaseAddressAnyIssues: t(
            "ChamberMemberSelector.pleaseAddressAnyIssues"
          ),
          create: t("ChamberMemberSelector.create"),
        },
        BannerPicker: {
          previewBanners: t("BannerPicker.previewBanners"),
          delete: {
            deleteTitle: t("BannerPicker.delete.deleteTitle"),
            deleteText: t("BannerPicker.delete.deleteText"),
            deleteDescription: t("BannerPicker.delete.deleteDescription"),
            cancel: t("BannerPicker.delete.cancel"),
            delete: t("BannerPicker.delete.delete"),
          },
          upload: {
            uploadText: t("BannerPicker.upload.uploadText"),
            changeImage: t("BannerPicker.upload.changeImage"),
            deleteImage: t("BannerPicker.upload.deleteImage"),
            clickToUpload: t("BannerPicker.upload.clickToUpload"),
            ImageLink: t("BannerPicker.upload.ImageLink"),
            description: t("BannerPicker.upload.description"),
            clickDescription: t("BannerPicker.upload.clickDescription"),
            saveBanner: t("BannerPicker.upload.saveBanner"),
            updateBanner: t("BannerPicker.upload.updateBanner"),
            youtubeDescription: t("BannerPicker.upload.youtubeDescription"),
            youtubeVideo: t("BannerPicker.upload.youtubeVideo"),
            youtubeDeleteDescription: t(
              "BannerPicker.upload.youtubeDeleteDescription"
            ),
            uploadImageVideo: t("BannerPicker.upload.uploadImageVideo"),
          },
        },
        warningToCreate: t("warningToCreate"),
        RedeemOffer: {
          DepartmentOrContactName: t("RedeemOffer.DepartmentOrContactName"),
          CompanyContactEmail: t("RedeemOffer.CompanyContactEmail"),
          ContactNumber: t("RedeemOffer.ContactNumber"),
        },
        UpdateDiscount: {
          PerkTitle: t("UpdateDiscount.PerkTitle"),
          PerkDescription: t("UpdateDiscount.PerkDescription"),
          CharactersRemaining: t("UpdateDiscount.CharactersRemaining"),
          UpdatedPerksDescription: t("UpdateDiscount.UpdatedPerksDescription"),
          NewPerkDescription: t("UpdateDiscount.NewPerkDescription"),
        },
        CreateDiscount: {
          HowToCreate: t("CreateDiscount.HowToCreate"),
          for: t("CreateDiscount.for"),
          HowToSearchAndRedeemPerks: t(
            "CreateDiscount.HowToSearchAndRedeemPerks"
          ),
        },
        ManageAllCategogry: {
          HowToUpload: t("ManageAllCategogry.HowToUpload"),
        },
        CPAMarketingToolkit: {
          MarketingToolkit: t("CPAMarketingToolkit.MarketingToolkit"),
          ChamberMarketer: t("CPAMarketingToolkit.ChamberMarketer"),
          AccessGuide: t("CPAMarketingToolkit.AccessGuide"),
          Guide1: t("CPAMarketingToolkit.Guide1"),
          Guide2: t("CPAMarketingToolkit.Guide2"),
          Guide3: t("CPAMarketingToolkit.Guide3"),
          Guide4: t("CPAMarketingToolkit.Guide4"),
          Guide5: t("CPAMarketingToolkit.Guide5"),
          Guide5SecondPart: t("CPAMarketingToolkit.Guide5SecondPart"),
          basicPlan: t("CPAMarketingToolkit.basicPlan"),
          premiumPlan: t("CPAMarketingToolkit.premiumPlan"),
        },
        Nav: {
          FeaturedMemberInactive: t("Nav.FeaturedMemberInactive"),
          ToLearnMore: t("Nav.ToLearnMore"),
          FeaturedMemberActive: t("Nav.FeaturedMemberActive"),
        },
        CSVDownloadNotification: {
          title: t("CSVDownloadNotification.title"),
          numOfMembersNotLoggedInYet: t(
            "CSVDownloadNotification.numOfMembersNotLoggedInYet"
          ),
          notLoggedInYet: t("CSVDownloadNotification.notLoggedInYet"),
          activeMembershipStatus: t(
            "CSVDownloadNotification.activeMembershipStatus"
          ),
          downloadInfo: t("CSVDownloadNotification.downloadInfo"),
          downloadsuccess: t("CSVDownloadNotification.downloadsuccess"),
          downloadSuccessDescription: t("CSVDownloadNotification.downloadSuccessDescription"),
          download: t("CSVDownloadNotification.download"),
          close: t("CSVDownloadNotification.close"),
          CredentialsCsv: t("CSVDownloadNotification.CredentialsCsv"),
        },
        DownloadMemberCredentials: {
          mainModal: {
            title: t("DownloadMemberCredentials.mainModal.title"),
            emailInstructions: t(
              "DownloadMemberCredentials.mainModal.emailInstructions"
            ),
            importantLabel: t(
              "DownloadMemberCredentials.mainModal.importantLabel"
            ),
            importantNote: t(
              "DownloadMemberCredentials.mainModal.importantNote"
            ),
            autoSectionTitle: t(
              "DownloadMemberCredentials.mainModal.autoSectionTitle"
            ),
            autoSectionDescription: t(
              "DownloadMemberCredentials.mainModal.autoSectionDescription"
            ),
            manualSectionTitle: t(
              "DownloadMemberCredentials.mainModal.manualSectionTitle"
            ),
            manualSectionDescriptionLine1: t(
              "DownloadMemberCredentials.mainModal.manualSectionDescriptionLine1"
            ),
            manualSectionDescriptionLine2: t(
              "DownloadMemberCredentials.mainModal.manualSectionDescriptionLine2"
            ),
            lastEmailSent: t("DownloadMemberCredentials.mainModal.lastEmailSent"),
            sendAutoButton: t(
              "DownloadMemberCredentials.mainModal.sendAutoButton"
            ),
            previewLink: t("DownloadMemberCredentials.mainModal.previewLink"),
            lastDownloaded: t(
              "DownloadMemberCredentials.mainModal.lastDownloaded"
            ),
            downloadButton: t(
              "DownloadMemberCredentials.mainModal.downloadButton"
            ),
          },
          emailPending: {
            title: t("DownloadMemberCredentials.emailPending.title"),
            description: t("DownloadMemberCredentials.emailPending.description"),
          },
          emailSuccess: {
            title: t("DownloadMemberCredentials.emailSuccess.title"),
            description: t("DownloadMemberCredentials.emailSuccess.description"),
            additionalInfo: t(
              "DownloadMemberCredentials.emailSuccess.additionalInfo"
            ),
          },
          emailConfirmation: {
            title: t("DownloadMemberCredentials.emailConfirmation.title"),
            sendToMembersPrefix: t(
              "DownloadMemberCredentials.emailConfirmation.sendToMembersPrefix"
            ),
            sendToMembersSuffix: t(
              "DownloadMemberCredentials.emailConfirmation.sendToMembersSuffix"
            ),
            member: t("DownloadMemberCredentials.emailConfirmation.member"),
            members: t("DownloadMemberCredentials.emailConfirmation.members"),
            lastSentPrefix: t(
              "DownloadMemberCredentials.emailConfirmation.lastSentPrefix"
            ),
            lastSentSuffix: t(
              "DownloadMemberCredentials.emailConfirmation.lastSentSuffix"
            ),
            neverSent: t("DownloadMemberCredentials.emailConfirmation.neverSent"),
            message: t("DownloadMemberCredentials.emailConfirmation.message"),
            confirmButton: t(
              "DownloadMemberCredentials.emailConfirmation.confirmButton"
            ),
            cancelButton: t(
              "DownloadMemberCredentials.emailConfirmation.cancelButton"
            ),
          },
          downloadConfirmation: {
            title: t("DownloadMemberCredentials.downloadConfirmation.title"),
            downloadForMembersPrefix: t(
              "DownloadMemberCredentials.downloadConfirmation.downloadForMembersPrefix"
            ),
            downloadForMembersSuffix: t(
              "DownloadMemberCredentials.downloadConfirmation.downloadForMembersSuffix"
            ),
            member: t("DownloadMemberCredentials.downloadConfirmation.member"),
            members: t("DownloadMemberCredentials.downloadConfirmation.members"),
            lastSentPrefix: t(
              "DownloadMemberCredentials.downloadConfirmation.lastSentPrefix"
            ),
            lastSentSuffix: t(
              "DownloadMemberCredentials.downloadConfirmation.lastSentSuffix"
            ),
            neverSent: t(
              "DownloadMemberCredentials.downloadConfirmation.neverSent"
            ),
            message: t("DownloadMemberCredentials.downloadConfirmation.message"),
            confirmButton: t(
              "DownloadMemberCredentials.downloadConfirmation.confirmButton"
            ),
            cancelButton: t(
              "DownloadMemberCredentials.downloadConfirmation.cancelButton"
            ),
          },
          disabledButtonTooltip: {
            waitPrefix: t(
              "DownloadMemberCredentials.disabledButtonTooltip.waitPrefix"
            ),
            waitSuffix: t(
              "DownloadMemberCredentials.disabledButtonTooltip.waitSuffix"
            ),
            minute: t("DownloadMemberCredentials.disabledButtonTooltip.minute"),
            minutes: t("DownloadMemberCredentials.disabledButtonTooltip.minutes"),
          },
          timeAgo: {
            day: t("DownloadMemberCredentials.timeAgo.day"),
            days: t("DownloadMemberCredentials.timeAgo.days"),
            hour: t("DownloadMemberCredentials.timeAgo.hour"),
            hours: t("DownloadMemberCredentials.timeAgo.hours"),
            minute: t("DownloadMemberCredentials.timeAgo.minute"),
            minutes: t("DownloadMemberCredentials.timeAgo.minutes"),
            lessThanOneMinute: t(
              "DownloadMemberCredentials.timeAgo.lessThanOneMinute"
            ),
          },
          previousDownloadTitle: t(
            "DownloadMemberCredentials.previousDownloadTitle"
          ),
          previousDownloadTimestamp: t(
            "DownloadMemberCredentials.previousDownloadTimestamp"
          ),
          downloadTitle: t("DownloadMemberCredentials.downloadTitle"),
          notLoggedInYet: t("DownloadMemberCredentials.notLoggedInYet"),
          activeMembershipStatus: t(
            "DownloadMemberCredentials.activeMembershipStatus"
          ),
          spreadsheetInfo: t("DownloadMemberCredentials.spreadsheetInfo"),
          currentStatus: t("DownloadMemberCredentials.currentStatus"),
          numOfMembersNotLoggedInYet: t(
            "DownloadMemberCredentials.numOfMembersNotLoggedInYet"
          ),
          followUpMessage: t("DownloadMemberCredentials.followUpMessage"),
          massEmailingInfo: t("DownloadMemberCredentials.massEmailingInfo"),
          downloadTimeInfo: t("DownloadMemberCredentials.downloadTimeInfo"),
          noteTitle: t("DownloadMemberCredentials.noteTitle"),
          temporaryPasswordNote: t(
            "DownloadMemberCredentials.temporaryPasswordNote"
          ),
          buttons: {
            download: t("DownloadMemberCredentials.buttons.download"),
            close: t("DownloadMemberCredentials.buttons.close"),
          },
          downloadInProgress: t("DownloadMemberCredentials.downloadInProgress"),
          description: t("DownloadMemberCredentials.description"),
          downloadIn5: t("DownloadMemberCredentials.downloadIn5"),
        },
        labels: {
          memberName: t("labels.memberName"),
          membershipStatus: t("labels.membershipStatus"),
          category1: t("labels.category1"),
          category2: t("labels.category2"),
          numberOfPerksOffered: t("labels.numberOfPerksOffered"),
          hasSignedUp: t("labels.hasSignedUp"),
          hasLogo: t("labels.hasLogo"),
          numberOfUsers: t("labels.numberOfUsers"),
          creationTimeUtc: t("labels.creationTimeUtc"),
          memberDashboardContactName: t("labels.memberDashboardContactName"),
          memberDashboardContactEmail: t("labels.memberDashboardContactEmail"),
          companyContactName: t("labels.companyContactName"),
          companyContactEmail: t("labels.companyContactEmail"),
        },
        rewardsPage: {
          rewardsPreview: t("rewardsPage.rewardsPreview"),
          descriptionText1: t("rewardsPage.descriptionText1"),
          create: t("rewardsPage.create"),
          enterText: t("rewardsPage.enterText"),
          sponsoringMember: t("rewardsPage.sponsoringMember"),
          campaignName: t("rewardsPage.campaignName"),
          prizeDescription: t("rewardsPage.prizeDescription"),
          startDate: t("rewardsPage.startDate"),
          endDate: t("rewardsPage.endDate"),
          prizeDraw: t("rewardsPage.prizeDraw"),
          campaignStatus: t("rewardsPage.campaignStatus"),
          noneSoFar: t("rewardsPage.noneSoFar"),
          noPreviousRewards: t("rewardsPage.noPreviousRewards"),
          noActiveRewards: t("rewardsPage.noActiveRewards"),
          deletedMember: t("rewardsPage.deletedMember"),
          openList: t("rewardsPage.openList"),
          upcoming: t("rewardsPage.upcoming"),
          active: t("rewardsPage.active"),
          end: t("rewardsPage.end"),
          userEmail: t("rewardsPage.userEmail"),
          ballotsEarned: t("rewardsPage.ballotsEarned"),
          rewardStartDate: t("rewardsPage.rewardStartDate"),
          rewardEndDate: t("rewardsPage.rewardEndDate"),
          updateCampaignText1: t("rewardsPage.updateCampaignText1"),
          updateCampaignText2: t("rewardsPage.updateCampaignText2"),
          updateCampaignText3: t("rewardsPage.updateCampaignText3"),
          goBack: t("rewardsPage.goBack"),
          areYouSure: t("rewardsPage.areYouSure"),
          campaignEnd: t("rewardsPage.campaignEnd"),
          adminCreatedCampaign: t("rewardsPage.adminCreatedCampaign"),
          campaignCompanySponsor: t("rewardsPage.campaignCompanySponsor"),
          campaignCompanySponsorHelper: t("rewardsPage.campaignCompanySponsorHelper"),
          rewardsCampaignName: t("rewardsPage.rewardsCampaignName"),
          rewardsCampaignNameHelper: t("rewardsPage.rewardsCampaignNameHelper"),
          rewardsCampaignNamePlaceholder: t("rewardsPage.rewardsCampaignNamePlaceholder"),
          rewardsCampaignPrizeDescription: t("rewardsPage.rewardsCampaignPrizeDescription"),
          rewardsCampaignPrizeDescriptionHelper: t("rewardsPage.rewardsCampaignPrizeDescriptionHelper"),
          rewardsCampaignPrizeDescriptionPlaceholder: t("rewardsPage.rewardsCampaignPrizeDescriptionPlaceholder"),
          campaignDuration: t("rewardsPage.campaignDuration"),
          campaignDurationHelper: t("rewardsPage.campaignDurationHelper"),
          rewardsCampaignStartDate: t("rewardsPage.rewardsCampaignStartDate"),
          rewardsCampaignEndDate: t("rewardsPage.rewardsCampaignEndDate"),
          pleaseSelectStartDateFirst: t("rewardsPage.pleaseSelectStartDateFirst"),
          previewPrizeDescription: t("rewardsPage.previewPrizeDescription"),
          rewardsPrizeDescription: t("rewardsPage.rewardsPrizeDescription"),
          shareMoreWinChances: t("rewardsPage.shareMoreWinChances"),
          rewardsAlreadyExists: t("rewardsPage.rewardsAlreadyExists"),
          rewardsCampaignAlreadyActive: t("rewardsPage.rewardsCampaignAlreadyActive"),
          successDetailsSentSuccessfully: t("rewardsPage.successDetailsSentSuccessfully"),
          rewardsCampaignCreatedSuccessfully: t("rewardsPage.rewardsCampaignCreatedSuccessfully"),
          rewardsCampaignUpdatedSuccessfully: t("rewardsPage.rewardsCampaignUpdatedSuccessfully"),
          companySponsor: t("rewardsPage.companySponsor"),
          usersEntered: t("rewardsPage.usersEntered"),
          contestStatus: t("rewardsPage.contestStatus"),
          viewUsers: t("rewardsPage.viewUsers"),
          viewUsersPickWinner: t("rewardsPage.viewUsersPickWinner"),
          update: t("rewardsPage.update"),
          ended: t("rewardsPage.ended"),
          prizeDrawParticipantsTitle: t("rewardsPage.prizeDrawParticipantsTitle"),
          prizeDrawParticipantsDescription: t("rewardsPage.prizeDrawParticipantsDescription"),
          participantEmail: t("rewardsPage.participantEmail"),
          pointsEarned: t("rewardsPage.pointsEarned"),
          pickRandomWinner: t("rewardsPage.pickRandomWinner"),
          downloadList: t("rewardsPage.downloadList"),
          randomWinnerIs: t("rewardsPage.randomWinnerIs"),
          redeemedPerksText: t("rewardsPage.redeemedPerksText"),
          listOfParticipants: t("rewardsPage.listOfParticipants"),
          searchPlaceholder: t("rewardsPage.searchPlaceholder"),
          close: t("rewardsPage.close"),
        },
        RootAdminlabels1: {
          chamberName: t("RootAdminlabels1.chamberName"),
          chamberMemberName: t("RootAdminlabels1.chamberMemberName"),
          membershipStatus: t("RootAdminlabels1.membershipStatus"),
          category1: t("RootAdminlabels1.category1"),
          category2: t("RootAdminlabels1.category2"),
          numberOfUsers: t("RootAdminlabels1.numberOfUsers"),
          numberOfPerksOffered: t("RootAdminlabels1.numberOfPerksOffered"),
          hasLoggedIn: t("RootAdminlabels1.hasLoggedIn"),
          hasLogo: t("RootAdminlabels1.hasLogo"),
          creationTimeUtc: t("RootAdminlabels1.creationTimeUtc"),
        },
        exportPerksCsvDiscountListLabel: {
          chamberName: t("exportPerksCsvDiscountListLabel.chamberName"),
          memberName: t("exportPerksCsvDiscountListLabel.memberName"),
          category1: t("exportPerksCsvDiscountListLabel.category1"),
          category2: t("exportPerksCsvDiscountListLabel.category2"),
          discountName: t("exportPerksCsvDiscountListLabel.discountName"),
          offeredTo: t("exportPerksCsvDiscountListLabel.offeredTo"),
          discountViews: t("exportPerksCsvDiscountListLabel.discountViews"),
          discountRedemptions: t(
            "exportPerksCsvDiscountListLabel.discountRedemptions"
          ),
          creationTimeUtc: t("exportPerksCsvDiscountListLabel.creationTimeUtc"),
          deletionTimeUtc: t("exportPerksCsvDiscountListLabel.deletionTimeUtc"),
        },
        validation: {
          selectAtLeastOneDay: t("validation.selectAtLeastOneDay"),
          selectAtLeastOneMonth: t("validation.selectAtLeastOneMonth"),
          whereToRedeemIsRequired: t("validation.whereToRedeemIsRequired"),
          codeIsRequired: t("validation.codeIsRequired"),
          enterValidWebsite: t("validation.enterValidWebsite"),
          websiteIsRequired: t("validation.websiteIsRequired"),
          enterValidEmail: t("validation.enterValidEmail"),
          enterValidPhoneNumber: t("validation.enterValidPhoneNumber"),
          contactNameIsRequired: t("validation.contactNameIsRequired"),
          perkTitleIsRequired: t("validation.perkTitleIsRequired"),
          perkDescriptionIsRequired: t("validation.perkDescriptionIsRequired"),
          memberCompanyNameIsRequired: t("validation.memberCompanyNameIsRequired"),
          selectRedemptionOption: t("validation.selectRedemptionOption"),
          selectWhereToRedeem: t("validation.selectWhereToRedeem"),
          urlIsRequired: t("validation.urlIsRequired"),
          maxCharacters50: t("validation.maxCharacters50"),
          maxCharacters250: t("validation.maxCharacters250"),
          startDateRequired: t("validation.startDateRequired"),
          endDateRequired: t("validation.endDateRequired"),
          selectWhoCanRedeem: t("validation.selectWhoCanRedeem"),
          contactEmailIsRequired: t("validation.contactEmailIsRequired"),
          contactNumberIsRequired: t("validation.contactNumberIsRequired"),
          contactEmailOrNumber: t("validation.contactEmailOrNumber"),
        },
        adminConsoleLoginEmail: {
          email: t("adminConsoleLoginEmail.email"),
          required: t("adminConsoleLoginEmail.required"),
        },
        changeEmailUsernameModal: {
          successMessage: {
            boldMessage: t("changeEmailUsernameModal.successMessage.boldMessage"),
            description: t("changeEmailUsernameModal.successMessage.description"),
            note: t("changeEmailUsernameModal.successMessage.note"),
          },
          formMessage: t("changeEmailUsernameModal.formMessage"),
          formLabels: {
            emailInput: t("changeEmailUsernameModal.formLabels.emailInput"),
          },
          formErrors: {
            invalidError: t("changeEmailUsernameModal.formErrors.invalidError"),
          },
          buttons: {
            update: t("changeEmailUsernameModal.buttons.update"),
            cancel: t("changeEmailUsernameModal.buttons.cancel"),
          },
        },
        dashboardEmailForm: {
          formMessage: t("dashboardEmailForm.formMessage"),
          formLabels: {
            emailInput: t("dashboardEmailForm.formLabels.emailInput"),
          },
          formErrors: {
            invalidError: t("dashboardEmailForm.formErrors.invalidError"),
          },
          buttons: {
            update: t("dashboardEmailForm.buttons.update"),
            cancel: t("dashboardEmailForm.buttons.cancel"),
          },
        },
        plusAddressedEmailSuggestionModal: {
          title: t("plusAddressedEmailSuggestionModal.title"),
          existingEmail: t("plusAddressedEmailSuggestionModal.existingEmail"),
          alternativeEmail: t(
            "plusAddressedEmailSuggestionModal.alternativeEmail"
          ),
          alternativeEmailInfo: t(
            "plusAddressedEmailSuggestionModal.alternativeEmailInfo"
          ),
          useAlternative: t("plusAddressedEmailSuggestionModal.useAlternative"),
          back: t("plusAddressedEmailSuggestionModal.back"),
          buttons: {
            useAlternative: t(
              "plusAddressedEmailSuggestionModal.buttons.useAlternative"
            ),
            back: t("plusAddressedEmailSuggestionModal.buttons.back"),
          },
        },
        modals: {
          plusAddressedEmailSuggestion: {
            title: t("modals.plusAddressedEmailSuggestion.title"),
            existingEmail: t("modals.plusAddressedEmailSuggestion.existingEmail"),
            alternativeEmail: t(
              "modals.plusAddressedEmailSuggestion.alternativeEmail"
            ),
            alternativeEmailInfo: t(
              "modals.plusAddressedEmailSuggestion.alternativeEmailInfo"
            ),
            useAlternative: t(
              "modals.plusAddressedEmailSuggestion.useAlternative"
            ),
            back: t("modals.plusAddressedEmailSuggestion.back"),
            buttons: {
              useAlternative: t(
                "modals.plusAddressedEmailSuggestion.buttons.useAlternative"
              ),
              back: t("modals.plusAddressedEmailSuggestion.buttons.back"),
            },
          },
          createPerkPartnerAdminUser: {
            title: t("modals.createPerkPartnerAdminUser.title"),
            csvMessage: t("modals.createPerkPartnerAdminUser.csvMessage"),
            note: t("modals.createPerkPartnerAdminUser.note"),
            formMessage: t("modals.createPerkPartnerAdminUser.formMessage"),
            buttons: {
              create: t("modals.createPerkPartnerAdminUser.buttons.create"),
              cancel: t("modals.createPerkPartnerAdminUser.buttons.cancel"),
            },
          },
          csvDownload: {
            downloadText: t("modals.csvDownload.downloadText"),
          },
        },
        validationMessages: {
          nameRequired: t("validationMessages.nameRequired"),
          validWebsite: t("validationMessages.validWebsite"),
          websiteRequired: t("validationMessages.websiteRequired"),
          provinceRequired: t("validationMessages.provinceRequired"),
          cityRequired: t("validationMessages.cityRequired"),
          postalCodeRequired: t("validationMessages.postalCodeRequired"),
          streetAddressRequired: t("validationMessages.streetAddressRequired"),
          supportEmailRequired: t("validationMessages.supportEmailRequired"),
          emailIsRequired: t("validationMessages.emailIsRequired"),
          descriptionMaxCharacters: t("validationMessages.descriptionMaxCharacters"),
        },

        validationSchema: {
          companyNameRequired: t("validationSchema.companyNameRequired"),
          dashboardContactNameRequired: t(
            "validationSchema.dashboardContactNameRequired"
          ),
          notValidEmail: t("validationSchema.notValidEmail"),
          contactEmailRequired: t("validationSchema.contactEmailRequired"),
          categoryRequired: t("validationSchema.categoryRequired"),
          descriptionRequired: t("validationSchema.descriptionRequired"),
          phoneRequired: t("validationSchema.phoneRequired"),
          titleRequired: t("validationSchema.titleRequired"),
          companyContactNameRequired: t("validationSchema.companyContactNameRequired"),
          companyPhoneRequired: t("validationSchema.companyPhoneRequired"),
          adminConsoleLoginEmailRequired: t("validationSchema.adminConsoleLoginEmailRequired"),
          adminConsoleEmailRequired: t("validationSchema.adminConsoleEmailRequired"),
          adminConsolePhoneRequired: t("validationSchema.adminConsolePhoneRequired"),
          primaryContactNameRequired: t("validationSchema.primaryContactNameRequired"),
          provinceRequired: t("validationSchema.provinceRequired"),
          cityRequired: t("validationSchema.cityRequired"),
          postalCodeRequired: t("validationSchema.postalCodeRequired"),
          streetAddressRequired: t("validationSchema.streetAddressRequired"),
        },
        modal: {
          shopLocalContestActive: t("modal.shopLocalContestActive"),
          perkUpdatedSuccessfully: t("modal.perkUpdatedSuccessfully"),
          companyParticipation: t("modal.companyParticipation"),
          redeemPrizeChance: t("modal.redeemPrizeChance"),
          participationCode: t("modal.participationCode"),
        },

        errorMessages: {
          addressIssues: t("errorMessages.addressIssues"),
        },
        forgotPasswordText: {
          pleaseEnterEmailText: t("forgotPasswordText.pleaseEnterEmailText"),
          goBacktoLoginText: t("forgotPasswordText.goBacktoLoginText"),
          emailLabel: t("forgotPasswordText.emailLabel"),
        },
        loginValidation: {
          emailRequired: t("loginValidation.emailRequired"),
          passwordRequired: t("loginValidation.passwordRequired"),
        },
        loginErrorMessages: {
          networkError: t("loginErrorMessages.networkError"),
          invalidCredentials: t("loginErrorMessages.invalidCredentials"),
          accessDenied: t("loginErrorMessages.accessDenied"),
          wrongChamber: (chamberName: string) => t("loginErrorMessages.wrongChamber", { chamberName }),
          accessTurnedOff: (chamberName: string) => t("loginErrorMessages.accessTurnedOff", { chamberName }),
          accessTurnedOffStaff: t("loginErrorMessages.accessTurnedOffStaff"),
          tooManyAttempts: t("loginErrorMessages.tooManyAttempts"),
          passwordResetRequired: t("loginErrorMessages.passwordResetRequired"),
          loginFailed: (chamberName: string) => t("loginErrorMessages.loginFailed", { chamberName }),
          loginFailedStaff: t("loginErrorMessages.loginFailedStaff"),
          newPasswordTimedOut: t("loginErrorMessages.newPasswordTimedOut"),
          newPasswordSaved: t("loginErrorMessages.newPasswordSaved"),
          newPasswordNotSaved: t("loginErrorMessages.newPasswordNotSaved"),
        },
        passwordResetErrorMessages: {
          networkError: t("passwordResetErrorMessages.networkError"),
          emailNotFound: t("passwordResetErrorMessages.emailNotFound"),
          resetNotAvailable: t("passwordResetErrorMessages.resetNotAvailable"),
          tooManyAttempts: t("passwordResetErrorMessages.tooManyAttempts"),
          sendCodeFailed: t("passwordResetErrorMessages.sendCodeFailed"),
          codeIncorrect: t("passwordResetErrorMessages.codeIncorrect"),
          codeExpired: t("passwordResetErrorMessages.codeExpired"),
          passwordRule: t("passwordResetErrorMessages.passwordRule"),
          resetFailed: t("passwordResetErrorMessages.resetFailed"),
        },
        loginText: {
          forgotPasswordText: t("loginText.forgotPasswordText"),
          emailLabel: t("loginText.emailLabel"),
          passwordLabel: t("loginText.passwordLabel"),
          loginButton: t("loginText.loginButton"),
          dashboardTitle: t("loginText.dashboardTitle"),
        },
        switchChamberText: {
          button: t("switchChamberText.button"),
          title: t("switchChamberText.title"),
          body: t("switchChamberText.body"),
          searchChambers: t("switchChamberText.searchChambers"),
          searchBoards: t("switchChamberText.searchBoards"),
          noMatchChambers: t("switchChamberText.noMatchChambers"),
          noMatchBoards: t("switchChamberText.noMatchBoards"),
          select: t("switchChamberText.select"),
          openDashboard: t("switchChamberText.openDashboard"),
          signedInAs: t("switchChamberText.signedInAs"),
          back: t("switchChamberText.back"),
          close: t("switchChamberText.close"),
          notLinked: t("switchChamberText.notLinked"),
          failed: t("switchChamberText.failed"),
        },
        memberLoginText: {
          welcomeTitleLine1: t("memberLoginText.welcomeTitleLine1"),
          welcomeTitleLine2: t("memberLoginText.welcomeTitleLine2"),
          description: t("memberLoginText.description"),
          emailLabel: t("memberLoginText.emailLabel"),
          emailPlaceholder: t("memberLoginText.emailPlaceholder"),
          passwordLabel: t("memberLoginText.passwordLabel"),
          passwordPlaceholder: t("memberLoginText.passwordPlaceholder"),
          forgotPasswordText: t("memberLoginText.forgotPasswordText"),
          loginButton: t("memberLoginText.loginButton"),
          noCredentialsText: t("memberLoginText.noCredentialsText"),
          poweredByText: t("memberLoginText.poweredByText"),
          hostDescription: t("memberLoginText.hostDescription"),
        },
        createPerkPartnerValidation: {
          companyName: t("createPerkPartnerValidation.companyName"),
          title: t("createPerkPartnerValidation.title"),
          description: t("createPerkPartnerValidation.description"),
          verifiedChamberMemberCapUserRedirectLink: t(
            "createPerkPartnerValidation.verifiedChamberMemberCapUserRedirectLink"
          ),
          unverifiedChamberMemberCapUserRedirectLink: t(
            "createPerkPartnerValidation.unverifiedChamberMemberCapUserRedirectLink"
          ),
          logo: t("createPerkPartnerValidation.logo"),
        },
        newPasswordValidation: {
          newPassword: t("newPasswordValidation.newPassword"),
          newPasswordmatches: t("newPasswordValidation.newPasswordmatches"),
          confirmPasswordoneOf: t("newPasswordValidation.confirmPasswordoneOf"),
        },
        newPasswordText: {
          pleaseEnterNewPasswordText: t(
            "newPasswordText.pleaseEnterNewPasswordText"
          ),
          changePasswordText: t("newPasswordText.changePasswordText"),
          newPasswordLabel: t("newPasswordText.newPasswordLabel"),
          confirmPasswordLabel: t("newPasswordText.confirmPasswordLabel"),
        },
        resetPasswordValidation: {
          code: t("resetPasswordValidation.code"),
          newPassword: t("resetPasswordValidation.newPassword"),
          newPasswordmatches: t("resetPasswordValidation.newPasswordmatches"),
          confirmPasswordoneOf: t("resetPasswordValidation.confirmPasswordoneOf"),
        },
        resetPassword: {
          pleaseVerifyEmailText: t("resetPassword.pleaseVerifyEmailText"),
          changePasswordText: t("resetPassword.changePasswordText"),
          resetPasswordSuccessText: t("resetPassword.resetPasswordSuccessText"),
          returnToLoginText: t("resetPassword.returnToLoginText"),
          verificationCodeLabel: t("resetPassword.verificationCodeLabel"),
          newPasswordLabel: t("resetPassword.newPasswordLabel"),
          confirmPasswordLabel: t("resetPassword.confirmPasswordLabel"),
        },
        createChamberRewardCampaignValidation: {
          sponsoringChamberMemberId: t(
            "createChamberRewardCampaignValidation.sponsoringChamberMemberId"
          ),
          campaignName: t("createChamberRewardCampaignValidation.campaignName"),
          prizeDescription: t(
            "createChamberRewardCampaignValidation.prizeDescription"
          ),
          startDateUtcMilli: t(
            "createChamberRewardCampaignValidation.startDateUtcMilli"
          ),
          startDateUtcMillimin: t(
            "createChamberRewardCampaignValidation.startDateUtcMilli.min"
          ),
          endDateUtcMilli: t(
            "createChamberRewardCampaignValidation.endDateUtcMilli"
          ),
          endDateUtcMillimin: t(
            "createChamberRewardCampaignValidation.endDateUtcMillimin"
          ),
        },
        updateChamberRewardCampaignValidation: {
          sponsoringChamberMemberId: t(
            "updateChamberRewardCampaignValidation.sponsoringChamberMemberId"
          ),
          campaignName: t("updateChamberRewardCampaignValidation.campaignName"),
          prizeDescription: t(
            "updateChamberRewardCampaignValidation.prizeDescription"
          ),
          startDateUtcMilli: t(
            "updateChamberRewardCampaignValidation.startDateUtcMilli"
          ),
          endDateUtcMilli: t(
            "updateChamberRewardCampaignValidation.endDateUtcMilli"
          ),
        },
        issuesUpdateToRed: t("issuesUpdateToRed"),

        createChamberValidationSchema: {
          name: t("createChamberValidationSchema.name"),
          numberOfMembersFromInitialSubscriptionAgreement: t(
            "createChamberValidationSchema.numberOfMembersFromInitialSubscriptionAgreement"
          ),
          numberOfMembersFromInitialSubscriptionAgreementmin: t(
            "createChamberValidationSchema.numberOfMembersFromInitialSubscriptionAgreementmin"
          ),
          subscriptionDate: t("createChamberValidationSchema.subscriptionDate"),
          website: t("createChamberValidationSchema.website"),
          supportContactEmail: t(
            "createChamberValidationSchema.supportContactEmail"
          ),
          supportContactEmailemail: t(
            "createChamberValidationSchema.supportContactEmailemail"
          ),
          addressprovince: t("createChamberValidationSchema.addressprovince"),
          addresscity: t("createChamberValidationSchema.addresscity"),
          addresspostalCode: t("createChamberValidationSchema.addresspostalCode"),
          addressstreetAddress: t(
            "createChamberValidationSchema.addressstreetAddress"
          ),
          adminConsoleLoginEmail: t(
            "createChamberValidationSchema.adminConsoleLoginEmail"
          ),
          adminConsoleLoginEmailemail: t(
            "createChamberValidationSchema.adminConsoleLoginEmail.email"
          ),
          jobPostingLink: t("createChamberValidationSchema.jobPostingLink"),
          eventsLink: t("createChamberValidationSchema.eventsLink"),
          newsletterLink: t("createChamberValidationSchema.newsletterLink"),
          surveyLink: t("createChamberValidationSchema.surveyLink"),
          memberSignupLink: t("createChamberValidationSchema.memberSignupLink"),
        },
        updateChamberShopLocalCompaignValidation: {
          contestName: t("updateChamberShopLocalCompaignValidation.contestName"),
          contestDescription: t(
            "updateChamberShopLocalCompaignValidation.contestDescription"
          ),
          oneParticipationPerMember: t(
            "updateChamberShopLocalCompaignValidation.oneParticipationPerMember"
          ),
          participantIsRequired: t(
            "updateChamberShopLocalCompaignValidation.participantIsRequired"
          ),
          startDateUtcMilli: t(
            "updateChamberShopLocalCompaignValidation.startDateUtcMilli"
          ),
          endDateUtcMilli: t(
            "updateChamberShopLocalCompaignValidation.endDateUtcMilli"
          ),
        },
        changeEmailUsername: t("changeEmailUsername"),

        downloadFailureModal: {
          title: t("downloadFailureModal.title"),
          message: t("downloadFailureModal.message"),
          pleaseContact: t("downloadFailureModal.pleaseContact"),
          thisIsTheError: t("downloadFailureModal.thisIsTheError"),
          closeButtonText: t("downloadFailureModal.closeButtonText"),
        },
        HoverToolTip: {
          description: t("HoverToolTip.description"),
          description2: t("HoverToolTip.description2"),
        },
        manageMemberCredentials: {
          pageTitle: t("manageMemberCredentials.pageTitle"),
          pageSubtitle: t("manageMemberCredentials.pageSubtitle"),
          downloadAllMemberCredentials: t(
            "manageMemberCredentials.downloadAllMemberCredentials"
          ),
          emailAllMemberCredentials: t(
            "manageMemberCredentials.emailAllMemberCredentials"
          ),
          totalMemberLogins: t("manageMemberCredentials.totalMemberLogins"),
          searchPlaceholder: t("manageMemberCredentials.searchPlaceholder"),
          filterByStatus: t("manageMemberCredentials.filterByStatus"),
          filterByCategory: t("manageMemberCredentials.filterByCategory"),
          clearStatus: t("manageMemberCredentials.clearStatus"),
          clearCategory: t("manageMemberCredentials.clearCategory"),
          tableHeaders: {
            memberCompany: t(
              "manageMemberCredentials.tableHeaders.memberCompany"
            ),
            companyAdminContact: t(
              "manageMemberCredentials.tableHeaders.companyAdminContact"
            ),
            memberDashboardLoginStatus: t(
              "manageMemberCredentials.tableHeaders.memberDashboardLoginStatus"
            ),
            sendCredentials: t(
              "manageMemberCredentials.tableHeaders.sendCredentials"
            ),
            credentialsSent: t(
              "manageMemberCredentials.tableHeaders.credentialsSent"
            ),
            companyNameHelper: t("manageMemberCredentials.tableHeaders.companyNameHelper"),
            adminContactHelper: t("manageMemberCredentials.tableHeaders.adminContactHelper"),
            sendCredentialsHelper: t("manageMemberCredentials.tableHeaders.sendCredentialsHelper"),
            loginStatusHelper: t("manageMemberCredentials.tableHeaders.loginStatusHelper"),
            credentialsSentHelper: t("manageMemberCredentials.tableHeaders.credentialsSentHelper"),
          },
          buttons: {
            sendLoginCredentials: t("manageMemberCredentials.buttons.sendLoginCredentials"),
            resendLoginCredentials: t("manageMemberCredentials.buttons.resendLoginCredentials"),
            resend: t("manageMemberCredentials.buttons.resend"),
          },
          chips: {
            loggedIn: t("manageMemberCredentials.chips.loggedIn"),
            neverLoggedIn: t("manageMemberCredentials.chips.neverLoggedIn"),
          },
          contactLabels: {
            contactName: t("manageMemberCredentials.contactLabels.contactName"),
            usernameEmail: t("manageMemberCredentials.contactLabels.usernameEmail"),
            contactDescription: t("manageMemberCredentials.contactLabels.contactDescription"),
          },
          resendCredentialsTitle: t("manageMemberCredentials.resendCredentialsTitle"),
          resendCredentialsDesc: t("manageMemberCredentials.resendCredentialsDesc"),
          confirmResendTitle: t("manageMemberCredentials.confirmResendTitle"),
          confirmResendDesc: t("manageMemberCredentials.confirmResendDesc"),
          resetPasswordDialog: {
            tempPasswordWillBeSent: t("manageMemberCredentials.resetPasswordDialog.tempPasswordWillBeSent"),
            clickHereToEditContact: t("manageMemberCredentials.resetPasswordDialog.clickHereToEditContact"),
            toSendToAnotherContact: t("manageMemberCredentials.resetPasswordDialog.toSendToAnotherContact"),
            newTempPasswordGenerated: t("manageMemberCredentials.resetPasswordDialog.newTempPasswordGenerated"),
            memberWillBePrompted: t("manageMemberCredentials.resetPasswordDialog.memberWillBePrompted"),
            areYouSureContinue: t("manageMemberCredentials.resetPasswordDialog.areYouSureContinue"),
            yes: t("manageMemberCredentials.resetPasswordDialog.yes"),
            no: t("manageMemberCredentials.resetPasswordDialog.no"),
          },
          successDialog: {
            credentialsEmailed: t("manageMemberCredentials.successDialog.credentialsEmailed"),
            close: t("manageMemberCredentials.successDialog.close"),
            newTempPasswordGenerated: t("manageMemberCredentials.successDialog.newTempPasswordGenerated"),
            manuallySendTitle: t("manageMemberCredentials.successDialog.manuallySendTitle"),
            sendPasswordResetInstructions: t("manageMemberCredentials.successDialog.sendPasswordResetInstructions"),
            downloadTempPasswordDesc: t("manageMemberCredentials.successDialog.downloadTempPasswordDesc"),
            clickHerePreview: t("manageMemberCredentials.successDialog.clickHerePreview"),
            previewTemplateDesc: t("manageMemberCredentials.successDialog.previewTemplateDesc"),
            downloadTempPassword: t("manageMemberCredentials.successDialog.downloadTempPassword"),
            automaticallySendTitle: t("manageMemberCredentials.successDialog.automaticallySendTitle"),
            emailPasswordResetInstructions: t("manageMemberCredentials.successDialog.emailPasswordResetInstructions"),
            sendEmailDesc: t("manageMemberCredentials.successDialog.sendEmailDesc"),
            withInstructions: t("manageMemberCredentials.successDialog.withInstructions"),
            spamFolderWarning: t("manageMemberCredentials.successDialog.spamFolderWarning"),
            sendEmail: t("manageMemberCredentials.successDialog.sendEmail"),
            previewEmail: t("manageMemberCredentials.successDialog.previewEmail"),
          },
          previewEmailDialog: {
            title: t("manageMemberCredentials.previewEmailDialog.title"),
            subject: t("manageMemberCredentials.previewEmailDialog.subject"),
            greeting: t("manageMemberCredentials.previewEmailDialog.greeting"),
            introMessage: t("manageMemberCredentials.previewEmailDialog.introMessage"),
            primaryContactParagraph: t("manageMemberCredentials.previewEmailDialog.primaryContactParagraph"),
            dashboardAccessParagraph: t("manageMemberCredentials.previewEmailDialog.dashboardAccessParagraph"),
            dashboardUrl: t("manageMemberCredentials.previewEmailDialog.dashboardUrl"),
            loginCredentialsHeader: t("manageMemberCredentials.previewEmailDialog.loginCredentialsHeader"),
            emailUsername: t("manageMemberCredentials.previewEmailDialog.emailUsername"),
            tempPassword: t("manageMemberCredentials.previewEmailDialog.tempPassword"),
            insertTempPassword: t("manageMemberCredentials.previewEmailDialog.insertTempPassword"),
            employeeEncouragement: t("manageMemberCredentials.previewEmailDialog.employeeEncouragement"),
            contactInfo: t("manageMemberCredentials.previewEmailDialog.contactInfo"),
            closingMessage: t("manageMemberCredentials.previewEmailDialog.closingMessage"),
            sincerely: t("manageMemberCredentials.previewEmailDialog.sincerely"),
            passwordResetMessage: t("manageMemberCredentials.previewEmailDialog.passwordResetMessage"),
            accessTitle: t("manageMemberCredentials.previewEmailDialog.accessTitle"),
            accessOption1: t("manageMemberCredentials.previewEmailDialog.accessOption1"),
            accessOption2: t("manageMemberCredentials.previewEmailDialog.accessOption2"),
            or: t("manageMemberCredentials.previewEmailDialog.or"),
            loginInstructions: t("manageMemberCredentials.previewEmailDialog.loginInstructions"),
            newPasswordPrompt: t("manageMemberCredentials.previewEmailDialog.newPasswordPrompt"),
            shareCredentials: t("manageMemberCredentials.previewEmailDialog.shareCredentials"),
            forgotPasswordNote: t("manageMemberCredentials.previewEmailDialog.forgotPasswordNote"),
            resetLoginInstructions: t("manageMemberCredentials.previewEmailDialog.resetLoginInstructions"),
            bestRegards: t("manageMemberCredentials.previewEmailDialog.bestRegards"),
          },
          sendCredsPreviewEmail: {
            subject: t("manageMemberCredentials.sendCredsPreviewEmail.subject"),
            greeting: t("manageMemberCredentials.sendCredsPreviewEmail.greeting"),
            introParagraph: t("manageMemberCredentials.sendCredsPreviewEmail.introParagraph"),
            contactParagraph: t("manageMemberCredentials.sendCredsPreviewEmail.contactParagraph"),
            dashboardAccessParagraph: t("manageMemberCredentials.sendCredsPreviewEmail.dashboardAccessParagraph"),
            dashboardUrl: t("manageMemberCredentials.sendCredsPreviewEmail.dashboardUrl"),
            loginCredentialsHeader: t("manageMemberCredentials.sendCredsPreviewEmail.loginCredentialsHeader"),
            emailUsername: t("manageMemberCredentials.sendCredsPreviewEmail.emailUsername"),
            tempPassword: t("manageMemberCredentials.sendCredsPreviewEmail.tempPassword"),
            insertTempPassword: t("manageMemberCredentials.sendCredsPreviewEmail.insertTempPassword"),
            employeeEncouragement: t("manageMemberCredentials.sendCredsPreviewEmail.employeeEncouragement"),
            contactInfo: t("manageMemberCredentials.sendCredsPreviewEmail.contactInfo"),
            closingMessage: t("manageMemberCredentials.sendCredsPreviewEmail.closingMessage"),
            sincerely: t("manageMemberCredentials.sendCredsPreviewEmail.sincerely"),
          },
        },
        rootChamberAdmin: {
          anyone: t("rootChamberAdmin.anyone"),
          allMembers: t("rootChamberAdmin.allMembers"),
          localMembers: t("rootChamberAdmin.localMembers"),
          memberCompany: t("rootChamberAdmin.memberCompany"),
          memberName: t("rootChamberAdmin.memberName"),
          discountName: t("rootChamberAdmin.discountName"),
          withOutSelect: t("rootChamberAdmin.withOutSelect"),
          noLeadsYet: t("rootChamberAdmin.noLeadsYet"),
          companyName: t("rootChamberAdmin.companyName"),
          categoryId: t("rootChamberAdmin.categoryId"),
          totalNumOfMembers: t("rootChamberAdmin.totalNumOfMembers"),
          category: t("rootChamberAdmin.category"),
          subscriptionStartDate: t("rootChamberAdmin.subscriptionStartDate"),
          appUsersSelectedChamber: t("rootChamberAdmin.appUsersSelectedChamber"),
          appUsersSelectedEmployer: t(
            "rootChamberAdmin.appUsersSelectedEmployer"
          ),
          appUsersNoEmployer: t("rootChamberAdmin.appUsersNoEmployer"),
          livePerks: t("rootChamberAdmin.livePerks"),
          perkViews: t("rootChamberAdmin.perkViews"),
          perkRedemptions: t("rootChamberAdmin.perkRedemptions"),
          initialMemberCompanies: t("rootChamberAdmin.initialMemberCompanies"),
          currentMemberCompanies: t("rootChamberAdmin.currentMemberCompanies"),
          companiesOfferingLivePerks: t(
            "rootChamberAdmin.companiesOfferingLivePerks"
          ),
          companiesLoggedInDashboard: t(
            "rootChamberAdmin.companiesLoggedInDashboard"
          ),
          chamberBoardName: t("rootChamberAdmin.chamberBoardName"),
          chamberAppSigned: t("rootChamberAdmin.chamberAppSigned"),
          yes: t("rootChamberAdmin.yes"),
          no: t("rootChamberAdmin.no"),
          exportUserPerBoard: t("rootChamberAdmin.exportUserPerBoard"),
          exportMembers: t("rootChamberAdmin.exportMembers"),
          exportAllPerks: t("rootChamberAdmin.exportAllPerks"),
          newestMember: t("rootChamberAdmin.newestMember"),
          memberNam: t("rootChamberAdmin.memberNam"),
          chamberName: t("rootChamberAdmin.chamberName"),
          creationDate: t("rootChamberAdmin.creationDate"),
          totalPerksViews: t("rootChamberAdmin.totalPerksViews"),
          allMember: t("rootChamberAdmin.allMember"),
          localMembersOnly: t("rootChamberAdmin.localMembersOnly"),
          memberCompanyEmployees: t("rootChamberAdmin.memberCompanyEmployees"),
          newestPerks: t("rootChamberAdmin.newestPerks"),
          discountName2: t("rootChamberAdmin.discountName2"),
          offeredTo: t("rootChamberAdmin.offeredTo"),
          chamberName2: t("rootChamberAdmin.chamberName2"),
          discountViews: t("rootChamberAdmin.discountViews"),
          discountRedemptions: t("rootChamberAdmin.discountRedemptions"),
          createdAtUtcMilli: t("rootChamberAdmin.createdAtUtcMilli"),
          deletionTimeUtcMilli: t("rootChamberAdmin.deletionTimeUtcMilli"),
          submissionDate: t("rootChamberAdmin.submissionDate"),
          targetWillResetAt: t("rootChamberAdmin.targetWillResetAt"),
          target: t("rootChamberAdmin.target"),
          idealTarget: t("rootChamberAdmin.idealTarget"),
          idealTargetReached: t("rootChamberAdmin.idealTargetReached"),
          withAnEmployerSelected: t("rootChamberAdmin.withAnEmployerSelected"),
          updatedDashboard: {
            headingTitle: t("rootChamberAdmin.updatedDashboard.headingTitle"),
            headingDescription: t(
              "rootChamberAdmin.updatedDashboard.headingDescription"
            ),
            chamberPerksAppOverview: t(
              "rootChamberAdmin.updatedDashboard.chamberPerksAppOverview"
            ),
            memberEngagement: t(
              "rootChamberAdmin.updatedDashboard.memberEngagement"
            ),
            perkEngagement: t("rootChamberAdmin.updatedDashboard.perkEngagement"),
            progressPercentageSuffix: t(
              "rootChamberAdmin.updatedDashboard.progressPercentageSuffix"
            ),
            tipModalTitle: t("rootChamberAdmin.updatedDashboard.tipModalTitle"),
            targetResetSubtitle: t(
              "rootChamberAdmin.updatedDashboard.targetResetSubtitle"
            ),
            progressTicks: ["0%", "25%", "50%", "75%", "100%"],
            metrics: {
              membersOnApp: {
                title: t(
                  "rootChamberAdmin.updatedDashboard.metrics.membersOnApp.title"
                ),
              },
              totalMembersFeatured: {
                title: t(
                  "rootChamberAdmin.updatedDashboard.metrics.totalMembersFeatured.title"
                ),
              },
              notificationsEnabled: {
                title: t(
                  "rootChamberAdmin.updatedDashboard.metrics.notificationsEnabled.title"
                ),
                targetProgressTitle: t(
                  "rootChamberAdmin.updatedDashboard.metrics.notificationsEnabled.targetProgressTitle"
                ),
                ctaLabel: t(
                  "rootChamberAdmin.updatedDashboard.metrics.notificationsEnabled.ctaLabel"
                ),
                ctaHref: t(
                  "rootChamberAdmin.updatedDashboard.metrics.notificationsEnabled.ctaHref"
                ),
                progressAriaLabel: t(
                  "rootChamberAdmin.updatedDashboard.metrics.notificationsEnabled.progressAriaLabel"
                ),
                targetLabel: t(
                  "rootChamberAdmin.updatedDashboard.metrics.notificationsEnabled.targetLabel"
                ),
                xAxisLabel: t(
                  "rootChamberAdmin.updatedDashboard.metrics.notificationsEnabled.xAxisLabel"
                ),
                tipContent: t(
                  "rootChamberAdmin.updatedDashboard.metrics.notificationsEnabled.tipContent"
                ),
              },
              memberUserAccounts: {
                title: t(
                  "rootChamberAdmin.updatedDashboard.metrics.memberUserAccounts.title"
                ),
                twelveMonthTargetTitle: t(
                  "rootChamberAdmin.updatedDashboard.metrics.memberUserAccounts.twelveMonthTargetTitle"
                ),
                downloadHistoryTitle: t(
                  "rootChamberAdmin.updatedDashboard.metrics.memberUserAccounts.downloadHistoryTitle"
                ),
                ctaLabel: t(
                  "rootChamberAdmin.updatedDashboard.metrics.memberUserAccounts.ctaLabel"
                ),
                ctaHref: t(
                  "rootChamberAdmin.updatedDashboard.metrics.memberUserAccounts.ctaHref"
                ),
                progressAriaLabel: t(
                  "rootChamberAdmin.updatedDashboard.metrics.memberUserAccounts.progressAriaLabel"
                ),
                xAxisLabel: t(
                  "rootChamberAdmin.updatedDashboard.metrics.memberUserAccounts.xAxisLabel"
                ),
                chartYAxisLabel: t(
                  "rootChamberAdmin.updatedDashboard.metrics.memberUserAccounts.chartYAxisLabel"
                ),
                chartXAxisLabel: t(
                  "rootChamberAdmin.updatedDashboard.metrics.memberUserAccounts.chartXAxisLabel"
                ),
                tipContent: t(
                  "rootChamberAdmin.updatedDashboard.metrics.memberUserAccounts.tipContent"
                ),
                starDiagramDescription: t(
                  "rootChamberAdmin.updatedDashboard.metrics.memberUserAccounts.starDiagramDescription"
                ),
              },
              memberDashboardLogins: {
                title: t(
                  "rootChamberAdmin.updatedDashboard.metrics.memberDashboardLogins.title"
                ),
                twelveMonthTargetTitle: t(
                  "rootChamberAdmin.updatedDashboard.metrics.memberDashboardLogins.twelveMonthTargetTitle"
                ),
                ctaLabel: t(
                  "rootChamberAdmin.updatedDashboard.metrics.memberDashboardLogins.ctaLabel"
                ),
                ctaHref: t(
                  "rootChamberAdmin.updatedDashboard.metrics.memberDashboardLogins.ctaHref"
                ),
                progressAriaLabel: t(
                  "rootChamberAdmin.updatedDashboard.metrics.memberDashboardLogins.progressAriaLabel"
                ),
                xAxisLabel: t(
                  "rootChamberAdmin.updatedDashboard.metrics.memberDashboardLogins.xAxisLabel"
                ),
                tipContent: t(
                  "rootChamberAdmin.updatedDashboard.metrics.memberDashboardLogins.tipContent"
                ),
              },
              chamberPerksOffered: {
                title: t(
                  "rootChamberAdmin.updatedDashboard.metrics.chamberPerksOffered.title"
                ),
                ctaLabel: t(
                  "rootChamberAdmin.updatedDashboard.metrics.chamberPerksOffered.ctaLabel"
                ),
                ctaHref: t(
                  "rootChamberAdmin.updatedDashboard.metrics.chamberPerksOffered.ctaHref"
                ),
                progressAriaLabel: t(
                  "rootChamberAdmin.updatedDashboard.metrics.chamberPerksOffered.progressAriaLabel"
                ),
                xAxisLabel: t(
                  "rootChamberAdmin.updatedDashboard.metrics.chamberPerksOffered.xAxisLabel"
                ),
                tipContent: t(
                  "rootChamberAdmin.updatedDashboard.metrics.chamberPerksOffered.tipContent"
                ),
                targetProgressTitle: t(
                  "rootChamberAdmin.updatedDashboard.metrics.chamberPerksOffered.targetProgressTitle"
                ),
              },
              chamberPerkViews: {
                title: t(
                  "rootChamberAdmin.updatedDashboard.metrics.chamberPerkViews.title"
                ),
              },
              totalPerksOffered: {
                title: t(
                  "rootChamberAdmin.updatedDashboard.metrics.totalPerksOffered.title"
                ),
              },
              totalPerkViews: {
                title: t(
                  "rootChamberAdmin.updatedDashboard.metrics.totalPerkViews.title"
                ),
              },
              companyLeads: {
                title: t(
                  "rootChamberAdmin.updatedDashboard.metrics.companyLeads.title"
                ),
                subtitle: t(
                  "rootChamberAdmin.updatedDashboard.metrics.companyLeads.subtitle"
                ),
              },
              numberOfPerksOffered: {
                title: t(
                  "rootChamberAdmin.updatedDashboard.metrics.numberOfPerksOffered.title"
                ),
                ctaLabel: t(
                  "rootChamberAdmin.updatedDashboard.metrics.numberOfPerksOffered.ctaLabel"
                ),
                ctaHref: t(
                  "rootChamberAdmin.updatedDashboard.metrics.numberOfPerksOffered.ctaHref"
                ),
                progressAriaLabel: t(
                  "rootChamberAdmin.updatedDashboard.metrics.numberOfPerksOffered.progressAriaLabel"
                ),
              },
              numberOfMembersOfferingPerks: {
                title: t(
                  "rootChamberAdmin.updatedDashboard.metrics.numberOfMembersOfferingPerks.title"
                ),
                targetProgressTitle: t(
                  "rootChamberAdmin.updatedDashboard.metrics.numberOfMembersOfferingPerks.targetProgressTitle"
                ),
                ctaLabel: t(
                  "rootChamberAdmin.updatedDashboard.metrics.numberOfMembersOfferingPerks.ctaLabel"
                ),
                ctaHref: t(
                  "rootChamberAdmin.updatedDashboard.metrics.numberOfMembersOfferingPerks.ctaHref"
                ),
                progressAriaLabel: t(
                  "rootChamberAdmin.updatedDashboard.metrics.numberOfMembersOfferingPerks.progressAriaLabel"
                ),
                xAxisLabel: t(
                  "rootChamberAdmin.updatedDashboard.metrics.numberOfMembersOfferingPerks.xAxisLabel"
                ),
                tipContent: t(
                  "rootChamberAdmin.updatedDashboard.metrics.numberOfMembersOfferingPerks.tipContent"
                ),
              },
              perkViews: {
                title: t(
                  "rootChamberAdmin.updatedDashboard.metrics.perkViews.title"
                ),
                ctaLabel: t(
                  "rootChamberAdmin.updatedDashboard.metrics.perkViews.ctaLabel"
                ),
                ctaHref: t(
                  "rootChamberAdmin.updatedDashboard.metrics.perkViews.ctaHref"
                ),
                progressAriaLabel: t(
                  "rootChamberAdmin.updatedDashboard.metrics.perkViews.progressAriaLabel"
                ),
                xAxisLabel: t(
                  "rootChamberAdmin.updatedDashboard.metrics.perkViews.xAxisLabel"
                ),
                tipContent: t(
                  "rootChamberAdmin.updatedDashboard.metrics.perkViews.tipContent"
                ),
              },
              perksCategoryBreakdown: {
                title: t(
                  "rootChamberAdmin.updatedDashboard.metrics.perksCategoryBreakdown.title"
                ),
                columns: {
                  category: t(
                    "rootChamberAdmin.updatedDashboard.metrics.perksCategoryBreakdown.columns.category"
                  ),
                  numberOfMembers: t(
                    "rootChamberAdmin.updatedDashboard.metrics.perksCategoryBreakdown.columns.numberOfMembers"
                  ),
                  numberOfPerks: t(
                    "rootChamberAdmin.updatedDashboard.metrics.perksCategoryBreakdown.columns.numberOfPerks"
                  ),
                  percentOfPerks: t(
                    "rootChamberAdmin.updatedDashboard.metrics.perksCategoryBreakdown.columns.percentOfPerks"
                  ),
                },
                sortByLabel: t(
                  "rootChamberAdmin.updatedDashboard.metrics.perksCategoryBreakdown.sortByLabel"
                ),
                noDataAvailable: t(
                  "rootChamberAdmin.updatedDashboard.metrics.perksCategoryBreakdown.noDataAvailable"
                ),
              },
              perkRedeemableByBreakdown: {
                title: t(
                  "rootChamberAdmin.updatedDashboard.metrics.perkRedeemableByBreakdown.title"
                ),
              },
            },
            cumulativeMemberDownloadsSince: t(
              "rootChamberAdmin.updatedDashboard.cumulativeMemberDownloadsSince"
            ),
            cumulativeMemberDownloadsTitle: t(
              "rootChamberAdmin.updatedDashboard.cumulativeMemberDownloadsTitle"
            ),
            year: t("rootChamberAdmin.updatedDashboard.year"),
            perkMilestonesDescription: t(
              "rootChamberAdmin.updatedDashboard.perkMilestonesDescription"
            ),
            membersOfferingPerksMilestonesDescription: t(
              "rootChamberAdmin.updatedDashboard.membersOfferingPerksMilestonesDescription"
            ),
            averageDownloads: t(
              "rootChamberAdmin.updatedDashboard.averageDownloads"
            ),
            milestoneTargetLabel: t(
              "rootChamberAdmin.updatedDashboard.milestoneTargetLabel"
            ),
            selectAnotherChamberStats: t(
              "rootChamberAdmin.updatedDashboard.selectAnotherChamberStats"
            ),
          },
        },
        selectMemberCompany: t("selectMemberCompany"),
        cpaMarketing: {
          description: t("cpaMarketing.description"),
          email: t("cpaMarketing.email"),
          description2: t("cpaMarketing.description2"),
          note: t("cpaMarketing.note"),
        },
        manageChamberEngage: {
          nonDuesRevenue: t("manageChamberEngage.nonDuesRevenue"),
          activationFee: t("manageChamberEngage.activationFee"),
          name: t("manageChamberEngage.name"),
          status: t("manageChamberEngage.status"),
          fillInformation: t("manageChamberEngage.fillInformation"),
          emailWarning: t("manageChamberEngage.emailWarning"),
          selectCompany: t("manageChamberEngage.selectCompany"),
          sellModal: {
            fillInfoForMember: {
              title: t("manageChamberEngage.sellModal.fillInfoForMember.title"),
              notificationMessage: t(
                "manageChamberEngage.sellModal.fillInfoForMember.notificationMessage"
              ),
            },
            viewResell: {
              title: t("manageChamberEngage.sellModal.viewResell.title"),
              notificationMessage: t(
                "manageChamberEngage.sellModal.viewResell.notificationMessage"
              ),
            },
          },
          sellbanner: t("manageChamberEngage.sellbanner"),
          waitingApproval: t("manageChamberEngage.waitingApproval"),
          memberCompanyName: t("manageChamberEngage.memberCompanyName"),
          pricing: t("manageChamberEngage.pricing"),
          startDate: t("manageChamberEngage.startDate"),
          expiryDate: t("manageChamberEngage.expiryDate"),
          loadingChamberMembers: t("manageChamberEngage.loadingChamberMembers"),
          chamberEngageSettingsUpdated: t(
            "manageChamberEngage.chamberEngageSettingsUpdated"
          ),
          updateChamberEngageSettings: t(
            "manageChamberEngage.updateChamberEngageSettings"
          ),
          cpaFeaturedMemberActivationFee: t(
            "manageChamberEngage.cpaFeaturedMemberActivationFee"
          ),
          featuredMemberAnnualFee: t(
            "manageChamberEngage.featuredMemberAnnualFee"
          ),
          cpaCategoryAdvertisingBannerRevenueShare: t(
            "manageChamberEngage.cpaCategoryAdvertisingBannerRevenueShare"
          ),
          categoryAdvertisingBannersFeeOptions: t(
            "manageChamberEngage.categoryAdvertisingBannersFeeOptions"
          ),
          memberProfileActivationFee: t(
            "manageChamberEngage.memberProfileActivationFee"
          ),
          memberProfileAnnualFee: t("manageChamberEngage.memberProfileAnnualFee"),
          months: t("manageChamberEngage.months"),
          categoryAdvertisingBannersActivationFeeOptions: t(
            "manageChamberEngage.categoryAdvertisingBannersActivationFeeOptions"
          ),
          fee: t("manageChamberEngage.fee"),
          addOption: t("manageChamberEngage.addOption"),
          option: t("manageChamberEngage.option"),
          activateChamberEngage: t("manageChamberEngage.activateChamberEngage"),
          update: t("manageChamberEngage.update"),
          close: t("manageChamberEngage.close"),
          searchByChamberOrBoardName: t(
            "manageChamberEngage.searchByChamberOrBoardName"
          ),
          loadingChambers: t("manageChamberEngage.loadingChambers"),
          activateCategoryAdvertisingBanners: t(
            "manageChamberEngage.activateCategoryAdvertisingBanners"
          ),
          activateFeaturedMemberBanners: t(
            "manageChamberEngage.activateFeaturedMemberBanners"
          ),
          viewInstructions: t("manageChamberEngage.viewInstructions"),
          statusActive: t("manageChamberEngage.statusActive"),
          statusInactive: t("manageChamberEngage.statusInactive"),
          loading: t("manageChamberEngage.loading"),
          edit: t("manageChamberEngage.edit"),
          categoryAdvertisingBanners: {
            manageButton: t(
              "manageChamberEngage.categoryAdvertisingBanners.manageButton"
            ),
            managePerkButton: t(
              "manageChamberEngage.categoryAdvertisingBanners.managePerkButton"
            ),
            enabled: t("manageChamberEngage.categoryAdvertisingBanners.enabled"),
            disabled: t(
              "manageChamberEngage.categoryAdvertisingBanners.disabled"
            ),
            enable: t("manageChamberEngage.categoryAdvertisingBanners.enable"),
            disable: t("manageChamberEngage.categoryAdvertisingBanners.disable"),
            autoApproval: t(
              "manageChamberEngage.categoryAdvertisingBanners.autoApproval"
            ),
            successfully: t(
              "manageChamberEngage.categoryAdvertisingBanners.successfully"
            ),
            failedTo: t(
              "manageChamberEngage.categoryAdvertisingBanners.failedTo"
            ),
            autoApprovalTwo: t(
              "manageChamberEngage.categoryAdvertisingBanners.autoApprovalTwo"
            ),
            autoApproveBannerContentUpdates: t(
              "manageChamberEngage.categoryAdvertisingBanners.autoApproveBannerContentUpdates"
            ),
            enabledStatus: t(
              "manageChamberEngage.categoryAdvertisingBanners.enabledStatus"
            ),
            disabledStatus: t(
              "manageChamberEngage.categoryAdvertisingBanners.disabledStatus"
            ),
            autoApproveDescriptionParagraph1: t(
              "manageChamberEngage.categoryAdvertisingBanners.autoApproveDescriptionParagraph1"
            ),
            autoApproveDescriptionParagraph2: t(
              "manageChamberEngage.categoryAdvertisingBanners.autoApproveDescriptionParagraph2"
            ),
            approvedStatus: t(
              "manageChamberEngage.categoryAdvertisingBanners.approvedStatus"
            ),
            notApprovedStatus: t(
              "manageChamberEngage.categoryAdvertisingBanners.notApprovedStatus"
            ),
            close: t(
              "manageChamberEngage.categoryAdvertisingBanners.close"
            ),
            advertisingBannersTitle: t(
              "manageChamberEngage.categoryAdvertisingBanners.advertisingBannersTitle"
            ),
            bannerDescription: t(
              "manageChamberEngage.categoryAdvertisingBanners.bannerDescription"
            ),
            bannerUploadInstructions: t(
              "manageChamberEngage.categoryAdvertisingBanners.bannerUploadInstructions"
            ),
            bannerUploadQuality: t(
              "manageChamberEngage.categoryAdvertisingBanners.bannerUploadQuality"
            ),
            bannerLabel: t(
              "manageChamberEngage.categoryAdvertisingBanners.bannerLabel"
            ),
            watchVideo: t(
              "manageChamberEngage.categoryAdvertisingBanners.watchVideo"
            ),
            uploadBanner: t(
              "manageChamberEngage.categoryAdvertisingBanners.uploadBanner"
            ),
            available: t(
              "manageChamberEngage.categoryAdvertisingBanners.available"
            ),
            sold: t(
              "manageChamberEngage.categoryAdvertisingBanners.sold"
            ),
            notAvailable: t(
              "manageChamberEngage.categoryAdvertisingBanners.notAvailable"
            ),
            view: t(
              "manageChamberEngage.categoryAdvertisingBanners.view"
            ),
            deactivate: t(
              "manageChamberEngage.categoryAdvertisingBanners.deactivate"
            ),
            cancel: t(
              "manageChamberEngage.categoryAdvertisingBanners.cancel"
            ),
            deactivateModalTitle: t(
              "manageChamberEngage.categoryAdvertisingBanners.deactivateModalTitle"
            ),
            deactivateModalDescription: t(
              "manageChamberEngage.categoryAdvertisingBanners.deactivateModalDescription"
            ),
            video: t(
              "manageChamberEngage.categoryAdvertisingBanners.video"
            ),
            categoryAdvertisingBanner: t(
              "manageChamberEngage.categoryAdvertisingBanners.categoryAdvertisingBanner"
            ),
            deactivateConfirmQuestion: t(
              "manageChamberEngage.categoryAdvertisingBanners.deactivateConfirmQuestion"
            ),
            autoApproveBannerContent: t(
              "manageChamberEngage.categoryAdvertisingBanners.autoApproveBannerContent"
            ),
            enableAutoApproveTitle: t(
              "manageChamberEngage.categoryAdvertisingBanners.enableAutoApproveTitle"
            ),
            disableAutoApproveTitle: t(
              "manageChamberEngage.categoryAdvertisingBanners.disableAutoApproveTitle"
            ),
            enableAutoApproveMessage: t(
              "manageChamberEngage.categoryAdvertisingBanners.enableAutoApproveMessage"
            ),
            disableAutoApproveMessage: t(
              "manageChamberEngage.categoryAdvertisingBanners.disableAutoApproveMessage"
            ),
            enableButton: t(
              "manageChamberEngage.categoryAdvertisingBanners.enableButton"
            ),
            disableButton: t(
              "manageChamberEngage.categoryAdvertisingBanners.disableButton"
            ),
            bannerApprovalStatus: t(
              "manageChamberEngage.categoryAdvertisingBanners.bannerApprovalStatus"
            ),
            bannerApprovalApproved: t(
              "manageChamberEngage.categoryAdvertisingBanners.bannerApprovalApproved"
            ),
            bannerApprovalNotApproved: t(
              "manageChamberEngage.categoryAdvertisingBanners.bannerApprovalNotApproved"
            ),
            bannerApprovalNotRequired: t(
              "manageChamberEngage.categoryAdvertisingBanners.bannerApprovalNotRequired"
            ),
          },
          modalMemberEngageTitle1: t(
            "manageChamberEngage.modalMemberEngageTitle1"
          ),
          modalMemberEngageTitle2: t(
            "manageChamberEngage.modalMemberEngageTitle2"
          ),
          manageActivationsTitle: t("manageChamberEngage.manageActivationsTitle"),
          manageActivationsDescription: t("manageChamberEngage.manageActivationsDescription"),
          manageActivationsDescriptionLine2: t("manageChamberEngage.manageActivationsDescriptionLine2"),
          activateCategoryTitle: t("manageChamberEngage.activateCategoryTitle"),
          activateCategoryDescription: t("manageChamberEngage.activateCategoryDescription"),
          activateCategoryDescriptionLine2: t("manageChamberEngage.activateCategoryDescriptionLine2"),
          categoryDirectoryAdvertisingFee: t("manageChamberEngage.categoryDirectoryAdvertisingFee"),
          categoryInvoicingDescription: t("manageChamberEngage.categoryInvoicingDescription"),
          categoryInvoicingDescriptionBold: t("manageChamberEngage.categoryInvoicingDescriptionBold"),
          tableHeaders: {
            category: t("manageChamberEngage.tableHeaders.category"),
            categorySubtitle: t("manageChamberEngage.tableHeaders.categorySubtitle"),
            availability: t("manageChamberEngage.tableHeaders.availability"),
            availabilitySubtitle: t("manageChamberEngage.tableHeaders.availabilitySubtitle"),
            viewBannerInfo: t("manageChamberEngage.tableHeaders.viewBannerInfo"),
            viewBannerInfoSubtitle: t("manageChamberEngage.tableHeaders.viewBannerInfoSubtitle"),
            waitingForApproval: t("manageChamberEngage.tableHeaders.waitingForApproval"),
            waitingForApprovalSubtitle: t("manageChamberEngage.tableHeaders.waitingForApprovalSubtitle"),
            memberCompanyHeader: t("manageChamberEngage.tableHeaders.memberCompanyHeader"),
            memberCompanySubtitle: t("manageChamberEngage.tableHeaders.memberCompanySubtitle"),
            memberName: t("manageChamberEngage.tableHeaders.memberName"),
            memberLogoDescription: t("manageChamberEngage.tableHeaders.memberLogoDescription"),
            action: t("manageChamberEngage.tableHeaders.action"),
            actionDescription: t("manageChamberEngage.tableHeaders.actionDescription"),
            annualFee: t("manageChamberEngage.tableHeaders.annualFee"),
            annualFeeDescription: t("manageChamberEngage.tableHeaders.annualFeeDescription"),
            pricing: t("manageChamberEngage.tableHeaders.pricing"),
            pricingDescription: t("manageChamberEngage.tableHeaders.pricingDescription"),
            startDate: t("manageChamberEngage.tableHeaders.startDate"),
            startDateDescription: t("manageChamberEngage.tableHeaders.startDateDescription"),
            endDate: t("manageChamberEngage.tableHeaders.endDate"),
            endDateDescription: t("manageChamberEngage.tableHeaders.endDateDescription"),
            expirationDate: t("manageChamberEngage.tableHeaders.expirationDate"),
            expirationDateDescription: t("manageChamberEngage.tableHeaders.expirationDateDescription"),
            updateStatus: t("manageChamberEngage.tableHeaders.updateStatus"),
            updateStatusDescription: t("manageChamberEngage.tableHeaders.updateStatusDescription"),
            soldTo: t("manageChamberEngage.tableHeaders.soldTo"),
            resoldTo: t("manageChamberEngage.tableHeaders.resoldTo"),
            activeForDays: t("manageChamberEngage.tableHeaders.activeForDays"),
            bannerStatus: t("manageChamberEngage.tableHeaders.bannerStatus"),
            bannerStatusSubtitle: t("manageChamberEngage.tableHeaders.bannerStatusSubtitle"),
            bannersFee: t("manageChamberEngage.tableHeaders.bannersFee"),
            bannersFeeSubtitle: t("manageChamberEngage.tableHeaders.bannersFeeSubtitle"),
            startDateSubtitle: t("manageChamberEngage.tableHeaders.startDateSubtitle"),
            endDateSubtitle: t("manageChamberEngage.tableHeaders.endDateSubtitle"),
            actionSubtitle: t("manageChamberEngage.tableHeaders.actionSubtitle"),
            resoldStatus: t("manageChamberEngage.tableHeaders.resoldStatus"),
            resoldAsOf: t("manageChamberEngage.tableHeaders.resoldAsOf"),
            soldStatus: t("manageChamberEngage.tableHeaders.soldStatus"),
            availableStatus: t("manageChamberEngage.tableHeaders.availableStatus"),
            daysLeft: t("manageChamberEngage.tableHeaders.daysLeft"),
            monthsDaysLeft: t("manageChamberEngage.tableHeaders.monthsDaysLeft"),
            monthsLeft: t("manageChamberEngage.tableHeaders.monthsLeft"),
          },
          advertisingBannerShare: t("manageChamberEngage.advertisingBannerShare"),
          humanReadableCategoryName: t(
            "manageChamberEngage.humanReadableCategoryName"
          ),
          availability: t("manageChamberEngage.availability"),
          waitingForApproval: t("manageChamberEngage.waitingForApproval"),
          activatedSuccessfully: t("manageChamberEngage.activatedSuccessfully"),
          categoryBannerActivatedSuccess: t("manageChamberEngage.categoryBannerActivatedSuccess"),
          active: t("manageChamberEngage.active"),
          reactivate: t("manageChamberEngage.reactivate"),
          selectMemberCompany: t("manageChamberEngage.selectMemberCompany"),
          advertisingTerm: t("manageChamberEngage.advertisingTerm"),
          selectAdvertisingOption: t(
            "manageChamberEngage.selectAdvertisingOption"
          ),
          resellStartDate: t("manageChamberEngage.resellStartDate"),
          updatedSuccessfully: t("manageChamberEngage.updatedSuccessfully"),
          sold: t("manageChamberEngage.sold"),
          soldAndResold: t("manageChamberEngage.soldAndResold"),
          approved: t("manageChamberEngage.approved"),
          bannersDiscrption: t("manageChamberEngage.bannersDiscrption"),
          bannersDiscrption2: t("manageChamberEngage.bannersDiscrption2"),
          howToActivate: t("manageChamberEngage.howToActivate"),
          viewStatus: t("manageChamberEngage.viewStatus"),
          searchByCategory: t("manageChamberEngage.searchByCategory"),
          searchByCompanyName: t("manageChamberEngage.searchByCompanyName"),
          filterByCategory: t("manageChamberEngage.filterByCategory"),
          filterByStatus: t("manageChamberEngage.filterByStatus"),
          allCategories: t("manageChamberEngage.allCategories"),
          allStatuses: t("manageChamberEngage.allStatuses"),
          clearFilter: t("manageChamberEngage.clearFilter"),
          clearCategory: t("manageChamberEngage.clearCategory"),
          clearStatus: t("manageChamberEngage.clearStatus"),
          clearAllFilters: t("manageChamberEngage.clearAllFilters"),
          term: t("manageChamberEngage.term"),
          noMemberPurchased: t("manageChamberEngage.noMemberPurchased"),
          activateForMember: t("manageChamberEngage.activateForMember"),
          unsold: t("manageChamberEngage.unsold"),
          activationForm: {
            startDateDescription: t("manageChamberEngage.activationForm.startDateDescription"),
            endDateDescription: t("manageChamberEngage.activationForm.endDateDescription"),
            emailNotification60Days: t("manageChamberEngage.activationForm.emailNotification60Days"),
            autoDeactivate365Days: t("manageChamberEngage.activationForm.autoDeactivate365Days"),
            reactivateInstructions: t("manageChamberEngage.activationForm.reactivateInstructions"),
            chooseMember: t("manageChamberEngage.activationForm.chooseMember"),
            chooseCategory: t("manageChamberEngage.activationForm.chooseCategory"),
            selectMemberDescription: t("manageChamberEngage.activationForm.selectMemberDescription"),
            selectMemberDescriptionBold: t("manageChamberEngage.activationForm.selectMemberDescriptionBold"),
            selectCategoryDescription: t("manageChamberEngage.activationForm.selectCategoryDescription"),
            selectCategoryDescriptionBold: t("manageChamberEngage.activationForm.selectCategoryDescriptionBold"),
            selectMemberForBanner: t("manageChamberEngage.activationForm.selectMemberForBanner"),
            selectMemberCompanyName: t("manageChamberEngage.activationForm.selectMemberCompanyName"),
            selectCategory: t("manageChamberEngage.activationForm.selectCategory"),
            chooseStartDate: t("manageChamberEngage.activationForm.chooseStartDate"),
            chooseEndDate: t("manageChamberEngage.activationForm.chooseEndDate"),
            endDateAutoCalculated: t("manageChamberEngage.activationForm.endDateAutoCalculated"),
            annualFee: t("manageChamberEngage.activationForm.annualFee"),
            chamberShare: t("manageChamberEngage.activationForm.chamberShare"),
            chamberInvoiceNote: t("manageChamberEngage.activationForm.chamberInvoiceNote"),
            noInactiveMembersAvailable: t("manageChamberEngage.activationForm.noInactiveMembersAvailable"),
          },
          buttons: {
            activate: t("manageChamberEngage.buttons.activate"),
            reactivate: t("manageChamberEngage.buttons.reactivate"),
            deactivate: t("manageChamberEngage.buttons.deactivate"),
            clearAll: t("manageChamberEngage.buttons.clearAll"),
            howToActivateMember: t("manageChamberEngage.buttons.howToActivateMember"),
          },
          modals: {
            activateBanner: t("manageChamberEngage.modals.activateBanner"),
            viewReactivation: t("manageChamberEngage.modals.viewReactivation"),
            resellBanner: t("manageChamberEngage.modals.resellBanner"),
            deactivateTitle: t("manageChamberEngage.modals.deactivateTitle"),
            deactivateConfirm: t("manageChamberEngage.modals.deactivateConfirm"),
            deactivateDescription: t("manageChamberEngage.modals.deactivateDescription"),
            deactivateEffect: t("manageChamberEngage.modals.deactivateEffect"),
            deactivateEffectEnd: t("manageChamberEngage.modals.deactivateEffectEnd"),
            deactivateButton: t("manageChamberEngage.modals.deactivateButton"),
            cancelButton: t("manageChamberEngage.modals.cancelButton"),
            reactivateModalTitle: t("manageChamberEngage.modals.reactivateModalTitle"),
            reactivateConfirmQuestion: t("manageChamberEngage.modals.reactivateConfirmQuestion"),
            reactivateBullet1: t("manageChamberEngage.modals.reactivateBullet1"),
            reactivateBullet2: t("manageChamberEngage.modals.reactivateBullet2"),
            reactivateBullet3: t("manageChamberEngage.modals.reactivateBullet3"),
            reactivateInvoiceInfo: t("manageChamberEngage.modals.reactivateInvoiceInfo"),
            reactivateActivationFee: t("manageChamberEngage.modals.reactivateActivationFee"),
            deactivateNewConfirm: t("manageChamberEngage.modals.deactivateNewConfirm"),
            deactivateNewEffect: t("manageChamberEngage.modals.deactivateNewEffect"),
            categoryBannerSuccessfullyActivated: t("manageChamberEngage.modals.categoryBannerSuccessfullyActivated"),
            failedToReactivate: t("manageChamberEngage.modals.failedToReactivate"),
            alreadyReactivated: t("manageChamberEngage.modals.alreadyReactivated"),
            failedToActivate: t("manageChamberEngage.modals.failedToActivate"),
            successfullyDeactivated: t("manageChamberEngage.modals.successfullyDeactivated"),
            failedToDeactivate: t("manageChamberEngage.modals.failedToDeactivate"),
          },
        },
        manageAffinityProgramActivations: {
          pageTitle: t("manageAffinityProgramActivations.pageTitle"),
          pageDescription: t(
            "manageAffinityProgramActivations.pageDescription"
          ),
          illustrationAlt: t(
            "manageAffinityProgramActivations.illustrationAlt"
          ),
          watchTutorials: t(
            "manageAffinityProgramActivations.watchTutorials"
          ),
          viewInstructions: t(
            "manageAffinityProgramActivations.viewInstructions"
          ),
          searchPlaceholder: t(
            "manageAffinityProgramActivations.searchPlaceholder"
          ),
          filterByStatus: t(
            "manageAffinityProgramActivations.filterByStatus"
          ),
          filterAll: t("manageAffinityProgramActivations.filterAll"),
          clearAllFilters: t(
            "manageAffinityProgramActivations.clearAllFilters"
          ),
          statusActivated: t(
            "manageAffinityProgramActivations.statusActivated"
          ),
          statusReactivated: t(
            "manageAffinityProgramActivations.statusReactivated"
          ),
          statusDeactivated: t(
            "manageAffinityProgramActivations.statusDeactivated"
          ),
          statusReactivatedAsOf: t(
            "manageAffinityProgramActivations.statusReactivatedAsOf"
          ),
          daysLeft: t("manageAffinityProgramActivations.daysLeft"),
          columnCompanyName: t(
            "manageAffinityProgramActivations.columnCompanyName"
          ),
          columnCompanyNameSubtitle: t(
            "manageAffinityProgramActivations.columnCompanyNameSubtitle"
          ),
          columnActivationStatus: t(
            "manageAffinityProgramActivations.columnActivationStatus"
          ),
          columnAction: t("manageAffinityProgramActivations.columnAction"),
          columnActionSubtitle: t(
            "manageAffinityProgramActivations.columnActionSubtitle"
          ),
          columnStartDate: t(
            "manageAffinityProgramActivations.columnStartDate"
          ),
          columnStartDateSubtitle: t(
            "manageAffinityProgramActivations.columnStartDateSubtitle"
          ),
          columnEndDate: t("manageAffinityProgramActivations.columnEndDate"),
          columnEndDateSubtitle: t(
            "manageAffinityProgramActivations.columnEndDateSubtitle"
          ),
          reactivate: t("manageAffinityProgramActivations.reactivate"),
          deactivate: t("manageAffinityProgramActivations.deactivate"),
          viewDeactivated: t(
            "manageAffinityProgramActivations.viewDeactivated"
          ),
          updateProgram: t("manageAffinityProgramActivations.updateProgram"),
          viewStats: t("manageAffinityProgramActivations.viewStats"),
          deactivateModalTitle: t(
            "manageAffinityProgramActivations.deactivateModalTitle"
          ),
          deactivateModalBody: t(
            "manageAffinityProgramActivations.deactivateModalBody"
          ),
          modalConfirm: t("manageAffinityProgramActivations.modalConfirm"),
          modalCancel: t("manageAffinityProgramActivations.modalCancel"),
          deactivateSuccess: t(
            "manageAffinityProgramActivations.deactivateSuccess"
          ),
          deactivateFailed: t(
            "manageAffinityProgramActivations.deactivateFailed"
          ),
          reactivateModalTitle: t(
            "manageAffinityProgramActivations.reactivateModalTitle"
          ),
          reactivateModalBody: t(
            "manageAffinityProgramActivations.reactivateModalBody"
          ),
          reactivateModalConfirm: t(
            "manageAffinityProgramActivations.reactivateModalConfirm"
          ),
          reactivateSuccess: t(
            "manageAffinityProgramActivations.reactivateSuccess"
          ),
          reactivateFailed: t(
            "manageAffinityProgramActivations.reactivateFailed"
          ),
          orderUpdateFailed: t(
            "manageAffinityProgramActivations.orderUpdateFailed"
          ),
          emptyStateTitle: t(
            "manageAffinityProgramActivations.emptyStateTitle"
          ),
          emptyStateBody: t("manageAffinityProgramActivations.emptyStateBody"),
          emptyStateCta: t("manageAffinityProgramActivations.emptyStateCta"),
        },
        updateAffinityProgram: {
          pageTitle: t("updateAffinityProgram.pageTitle"),
          pageDescription: t("updateAffinityProgram.pageDescription"),
          illustrationAlt: t("updateAffinityProgram.illustrationAlt"),
          watchTutorials: t("updateAffinityProgram.watchTutorials"),
          viewInstructions: t("updateAffinityProgram.viewInstructions"),
          companyNameLabel: t("updateAffinityProgram.companyNameLabel"),
          companyNameSubtitle: t("updateAffinityProgram.companyNameSubtitle"),
          logoLabel: t("updateAffinityProgram.logoLabel"),
          logoSubtitle: t("updateAffinityProgram.logoSubtitle"),
          logoUploadFailed: t("updateAffinityProgram.logoUploadFailed"),
          updateLogo: t("updateAffinityProgram.updateLogo"),
          deleteLogo: t("updateAffinityProgram.deleteLogo"),
          descriptionLabel: t("updateAffinityProgram.descriptionLabel"),
          descriptionSubtitle: t("updateAffinityProgram.descriptionSubtitle"),
          descriptionPlaceholder: t(
            "updateAffinityProgram.descriptionPlaceholder"
          ),
          titleLabel: t("updateAffinityProgram.titleLabel"),
          titleSubtitle: t("updateAffinityProgram.titleSubtitle"),
          titlePlaceholder: t("updateAffinityProgram.titlePlaceholder"),
          bannersBadge: t("updateAffinityProgram.bannersBadge"),
          bannersHelp: t("updateAffinityProgram.bannersHelp"),
          bannerLabel: t("updateAffinityProgram.bannerLabel"),
          uploadBanner: t("updateAffinityProgram.uploadBanner"),
          previewBanners: t("updateAffinityProgram.previewBanners"),
          afterPreviewHelp: t("updateAffinityProgram.afterPreviewHelp"),
          redirectHelp: t("updateAffinityProgram.redirectHelp"),
          redirectLinkLabel: t("updateAffinityProgram.redirectLinkLabel"),
          redirectLinkPlaceholder: t(
            "updateAffinityProgram.redirectLinkPlaceholder"
          ),
          requiredFieldError: t(
            "updateAffinityProgram.requiredFieldError"
          ),
          invalidRedirectLinkError: t(
            "updateAffinityProgram.invalidRedirectLinkError"
          ),
          updateButton: t("updateAffinityProgram.updateButton"),
          updateSuccess: t("updateAffinityProgram.updateSuccess"),
          updateFailed: t("updateAffinityProgram.updateFailed"),
          missingFieldsError: t("updateAffinityProgram.missingFieldsError"),
          characterCount: t("updateAffinityProgram.characterCount"),
          charactersRemaining: t("updateAffinityProgram.charactersRemaining"),
          comingSoonNote: t("updateAffinityProgram.comingSoonNote"),
        },
        viewAffinityProgramStats: {
          pageTitle: t("viewAffinityProgramStats.pageTitle"),
          pageDescription: t("viewAffinityProgramStats.pageDescription"),
          illustrationAlt: t("viewAffinityProgramStats.illustrationAlt"),
          watchTutorials: t("viewAffinityProgramStats.watchTutorials"),
          viewInstructions: t("viewAffinityProgramStats.viewInstructions"),
          totalViewsTitle: t("viewAffinityProgramStats.totalViewsTitle"),
          totalViewsDescription: t(
            "viewAffinityProgramStats.totalViewsDescription"
          ),
          totalOpensTitle: t("viewAffinityProgramStats.totalOpensTitle"),
          totalOpensDescription: t(
            "viewAffinityProgramStats.totalOpensDescription"
          ),
          totalRedirectsTitle: t(
            "viewAffinityProgramStats.totalRedirectsTitle"
          ),
          totalRedirectsDescription: t(
            "viewAffinityProgramStats.totalRedirectsDescription"
          ),
          totalInquiriesTitle: t(
            "viewAffinityProgramStats.totalInquiriesTitle"
          ),
          totalInquiriesDescription: t(
            "viewAffinityProgramStats.totalInquiriesDescription"
          ),
          inquiriesTitle: t("viewAffinityProgramStats.inquiriesTitle"),
          inquiriesDescription: t(
            "viewAffinityProgramStats.inquiriesDescription"
          ),
          inquiriesEmpty: t("viewAffinityProgramStats.inquiriesEmpty"),
          inquiryDate: t("viewAffinityProgramStats.inquiryDate"),
          inquiryName: t("viewAffinityProgramStats.inquiryName"),
          inquiryEmail: t("viewAffinityProgramStats.inquiryEmail"),
          inquiryEmployer: t("viewAffinityProgramStats.inquiryEmployer"),
          inquiryDeliveryStatus: t(
            "viewAffinityProgramStats.inquiryDeliveryStatus"
          ),
          inquiryStatusPending: t(
            "viewAffinityProgramStats.inquiryStatusPending"
          ),
          inquiryStatusSent: t(
            "viewAffinityProgramStats.inquiryStatusSent"
          ),
          inquiryStatusFailed: t(
            "viewAffinityProgramStats.inquiryStatusFailed"
          ),
          inquiriesShowing: t(
            "viewAffinityProgramStats.inquiriesShowing"
          ),
          inquiriesLoadMore: t(
            "viewAffinityProgramStats.inquiriesLoadMore"
          ),
          inquiriesLoadingMore: t(
            "viewAffinityProgramStats.inquiriesLoadingMore"
          ),
          inquiriesLoadMoreError: t(
            "viewAffinityProgramStats.inquiriesLoadMoreError"
          ),
          loadError: t("viewAffinityProgramStats.loadError"),
          retry: t("viewAffinityProgramStats.retry"),
          conversionRate: t("viewAffinityProgramStats.conversionRate"),
          memberOpens: t("viewAffinityProgramStats.memberOpens"),
          memberConversionRate: t(
            "viewAffinityProgramStats.memberConversionRate"
          ),
          nonMemberOpens: t("viewAffinityProgramStats.nonMemberOpens"),
          nonMemberConversionRate: t(
            "viewAffinityProgramStats.nonMemberConversionRate"
          ),
          members: t("viewAffinityProgramStats.members"),
          redirectConversionRate: t(
            "viewAffinityProgramStats.redirectConversionRate"
          ),
          nonMembers: t("viewAffinityProgramStats.nonMembers"),
          chartTitle: t("viewAffinityProgramStats.chartTitle"),
          legendViews: t("viewAffinityProgramStats.legendViews"),
          legendOpen: t("viewAffinityProgramStats.legendOpen"),
          legendRedirects: t("viewAffinityProgramStats.legendRedirects"),
          legendRedirectMembers: t(
            "viewAffinityProgramStats.legendRedirectMembers"
          ),
          legendRedirectNonMembers: t(
            "viewAffinityProgramStats.legendRedirectNonMembers"
          ),
          rangeMonths: t("viewAffinityProgramStats.rangeMonths"),
          rangeYear: t("viewAffinityProgramStats.rangeYear"),
          rangeInception: t("viewAffinityProgramStats.rangeInception"),
          tableMonth: t("viewAffinityProgramStats.tableMonth"),
          tableViews: t("viewAffinityProgramStats.tableViews"),
          tableOpens: t("viewAffinityProgramStats.tableOpens"),
          tableRedirects: t("viewAffinityProgramStats.tableRedirects"),
          tableMember: t("viewAffinityProgramStats.tableMember"),
          tableNonMember: t("viewAffinityProgramStats.tableNonMember"),
          tableTotal: t("viewAffinityProgramStats.tableTotal"),
          tableOverallConversion: t(
            "viewAffinityProgramStats.tableOverallConversion"
          ),
          comingSoonNote: t("viewAffinityProgramStats.comingSoonNote"),
          noProgramSelected: t("viewAffinityProgramStats.noProgramSelected"),
          back: t("viewAffinityProgramStats.back"),
        },
        activateAffinityProgram: {
          pageTitle: t("activateAffinityProgram.pageTitle"),
          pageDescription: t("activateAffinityProgram.pageDescription"),
          watchTutorials: t("activateAffinityProgram.watchTutorials"),
          viewInstructions: t("activateAffinityProgram.viewInstructions"),
          companyNameLabel: t("activateAffinityProgram.companyNameLabel"),
          companyNamePlaceholder: t("activateAffinityProgram.companyNamePlaceholder"),
          logoLabel: t("activateAffinityProgram.logoLabel"),
          logoSubtitle: t("activateAffinityProgram.logoSubtitle"),
          logoUploadFailed: t("activateAffinityProgram.logoUploadFailed"),
          logoUploadText: t("activateAffinityProgram.logoUploadText"),
          logoChangeText: t("activateAffinityProgram.logoChangeText"),
          descriptionLabel: t("activateAffinityProgram.descriptionLabel"),
          descriptionSubtitle: t("activateAffinityProgram.descriptionSubtitle"),
          descriptionPlaceholder: t("activateAffinityProgram.descriptionPlaceholder"),
          titleLabel: t("activateAffinityProgram.titleLabel"),
          titleSubtitle: t("activateAffinityProgram.titleSubtitle"),
          titlePlaceholder: t("activateAffinityProgram.titlePlaceholder"),
          characterCount: t("activateAffinityProgram.characterCount"),
          bannersBadge: t("activateAffinityProgram.bannersBadge"),
          bannersHelp: t("activateAffinityProgram.bannersHelp"),
          bannerLabel: t("activateAffinityProgram.bannerLabel"),
          uploadBanner: t("activateAffinityProgram.uploadBanner"),
          previewBanners: t("activateAffinityProgram.previewBanners"),
          redirectHelp: t("activateAffinityProgram.redirectHelp"),
          redirectLinkLabel: t("activateAffinityProgram.redirectLinkLabel"),
          redirectLinkPlaceholder: t("activateAffinityProgram.redirectLinkPlaceholder"),
          requiredFieldError: t("activateAffinityProgram.requiredFieldError"),
          invalidRedirectLinkError: t(
            "activateAffinityProgram.invalidRedirectLinkError"
          ),
          startDateLabel: t("activateAffinityProgram.startDateLabel"),
          startDatePlaceholder: t("activateAffinityProgram.startDatePlaceholder"),
          endDateLabel: t("activateAffinityProgram.endDateLabel"),
          endDatePlaceholder: t("activateAffinityProgram.endDatePlaceholder"),
          infoLine1: t("activateAffinityProgram.infoLine1"),
          infoLine2: t("activateAffinityProgram.infoLine2"),
          activationFeeLabel: t("activateAffinityProgram.activationFeeLabel"),
          activationFeeAmount: t("activateAffinityProgram.activationFeeAmount"),
          invoicingDescription: t("activateAffinityProgram.invoicingDescription"),
          activateButton: t("activateAffinityProgram.activateButton"),
          activateSuccess: t("activateAffinityProgram.activateSuccess"),
          activateFailed: t("activateAffinityProgram.activateFailed"),
          missingFieldsError: t("activateAffinityProgram.missingFieldsError"),
        },
        createDiscount: {
          bannerPickerLabel: t("createDiscount.bannerPickerLabel"),
          shopLocal: t("createDiscount.shopLocal"),
          perkCreated: t("createDiscount.perkCreated"),
          yourCompany: t("createDiscount.yourCompany"),
          description: t("createDiscount.description"),
          description2: t("createDiscount.description2"),
          bannersDiscrption: t("createDiscount.bannersDiscrption"),
          bannersDiscrption2: t("createDiscount.bannersDiscrption2"),
          featuredBannerTooltipIntro: t(
            "createDiscount.featuredBannerTooltipIntro"
          ),
          featuredBannerTooltipPositioning: t(
            "createDiscount.featuredBannerTooltipPositioning"
          ),
        },
        perksLink: {
          linkYour: t("perksLink.linkYour"),
          perks: t("perksLink.perks"),
          description: t("perksLink.description"),
          chambers: t("perksLink.chambers"),
          boards: t("perksLink.boards"),
          requestSent: t("perksLink.requestSent"),
          requestCancelled: t("perksLink.requestCancelled"),
          requestDenied: t("perksLink.requestDenied"),
          unlinked: t("perksLink.unlinked"),
          unlink: t("perksLink.unlink"),
          successfully: t("perksLink.successfully"),
          requestAccepted: t("perksLink.requestAccepted"),
          linkedPerks: t("perksLink.linkedPerks"),
          at: t("perksLink.at"),
          on: t("perksLink.on"),
          or: t("perksLink.or"),
          and: t("perksLink.and"),
          linkRequested: t("perksLink.linkRequested"),
          requestLink: t("perksLink.requestLink"),
          linkRequestSent: t("perksLink.linkRequestSent"),
          linked: t("perksLink.linked"),
          activated: t("perksLink.activated"),
          perkLinkReq: t("perksLink.perkLinkReq"),
          sendRequestLink: t("perksLink.sendRequestLink"),
          sendRequestConfirm: t("perksLink.sendRequestConfirm"),
          clickSendRequest: t("perksLink.clickSendRequest"),
          sendRequest: t("perksLink.sendRequest"),
          requestSentSuccessfully: t("perksLink.requestSentSuccessfully"),
          scrollToSeeMore: t("perksLink.scrollToSeeMore"),
          // "chambers": "Chamber's",
          // "boards": "Board's",
          // "requestSent":"Request sent successfully to support team.",
          // "requestCancelled": "Request cancelled",
          // "requestDenied": "Request denied",
          // "unlinked":"Unlinked",
          // "unlink": "Unlink",
          // "successfully": "successfully",
          // "requestAccepted":"Request accepted successfully.",
          // "linkedPerks": "linked perks on",
          // "at": "at",
          // "on": "on",
          // "or": "or",
          // "linkRequested": "Link Requested",
          // "Request Link": "Request Link"

          // "chambers": "Chamber's",
          // "boards": "Board's",
        },
        chamberList: {
          placeholder: t("chamberList.placeholder"),
          primary: t("chamberList.primary"),
          description: t("chamberList.description"),
          description2: t("chamberList.description2"),
          description3: t("chamberList.description3"),
          cancelRequest: t("chamberList.cancelRequest"),
          hoverToolTipDescription: t("chamberList.hoverToolTipDescription"),
          hoverToolTipDescription2: t("chamberList.hoverToolTipDescription2"),
          hoverToolTipDescription3: t("chamberList.hoverToolTipDescription3"),
          hoverToolTipDescription4: t("chamberList.hoverToolTipDescription4"),
          acceptRequest: t("chamberList.acceptRequest"),
          denyRequest: t("chamberList.denyRequest"),
          linkConfirmationDescription: t(
            "chamberList.linkConfirmationDescription"
          ),
          linkConfirmationDescription2: t(
            "chamberList.linkConfirmationDescription2"
          ),
          linkConfirmationDescription3: t(
            "chamberList.linkConfirmationDescription3"
          ),
          linkConfirmationDescription4: t(
            "chamberList.linkConfirmationDescription4"
          ),
          sandBoxdDescription: t("chamberList.sandBoxdDescription"),
          cancelPerksLinkRequest: t("chamberList.cancelPerksLinkRequest"),
          cancelRequestConfirm: t("chamberList.cancelRequestConfirm"),
          clickCancelRequest: t("chamberList.clickCancelRequest"),
          cancelRequestButton: t("chamberList.cancelRequestButton"),
          requestCancelledSuccessfully: t("chamberList.requestCancelledSuccessfully"),
          cancelLinkRequestDescription: t(
            "chamberList.cancelLinkRequestDescription"
          ),
          cancelLinkRequestDescription2: t(
            "chamberList.cancelLinkRequestDescription2"
          ),
          unLinkRequestDescription: t("chamberList.unLinkRequestDescription"),
          unLinkRequestDescription2: t("chamberList.unLinkRequestDescription2"),
          disablePerksLink: t("chamberList.disablePerksLink"),
          acceptRequestDescription: t("chamberList.acceptRequestDescription"),
          acceptPerksLinkRequest: t("chamberList.acceptPerksLinkRequest"),
          acceptRequestDescription2: t("chamberList.acceptRequestDescription2"),
          chamberListTitle: t("chamberList.chamberListTitle"),
          chamberListDescription: {
            step1Title: t("chamberList.chamberListDescription.step1Title"),
            step1Desc: t("chamberList.chamberListDescription.step1Desc"),
            step2Title: t("chamberList.chamberListDescription.step2Title"),
            step2Desc: t("chamberList.chamberListDescription.step2Desc"),
            step2Bullet1: t("chamberList.chamberListDescription.step2Bullet1"),
            step2Bullet2: t("chamberList.chamberListDescription.step2Bullet2"),
            step3Title: t("chamberList.chamberListDescription.step3Title"),
            step3Desc: t("chamberList.chamberListDescription.step3Desc"),
            step4Title: t("chamberList.chamberListDescription.step4Title"),
            step4Desc: t("chamberList.chamberListDescription.step4Desc"),
            step5: t("chamberList.chamberListDescription.step5"),
          },
        },
        linkIsRequired: t("linkIsRequired"),
        contestParticipationCode: {
          description: t("contestParticipationCode.description"),
          instructions: {
            Instruction1: t("contestParticipationCode.instructions.Instruction1"),
            Instruction2: t("contestParticipationCode.instructions.Instruction2"),
            Instruction3: t("contestParticipationCode.instructions.Instruction3"),
            Instruction4: t("contestParticipationCode.instructions.Instruction4"),
          },
        },
        chooseParticipating: {
          description: t("chooseParticipating.description"),
          description2: t("chooseParticipating.description2"),
        },

        fullPageShopLocal: {
          contestName: t("fullPageShopLocal.contestName"),
          prizeDescription: t("fullPageShopLocal.prizeDescription"),
          startDate: t("fullPageShopLocal.startDate"),
          endDate: t("fullPageShopLocal.endDate"),
          participatingMembers: t("fullPageShopLocal.participatingMembers"),
          prizeDrawParticipants: t("fullPageShopLocal.prizeDrawParticipants"),
          status: t("fullPageShopLocal.status"),
          updateActiveCampaign: t("fullPageShopLocal.updateActiveCampaign"),
          description: t("fullPageShopLocal.description"),
          updateMemberList: t("fullPageShopLocal.updateMemberList"),
          randomWinnerIs: t("fullPageShopLocal.randomWinnerIs"),
          EntrantEmail: t("fullPageShopLocal.EntrantEmail"),
          enteredPize: t("fullPageShopLocal.enteredPize"),
          noneSoFar: t("fullPageShopLocal.noneSoFar"),
          description2: t("fullPageShopLocal.description2"),
          description3: t("fullPageShopLocal.description3"),
        },
        updateChamberShopLocal: {
          contestUpdatedSuccessfully: t(
            "updateChamberShopLocal.contestUpdatedSuccessfully"
          ),
          close: t("updateChamberShopLocal.close"),
          title: t("updateChamberShopLocal.title"),
          description: t("updateChamberShopLocal.description"),
          description2: t("updateChamberShopLocal.description2"),
          issues: t("updateChamberShopLocal.issues"),
          endCampaign: t("updateChamberShopLocal.endCampaign"),
          description3: t("updateChamberShopLocal.description3"),
          description4: t("updateChamberShopLocal.description4"),
          description5: t("updateChamberShopLocal.description5"),
          goBack: t("updateChamberShopLocal.goBack"),
        },
        perkPartnerAdmin: {
          UnableToCreate: t("perkPartnerAdmin.UnableToCreate"),
          alreadyExists: t("perkPartnerAdmin.alreadyExists"),
          emailUsername: t("perkPartnerAdmin.emailUsername"),
          temporaryPassword: t("perkPartnerAdmin.temporaryPassword"),
          filename: t("perkPartnerAdmin.filename"),
          unableToCreateMember: t("perkPartnerAdmin.unableToCreateMember"),
          unableToCreateMemberPleaseTryAgin: t(
            "perkPartnerAdmin.unableToCreateMemberPleaseTryAgin"
          ),
          unableToCreatePerk: t("perkPartnerAdmin.unableToCreatePerk"),
          unableToCreateReward: t("perkPartnerAdmin.unableToCreateReward"),
          unableToCreateLocalContest: t(
            "perkPartnerAdmin.unableToCreateLocalContest"
          ),
          unableToCreateChamber: t("perkPartnerAdmin.unableToCreateChamber"),
          csvDashboard: t("perkPartnerAdmin.csvDashboard"),
          create: t("perkPartnerAdmin.create"),
          created: t("perkPartnerAdmin.created"),
          error: t("perkPartnerAdmin.error"),
          companyName: t("perkPartnerAdmin.companyName"),
          perkTitle: t("perkPartnerAdmin.perkTitle"),
          charRemaining: t("perkPartnerAdmin.charRemaining"),
          perkDescription: t("perkPartnerAdmin.perkDescription"),
          chamberRedirectLink: t("perkPartnerAdmin.chamberRedirectLink"),
          unverifiedChamberRedirectLink: t(
            "perkPartnerAdmin.unverifiedChamberRedirectLink"
          ),
          perkLogo: t("perkPartnerAdmin.perkLogo"),
          deleteConfirmation: t("perkPartnerAdmin.deleteConfirmation"),
          perkBanners: t("perkPartnerAdmin.perkBanners"),
          noPerkPartner: t("perkPartnerAdmin.noPerkPartner"),
        },
        messageCreated: t("messageCreated"),
        general404: {
          title: t("general404.title"),
          description: t("general404.description"),
        },
        sendLoginModal: {
          option1Header: t("sendLoginModal.option1Header"),
          option1HeaderBold: t("sendLoginModal.option1HeaderBold"),
          option1HeaderRest: t("sendLoginModal.option1HeaderRest"),
          option1HeaderReset: t("sendLoginModal.option1HeaderReset"),
          option2Header: t("sendLoginModal.option2Header"),
          option2HeaderBold: t("sendLoginModal.option2HeaderBold"),
          option2HeaderRest: t("sendLoginModal.option2HeaderRest"),
          option2HeaderReset: t("sendLoginModal.option2HeaderReset"),
          option3Header: t("sendLoginModal.option3Header"),
          downloadCredentials: t("sendLoginModal.downloadCredentials"),
          with: t("sendLoginModal.with"),
          to: t("sendLoginModal.to"),
          at: t("sendLoginModal.at"),
          memberEmail: t("sendLoginModal.memberEmail"),
          memberPassword: t("sendLoginModal.memberPassword"),
          memberTitle: t("sendLoginModal.memberTitle"),
          csvFilename: t("sendLoginModal.csvfilname"),
          memberTitle2: t("sendLoginModal.memberTitle2"),
          close: t("sendLoginModal.close"),
          goBack: t("sendLoginModal.goBack"),
          download: t("sendLoginModal.download"),
          resendLoginCredentials: t("sendLoginModal.resendLoginCredentials"),
          sendLoginCredentials: t("sendLoginModal.sendLoginCredentials"),
          changeSuccess: t("sendLoginModal.changeSuccess"),
          newTempPassword: t("sendLoginModal.newTempPassword"),
          description: t("sendLoginModal.description"),
          description2: t("sendLoginModal.description2"),
          instruction1: t("sendLoginModal.instruction1"),
          instruction2: t("sendLoginModal.instruction2"),
          sendEmail: t("sendLoginModal.sendEmail"),
          sendAutoEmail: t("sendLoginModal.sendAutoEmail"),
          preview: t("sendLoginModal.preview"),
          downloadCredentialsInfo: t("sendLoginModal.downloadCredentialsInfo"),
          clickHere: t("sendLoginModal.clickHere"),
          info2: t("sendLoginModal.info2"),
          massEmailInstruction: t("sendLoginModal.massEmailInstruction"),
          temporaryPasswordNote: t("sendLoginModal.temporaryPasswordNote"),
          noteTitle: t("sendLoginModal.noteTitle"),
          resetPasswordSubtitle: t("sendLoginModal.resetPasswordSubtitle"),
          manuallyDescReset: t("sendLoginModal.manuallyDescReset"),
          automaticallyDescReset: t("sendLoginModal.automaticallyDescReset"),
          previewEmail: t("sendLoginModal.previewEmail"),
          changeLoginEmailSubtitle: t("sendLoginModal.changeLoginEmailSubtitle"),
          manuallyDescChangeEmail: t("sendLoginModal.manuallyDescChangeEmail"),
          automaticallyDescChangeEmail: t("sendLoginModal.automaticallyDescChangeEmail"),
          sendCredentialsSubtitle: t("sendLoginModal.sendCredentialsSubtitle"),
          manuallyDescSendCreds: t("sendLoginModal.manuallyDescSendCreds"),
          automaticallyDescSendCreds: t("sendLoginModal.automaticallyDescSendCreds"),
          credentialEmailSentSuccess: t("sendLoginModal.credentialEmailSentSuccess"),
        },

        privatePerk: {
          instructions: {
            oneByOneEmail: t("privatePerk.instructions.oneByOneEmail"),
            csvImport: t("privatePerk.instructions.csvImport"),
            csvNote: t("privatePerk.instructions.csvNote"),
            removeEmail: t("privatePerk.instructions.removeEmail"),
          },
          autocomplete: {
            placeholder: t("privatePerk.autocomplete.placeholder"),
          },
          importantNote: {
            prefix: t("privatePerk.importantNote.prefix"),
            message: t("privatePerk.importantNote.message"),
            action: t("privatePerk.importantNote.action"),
            postActionMessage: t("privatePerk.importantNote.postActionMessage"),
          },
        },
        fullPagePerk: {
          companyName: t("fullPagePerk.companyName"),
          title: t("fullPagePerk.title"),
          numberOfChambersAvailableTo: t(
            "fullPagePerk.numberOfChambersAvailableTo"
          ),
          views: t("fullPagePerk.views"),
          opens: t("fullPagePerk.opens"),
          redeems: t("fullPagePerk.redeems"),
          createdAtUtcMilli: t("fullPagePerk.createdAtUtcMilli"),
          updatedAtUtcMilli: t("fullPagePerk.updatedAtUtcMilli"),
        },
        accountDeletionGuide: {
          title: t("accountDeletionGuide.title"),
          instruction: t("accountDeletionGuide.instruction"),
          deleteThroughAppTitle: t("accountDeletionGuide.deleteThroughAppTitle"),
          deleteThroughAppStep1: t("accountDeletionGuide.deleteThroughAppStep1"),
          deleteThroughAppStep2: t("accountDeletionGuide.deleteThroughAppStep2"),
          deleteThroughAppStep3: t("accountDeletionGuide.deleteThroughAppStep3"),
          deleteThroughAppStep4: t("accountDeletionGuide.deleteThroughAppStep4"),
          deleteThroughAppStep5: t("accountDeletionGuide.deleteThroughAppStep5"),
          requestByEmailTitle: t("accountDeletionGuide.requestByEmailTitle"),
          requestByEmailStep1: t("accountDeletionGuide.requestByEmailStep1"),
          requestByEmailStep2: t("accountDeletionGuide.requestByEmailStep2"),
          emailAddress: t("accountDeletionGuide.emailAddress"),
          dataRetentionNote: t("accountDeletionGuide.dataRetentionNote"),
          perkRedemptionDataNote: t(
            "accountDeletionGuide.perkRedemptionDataNote"
          ),
        },
        exportCSV: t("exportCSV"),
        downloadCSV: t("downloadCSV"),
        plaseWait: t("plaseWait"),

        chamberMember: {
          modal: {
            title: t("chamberMember.modal.title"),
            advertisingDisclaimerTitle: t(
              "chamberMember.modal.advertisingDisclaimerTitle"
            ),
            acceptText: t("chamberMember.modal.acceptText"),
            cancelText: t("chamberMember.modal.cancelText"),
            category: t("chamberMember.modal.category"),
            memberProfile: t("chamberMember.modal.memberProfile"),
          },
          agreement: {
            lead: t("chamberMember.agreement.lead"),
            section1Title: t("chamberMember.agreement.section1Title"),
            section1Text: t("chamberMember.agreement.section1Text"),
            section2Title: t("chamberMember.agreement.section2Title"),
            section2Text: t("chamberMember.agreement.section2Text"),
            section3Title: t("chamberMember.agreement.section3Title"),
            section3Text: t("chamberMember.agreement.section3Text"),
            section4Title: t("chamberMember.agreement.section4Title"),
            section4Text: t("chamberMember.agreement.section4Text"),
            section5Title: t("chamberMember.agreement.section5Title"),
            section5Text: t("chamberMember.agreement.section5Text"),
            effectiveDisclaimerText: t(
              "chamberMember.agreement.effectiveDisclaimerText"
            ),
          },
          buttons: {
            accept: t("chamberMember.buttons.accept"),
            cancel: t("chamberMember.buttons.cancel"),
          },
          categoryBannerSentForApproval: t("chamberMember.categoryBannerSentForApproval"),
          categoryBannerUpdatedSuccessfully: t("chamberMember.categoryBannerUpdatedSuccessfully"),
          categoryAdvertisingNotActivatedTitle: t("chamberMember.categoryAdvertisingNotActivatedTitle"),
          categoryAdvertisingNotActivatedDesc: t("chamberMember.categoryAdvertisingNotActivatedDesc"),
          categoryAdvertisingIntro: t("chamberMember.categoryAdvertisingIntro"),
          purchaseCategoryBannerTitle: t("chamberMember.purchaseCategoryBannerTitle"),
          purchaseCategoryBannerDesc: t("chamberMember.purchaseCategoryBannerDesc"),
          purchaseCategoryBannerSendRequest: t("chamberMember.purchaseCategoryBannerSendRequest"),
          categoryAdvertisingBannerUploadDesc: t("chamberMember.categoryAdvertisingBannerUploadDesc"),
          categoryAdvertisingBannerQualityDesc: t("chamberMember.categoryAdvertisingBannerQualityDesc"),
          categoryAdvertisingActiveApprovalDesc: t("chamberMember.categoryAdvertisingActiveApprovalDesc"),
          categoryAdvertisingActiveBannerTitle: t("chamberMember.categoryAdvertisingActiveBannerTitle"),
          memberStatus: t("chamberMember.memberStatus"),
          setAvaibility: t("chamberMember.setAvaibility"),
          chamberSelect: t("chamberMember.chamberSelect"),
          settings: {
            settingsText1: t("chamberMember.settings.settingsText1"),
            settingsText2: t("chamberMember.settings.settingsText2"),
            goto: t("chamberMember.settings.goto"),
            or: t("chamberMember.settings.or"),
            technicalIssueHere: t("chamberMember.settings.technicalIssueHere"),
            memberDashboardQuestions: t(
              "chamberMember.settings.memberDashboardQuestions"
            ),
            chamberPerksDashboardQuestions: t(
              "chamberMember.settings.chamberPerksDashboardQuestions"
            ),
            pleaseTypeQuestionHere: t(
              "chamberMember.settings.pleaseTypeQuestionHere"
            ),
            pleaseTypeFeedbackhere: t(
              "chamberMember.settings.pleaseTypeFeedbackhere"
            ),
          },
        },
        perkPartnerLable: {
          chamber: t("perkPartnerLable.chamber"),
          perkEnabled: t("perkPartnerLable.perkEnabled"),
          totalViews: t("perkPartnerLable.totalViews"),
          unverifiedViews: t("perkPartnerLable.unverifiedViews"),
          verifiedViews: t("perkPartnerLable.verifiedViews"),
          ineligibleViews: t("perkPartnerLable.ineligibleViews"),
          totalOpens: t("perkPartnerLable.totalOpens"),
          unverifiedOpens: t("perkPartnerLable.unverifiedOpens"),
          verifiedOpens: t("perkPartnerLable.verifiedOpens"),
          ineligibleOpens: t("perkPartnerLable.ineligibleOpens"),
          totalRedirects: t("perkPartnerLable.totalRedirects"),
          noEmployerSelectedRedirects: t(
            "perkPartnerLable.noEmployerSelectedRedirects"
          ),
          employerSelectedRedirects: t(
            "perkPartnerLable.employerSelectedRedirects"
          ),
          employerSelectedWithoutAccessToEmailRedirects: t(
            "perkPartnerLable.employerSelectedWithoutAccessToEmailRedirects"
          ),
          views: t("perkPartnerLable.views"),
          opens: t("perkPartnerLable.opens"),
        },
        jobTitles: {
          jobTitle1: t("jobTitles.jobTitle1"),
          tooltipText1: t("jobTitles.tooltipText1"),

          jobTitle2: t("jobTitles.jobTitle2"),
          tooltipText2: t("jobTitles.tooltipText2"),

          jobTitle3: t("jobTitles.jobTitle3"),
          tooltipText3: t("jobTitles.tooltipText3"),
        },
        contactTips: t("contactTips"),
        inPerson: t("inPerson"),
        online: t("online"),
        contactCompany: t("contactCompany"),

        privacyPolicy: {
          title: t("privacyPolicy.title"),
          lastModified: t("privacyPolicy.lastModified"),

          introduction: {
            title: t("privacyPolicy.introduction.title"),
            description: [t("privacyPolicy.introduction.description")],
          },

          informationWeCollect: {
            title: t("privacyPolicy.informationWeCollect.title"),
            description: [t("privacyPolicy.informationWeCollect.description")],
          },

          informationYouProvide: {
            title: t("privacyPolicy.informationYouProvide.title"),
            description: [t("privacyPolicy.informationYouProvide.description")],
          },

          automaticInformation: {
            title: t("privacyPolicy.automaticInformation.title"),
            description: [t("privacyPolicy.automaticInformation.description")],
          },

          thirdPartyInformation: {
            title: t("privacyPolicy.thirdPartyInformation.title"),
            description: [t("privacyPolicy.thirdPartyInformation.description")],
          },

          howWeUseInformation: {
            title: t("privacyPolicy.howWeUseInformation.title"),
            description: [t("privacyPolicy.howWeUseInformation.description")],
          },

          disclosureOfInformation: {
            title: t("privacyPolicy.disclosureOfInformation.title"),
            description: [t("privacyPolicy.disclosureOfInformation.description")],
          },
          transferringPersonalInformation: {
            title: t("privacyPolicy.transferringPersonalInformation.title"),
            description: [
              t("privacyPolicy.transferringPersonalInformation.description"),
            ],
          },

          choicesAboutInformation: {
            title: t("privacyPolicy.choicesAboutInformation.title"),
            description: [t("privacyPolicy.choicesAboutInformation.description")],
          },

          dataSecurity: {
            title: t("privacyPolicy.dataSecurity.title"),
            description: [t("privacyPolicy.dataSecurity.description")],
          },
          dataRetention: {
            title: t("privacyPolicy.dataRetention.title"),
            description: [t("privacyPolicy.dataRetention.description")],
          },
          childrenUnder13: {
            title: t("privacyPolicy.childrenUnder13.title"),
            description: [t("privacyPolicy.childrenUnder13.description")],
          },

          accessingAndCorrectingInformation: {
            title: t("privacyPolicy.accessingAndCorrectingInformation.title"),
            description: [
              t("privacyPolicy.accessingAndCorrectingInformation.description"),
            ],
          },

          withdrawingConsent: {
            title: t("privacyPolicy.withdrawingConsent.title"),
            description: [t("privacyPolicy.withdrawingConsent.description")],
          },

          changesToThisPolicy: {
            title: t("privacyPolicy.changesToThisPolicy.title"),
            description: [t("privacyPolicy.changesToThisPolicy.description")],
          },

          contactUs: {
            title: t("privacyPolicy.contactUs.title"),
            description: [t("privacyPolicy.contactUs.description")],
          },
        },
        passwordRequiredCharacters: t("passwordRequiredCharacters"),
        listed: t("listed"),
        shopLocaldescription: t("shopLocaldescription"),
        shopLocaldescription2: t("shopLocaldescription2"),
        available: t("available"),

        createChamberMemberSuggested: {
          dashboardAccountExistsText: t(
            "createChamberMemberSuggested.dashboardAccountExistsText"
          ),
          alreadyExists: t("createChamberMemberSuggested.alreadyExists"),
          alternativeEmail: t("createChamberMemberSuggested.alternativeEmail"),
          alternativeEmailPlus: t(
            "createChamberMemberSuggested.alternativeEmailPlus"
          ),
          click: t("createChamberMemberSuggested.click"),
          useAlternative: t("createChamberMemberSuggested.useAlternative"),
          toCreateMember: t("createChamberMemberSuggested.toCreateMember"),
          emailUsername: t("createChamberMemberSuggested.emailUsername"),
          back: t("createChamberMemberSuggested.back"),
          title: t("createChamberMemberSuggested.title"),
          bodyLine1Start: t("createChamberMemberSuggested.bodyLine1Start"),
          bodyLine1End: t("createChamberMemberSuggested.bodyLine1End"),
          bodyLine2Start: t("createChamberMemberSuggested.bodyLine2Start"),
          bodyLine2End: t("createChamberMemberSuggested.bodyLine2End"),
          bodyLine3: t("createChamberMemberSuggested.bodyLine3"),
          useAlternativeButton: t("createChamberMemberSuggested.useAlternativeButton"),
        },

        hoverTooltip: {
          description: t("hoverTooltip.description"),
          youtubeURL: t("hoverTooltip.youtubeURL"),
          imageDescription: t("hoverTooltip.imageDescription"),
          memberProfile: t("hoverTooltip.memberProfile"),
          profile: t("hoverTooltip.profile"),
          perk: t("hoverTooltip.perk"),
          perkPromote: t("hoverTooltip.perkPromote"),
          recommend: t("hoverTooltip.recommend"),
        },

        manageMemberProfile: {
          name: t("manageMemberProfile.name"),
          nameDescription: t("manageMemberProfile.nameDescription"),
          statusForTable: t("manageMemberProfile.statusForTable"),
          annualFeeForTable: t("manageMemberProfile.annualFeeForTable"),
          annualFeeDescription: t("manageMemberProfile.annualFeeDescription"),
          startDateForTable: t("manageMemberProfile.startDateForTable"),
          startDateDescription: t("manageMemberProfile.startDateDescription"),
          expiryDateForTable: t("manageMemberProfile.expiryDateForTable"),
          expiryDateDescription: t("manageMemberProfile.expiryDateDescription"),
          actionLabel: t("manageMemberProfile.actionLabel"),
          actionDescription: t("manageMemberProfile.actionDescription"),
          reActivatedSuccessfully: t(
            "manageMemberProfile.reActivatedSuccessfully"
          ),
          alreadyReactivated: t(
            "manageMemberProfile.alreadyReactivated"
          ),
          deactivate: t("manageMemberProfile.deactivate"),
          deactivateDiscription: t("manageMemberProfile.deactivateDiscription"),
          deactivateDiscription2: t("manageMemberProfile.deactivateDiscription2"),
          deactivateDiscription3: t("manageMemberProfile.deactivateDiscription3"),
          deactivateDiscription4: t("manageMemberProfile.deactivateDiscription4"),
          memberProfile: t("manageMemberProfile.memberProfile"),
          memberProfileDescription: t(
            "manageMemberProfile.memberProfileDescription"
          ),
          memberProfileDescription2: t(
            "manageMemberProfile.memberProfileDescription2"
          ),
          memberProfileDescription3: t(
            "manageMemberProfile.memberProfileDescription3"
          ),
          AnnualFee: t("manageMemberProfile.AnnualFee"),
          activate: t("manageMemberProfile.activate"),
          enhancedMemberProfile: t("manageMemberProfile.enhancedMemberProfile"),
          enterStartDate: t("manageMemberProfile.enterStartDate"),
          emailNotification: t("manageMemberProfile.emailNotification"),
          bannersDeactivation: t("manageMemberProfile.bannersDeactivation"),
          reactivateBanners: t("manageMemberProfile.reactivateBanners"),
          selectToActivate: t("manageMemberProfile.selectToActivate"),
          cancel: t("manageMemberProfile.cancel"),
          Deactivate: t("manageMemberProfile.Deactivate"),
          reactivateStartDate: t("manageMemberProfile.reactivateStartDate"),
          loadingChamberMembers: t("manageMemberProfile.loadingChamberMembers"),
          close: t("manageMemberProfile.close"),
          reaActivate: t("manageMemberProfile.reaActivate"),
          perksBanners: t("manageMemberProfile.perksBanners"),
          viewReActivation: t("manageMemberProfile.viewReActivation"),
          placeholderSearch: t("manageMemberProfile.placeholderSearch"),
          howtoActivateMember: t("manageMemberProfile.howtoActivateMember"),
          clicktoUpload: t("manageMemberProfile.clicktoUpload"),
          title: t("manageMemberProfile.title"),
          subtitle: t("manageMemberProfile.subtitle"),
          upgradeBanner: t("manageMemberProfile.upgradeBanner"),
          addBannerChip: t("manageMemberProfile.addBannerChip"),
          activeForDays: t("manageMemberProfile.activeForDays"),
          activateEnhancedTitle: t("manageMemberProfile.activateEnhancedTitle"),
          activateEnhancedDescription: t("manageMemberProfile.activateEnhancedDescription"),
          activateEnhancedDescriptionLine2: t("manageMemberProfile.activateEnhancedDescriptionLine2"),
          manageFeaturedMemberTitle: t("manageMemberProfile.manageFeaturedMemberTitle"),
          manageFeaturedMemberDescPart1: t("manageMemberProfile.manageFeaturedMemberDescPart1"),
          manageFeaturedMemberDescLink: t("manageMemberProfile.manageFeaturedMemberDescLink"),
          manageFeaturedMemberDescPart2: t("manageMemberProfile.manageFeaturedMemberDescPart2"),
          activateInfoText1: t("manageMemberProfile.activateInfoText1"),
          activateInfoText2: t("manageMemberProfile.activateInfoText2"),
          featuredMemberActivatedSuccess: t("manageMemberProfile.featuredMemberActivatedSuccess"),
          pleaseSelectMemberAndDate: t("manageMemberProfile.pleaseSelectMemberAndDate"),
          failedToActivate: t("manageMemberProfile.failedToActivate"),
          annualFeaturedMemberFee: t("manageMemberProfile.annualFeaturedMemberFee"),
          chamberNonDuesRevenue: t("manageMemberProfile.chamberNonDuesRevenue"),
          invoicingDescription: t("manageMemberProfile.invoicingDescription"),
          invoicingDescriptionBold: t("manageMemberProfile.invoicingDescriptionBold"),
          activateButton: t("manageMemberProfile.activateButton"),
          activatedStatus: t("manageMemberProfile.activatedStatus"),
          notActivatedStatus: t("manageMemberProfile.notActivatedStatus"),
          reactivatedStatus: t("manageMemberProfile.reactivatedStatus"),
          reactivatedAsOf: t("manageMemberProfile.reactivatedAsOf"),
          timeRemainingMonthsDays: t("manageMemberProfile.timeRemainingMonthsDays"),
          timeRemainingMonths: t("manageMemberProfile.timeRemainingMonths"),
          timeRemainingDays: t("manageMemberProfile.timeRemainingDays"),
          period: t("manageMemberProfile.period"),
          daysRemaining: t("manageMemberProfile.daysRemaining"),
          expiresSoon: t("manageMemberProfile.expiresSoon"),
          awaitingActivation: t("manageMemberProfile.awaitingActivation"),
          activateFeaturedBanner: t("manageMemberProfile.activateFeaturedBanner"),
        },
        requestLink: t("requestLink"),
        pleaseWait: t("pleaseWait"),
        updateChamberPricePreview: t("updateChamberPricePreview"),
        validImageLink: t("validImageLink"),
        waitingForApproval: t("waitingForApproval"),
        approved: t("approved"),
        approve: t("approve"),
        updatedSuccessfully: t("updatedSuccessfully"),
        memberUpdated: t("memberUpdated"),
        activatedSuccessfully: t("activatedSuccessfully"),
        deactivatedSuccessfully: t("deactivatedSuccessfully"),
        revenueCalculator: {
          mainTitle: t("revenueCalculator.mainTitle"),
          subtitle: t("revenueCalculator.subtitle"),
          enterNumberOfMembers: t("revenueCalculator.enterNumberOfMembers"),
          enterThe: t("revenueCalculator.enterThe"),
          ofMemberBusinesses: t("revenueCalculator.ofMemberBusinesses"),
          featureMemberDescription: t(
            "revenueCalculator.featureMemberDescription"
          ),
          featureMemberBannerRevenue: t(
            "revenueCalculator.featureMemberBannerRevenue"
          ),
          titleSponsorshipQuestion: t(
            "revenueCalculator.titleSponsorshipQuestion"
          ),
          titleSponsorshipNote: t("revenueCalculator.titleSponsorshipNote"),
          categoryBannerDescription: t(
            "revenueCalculator.categoryBannerDescription"
          ),
          categoryBannerSalesRevenue: t(
            "revenueCalculator.categoryBannerSalesRevenue"
          ),
          totalLabel: t("revenueCalculator.totalLabel"),
        },
        revenueBreakdown: {
          noDataAvailable: t("revenueBreakdown.noDataAvailable"),
          mainTitle: t("revenueBreakdown.mainTitle"),
          subtitle: t("revenueBreakdown.subtitle"),
          tableHeaders: {
            category: t("revenueBreakdown.tableHeaders.category"),
            sold: t("revenueBreakdown.tableHeaders.sold"),
            revenuePerBanner: t("revenueBreakdown.tableHeaders.revenuePerBanner"),
            currentRevenue: t("revenueBreakdown.tableHeaders.currentRevenue"),
            potentialRevenue: t("revenueBreakdown.tableHeaders.potentialRevenue"),
            percentAchieved: t("revenueBreakdown.tableHeaders.percentAchieved"),
          },
          featuredMemberBanners: t("revenueBreakdown.featuredMemberBanners"),
          categoryBannerSales: t("revenueBreakdown.categoryBannerSales"),
          titleSponsorshipSale: t("revenueBreakdown.titleSponsorshipSale"),
          estimatedRecommendation: t("revenueBreakdown.estimatedRecommendation"),
          estimatedRecommendedAmount: t(
            "revenueBreakdown.estimatedRecommendedAmount"
          ),
          priceBasedOnMembers: t("revenueBreakdown.priceBasedOnMembers"),
          potentialRevenueBasedOnPercentage: t(
            "revenueBreakdown.potentialRevenueBasedOnPercentage"
          ),
          total: t("revenueBreakdown.total"),
          categoryBanners: t("revenueBreakdown.categoryBanners"),
          and: t("revenueBreakdown.and"),
          summaryPart1: t("revenueBreakdown.summaryPart1"),
          summaryPart2: t("revenueBreakdown.summaryPart2"),
          summaryPart3: t("revenueBreakdown.summaryPart3"),
          summaryPart4: t("revenueBreakdown.summaryPart4"),
        },
        manageMembersPage: {
          title: t("manageMembersPage.title"),
          description: t("manageMembersPage.description"),
          viewInstructions: t("manageMembersPage.viewInstructions"),
          viewInstructionsTab: t("manageMembersPage.viewInstructions"),
          howMembersLogIn: t("manageMembersPage.howMembersLogIn"),
          exportMemberDirectory: t("manageMembersPage.exportMemberDirectory"),
          searchPlaceholder: t("manageMembersPage.searchPlaceholder"),
          companyName: t("manageMembersPage.companyName"),
          logInStatus: t("manageMembersPage.logInStatus"),
          membershipStatus: t("manageMembersPage.membershipStatus"),
          actions: t("manageMembersPage.actions"),
          loggedIn: t("manageMembersPage.loggedIn"),
          neverLoggedIn: t("manageMembersPage.neverLoggedIn"),
          suspended: t("manageMembersPage.suspended"),
          active: t("manageMembersPage.active"),
          suspendDialogDescription: t("manageMembersPage.suspendDialogDescription"),
          activateDialogDescription: t("manageMembersPage.activateDialogDescription"),
          areYouSureSuspend: t("manageMembersPage.areYouSureSuspend"),
          areYouSureActivate: t("manageMembersPage.areYouSureActivate"),
          suspendButton: t("manageMembersPage.suspendButton"),
          activateButton: t("manageMembersPage.activateButton"),
          cancelButton: t("manageMembersPage.cancelButton"),
          resetPassword: t("manageMembersPage.resetPassword"),
          editMember: t("manageMembersPage.editMember"),
          suspend: t("manageMembersPage.suspend"),
          delete: t("manageMembersPage.delete"),
          noMembersFound: t("manageMembersPage.noMembersFound"),
          updating: t("manageMembersPage.updating"),
          resetPasswordDialogTitle: t("manageMembersPage.resetPasswordDialogTitle"),
          resetPasswordDialogDesc1: t("manageMembersPage.resetPasswordDialogDesc1"),
          resetPasswordDialogDesc2: t("manageMembersPage.resetPasswordDialogDesc2"),
          areYouSureContinue: t("manageMembersPage.areYouSureContinue"),
          yes: t("manageMembersPage.yes"),
          no: t("manageMembersPage.no"),
          deleteDialogTitle: t("manageMembersPage.deleteDialogTitle"),
          deleteDialogDesc: t("manageMembersPage.deleteDialogDesc"),
          deleteDialogPlaceholder: t("manageMembersPage.deleteDialogPlaceholder"),
          cancel: t("manageMembersPage.cancel"),
          accountSuspension: t("manageMembersPage.accountSuspension"),
          accountStatusText: t("manageMembersPage.accountStatusText"),
          statusRightNow: t("manageMembersPage.statusRightNow"),
          keepStatus: t("manageMembersPage.keepStatus"),
          activate: t("manageMembersPage.activate"),
          update: t("manageMembersPage.update"),
          newPasswordGenerated: t("manageMembersPage.newPasswordGenerated"),
          manuallySend: t("manageMembersPage.manuallySend"),
          sendPasswordResetInstructions: t("manageMembersPage.sendPasswordResetInstructions"),
          downloadCredentialsDesc: t("manageMembersPage.downloadCredentialsDesc"),
          clickHere: t("manageMembersPage.clickHere"),
          previewTemplateEmail: t("manageMembersPage.previewTemplateEmail"),
          downloadCredentials: t("manageMembersPage.downloadCredentials"),
          automaticallySend: t("manageMembersPage.automaticallySend"),
          emailPasswordResetInstructions: t("manageMembersPage.emailPasswordResetInstructions"),
          sendEmailDesc: t("manageMembersPage.sendEmailDesc"),
          sendEmailDescEnd: t("manageMembersPage.sendEmailDescEnd"),
          sendEmail: t("manageMembersPage.sendEmail"),
          previewEmail: t("manageMembersPage.previewEmail"),
          emailSentSuccess: t("manageMembersPage.emailSentSuccess"),
          close: t("manageMembersPage.close"),
          copyPassword: t("manageMembersPage.copyPassword"),
          newTempPasswordFor: t("manageMembersPage.newTempPasswordFor"),
          is: t("manageMembersPage.is"),
          savePasswordNote: t("manageMembersPage.savePasswordNote"),
          passwordCopied: t("manageMembersPage.passwordCopied"),
        },
        memberCredentialsTable: {
          searchPlaceholder: t("memberCredentialsTable.searchPlaceholder"),
        },
        common: {
          active: t("common.active"),
          activated: t("common.activated"),
          activeStatus: t("common.activeStatus"),
          inactive: t("common.inactive"),
          menu: t("common.menu"),
          sponsoredBy: t("common.sponsoredBy"),
          general: t("common.general"),
          dashboard: t("common.dashboard"),
          errorLoadingMembers: t("common.errorLoadingMembers"),
          comingSoon: t("common.comingSoon"),
          comingSoonDesc: t("common.comingSoonDesc"),
          video: t("common.video"),
          okay: t("common.okay"),
          selectMember: t("common.selectMember"),
          createMemberCompanyProfile: t("common.createMemberCompanyProfile"),
          updateMemberCompanyProfile: t("common.updateMemberCompanyProfile"),
          previewBanners: t("common.previewBanners"),
          bannerPreview: t("common.bannerPreview"),
          redirectUrl: t("common.redirectUrl"),
          featuredMemberBannerGuidance: t("common.featuredMemberBannerGuidance"),
        },
        sendMemberCredentials: {
          title: t("sendMemberCredentials.title"),
          description: t("sendMemberCredentials.description"),
          downloadMemberCredentials: t("sendMemberCredentials.downloadMemberCredentials"),
          emailAllMemberCredentials: t("sendMemberCredentials.emailAllMemberCredentials"),
          totalMemberLogIns: t("sendMemberCredentials.totalMemberLogIns"),
          percentage: t("sendMemberCredentials.percentage"),
          newTempPasswordGenerated: t("sendMemberCredentials.newTempPasswordGenerated"),
          manuallySendTitle: t("sendMemberCredentials.manuallySendTitle"),
          manuallySendDesc: t("sendMemberCredentials.manuallySendDesc"),
          automaticallySendTitle: t("sendMemberCredentials.automaticallySendTitle"),
          automaticallySendDesc: t("sendMemberCredentials.automaticallySendDesc"),
        },
        addMemberBanners: {
          title: t("addMemberBanners.title"),
          description: t("addMemberBanners.description"),
          howToActivateMember: t("addMemberBanners.howToActivateMember"),
          chooseMember: t("addMemberBanners.chooseMember"),
          chooseMemberDesc: t("addMemberBanners.chooseMemberDesc"),
          selectMemberCompanyName: t("addMemberBanners.selectMemberCompanyName"),
          featuredMemberProfileBanners: t("addMemberBanners.featuredMemberProfileBanners"),
          featuredMemberProfileDesc: t("addMemberBanners.featuredMemberProfileDesc"),
          bannerLabel: t("addMemberBanners.bannerLabel"),
          uploadBanner: t("addMemberBanners.uploadBanner"),
          perks: t("addMemberBanners.perks"),
          createPerk: t("addMemberBanners.createPerk"),
          perkBannerLabel: t("addMemberBanners.perkBannerLabel"),
          uploadPerkBanner: t("addMemberBanners.uploadPerkBanner"),
          selectMemberFirst: t("addMemberBanners.selectMemberFirst"),
          selectMemberDesc: t("addMemberBanners.selectMemberDesc"),
          bannerSavedSuccess: t("addMemberBanners.bannerSavedSuccess"),
          bannerDeletedSuccess: t("addMemberBanners.bannerDeletedSuccess"),
          errorSavingBanner: t("addMemberBanners.errorSavingBanner"),
          errorDeletingBanner: t("addMemberBanners.errorDeletingBanner"),
          updateBannersButton: t("addMemberBanners.updateBannersButton"),
          previewBanners: t("addMemberBanners.previewBanners"),
          listOfPerks: t("addMemberBanners.listOfPerks"),
          perkUploadDesc: t("addMemberBanners.perkUploadDesc"),
          createPerkLink: t("addMemberBanners.createPerkLink"),
          perkUploadDescSuffix: t("addMemberBanners.perkUploadDescSuffix"),
          featuredMemberPerkBanner: t("addMemberBanners.featuredMemberPerkBanner"),
          banner: t("addMemberBanners.banner"),
          perkLabel: t("addMemberBanners.perkLabel"),
          noPerksDesc: t("addMemberBanners.noPerksDesc"),
          createAPerk: t("addMemberBanners.createAPerk"),
          noPerksDescSuffix: t("addMemberBanners.noPerksDescSuffix"),
          featuredMemberPerkBannersLabel: t("addMemberBanners.featuredMemberPerkBannersLabel"),
        },
        listAllChamberDiscounts: {
          title: t("listAllChamberDiscounts.title"),
          description: t("listAllChamberDiscounts.description"),
          exportButton: t("listAllChamberDiscounts.exportButton"),
          updateMemberPerks: t("listAllChamberDiscounts.updateMemberPerks"),
          deletePerk: t("listAllChamberDiscounts.deletePerk"),
          deleteConfirmation: t("listAllChamberDiscounts.deleteConfirmation"),
          cannotBeUndone: t("listAllChamberDiscounts.cannotBeUndone"),
          deleteButton: t("listAllChamberDiscounts.deleteButton"),
        },
        memberManagePerks: {
          perkTitle: t("memberManagePerks.perkTitle"),
          perkTitleDesc: t("memberManagePerks.perkTitleDesc"),
          redeemableBy: t("memberManagePerks.redeemableBy"),
          redemptionMethod: t("memberManagePerks.redemptionMethod"),
          views: t("memberManagePerks.views"),
          createdDate: t("memberManagePerks.createdDate"),
          createdDateDesc: t("memberManagePerks.createdDateDesc"),
          endDate: t("memberManagePerks.endDate"),
          endDateDesc: t("memberManagePerks.endDateDesc"),
          actions: t("memberManagePerks.actions"),
          updatePerk: t("memberManagePerks.updatePerk"),
          deletePerk: t("memberManagePerks.deletePerk"),
          deleteConfirmation: t("memberManagePerks.deleteConfirmation"),
          cannotBeUndone: t("memberManagePerks.cannotBeUndone"),
          deleteButton: t("memberManagePerks.deleteButton"),
          searchPlaceholder: t("memberManagePerks.searchPlaceholder"),
          noExpiry: t("memberManagePerks.noExpiry"),
        },
        updateMemberPerk: {
          pageTitle: t("updateMemberPerk.pageTitle"),
          pageTitleFor: t("updateMemberPerk.pageTitleFor"),
          pageSubtitle: t("updateMemberPerk.pageSubtitle"),
          perkTitleHelperText: t("updateMemberPerk.perkTitleHelperText"),
          perkDescHelperText: t("updateMemberPerk.perkDescHelperText"),
          perkBannersLabel: t("updateMemberPerk.perkBannersLabel"),
          sendUpdatedPerkNotification: t("updateMemberPerk.sendUpdatedPerkNotification"),
          onePerkNotificationPerDay: t("updateMemberPerk.onePerkNotificationPerDay"),
          updateMemberPerkButton: t("updateMemberPerk.updateMemberPerkButton"),
          successTitle: t("updateMemberPerk.successTitle"),
          successTitle2: t("updateMemberPerk.successTitle2"),
          errorUpdatingPerk: t("updateMemberPerk.errorUpdatingPerk"),
        },
        createMemberForm: {
          companyInformation: t("createMemberForm.companyInformation"),
          companyInfoDesc: t("createMemberForm.companyInfoDesc"),
          companyName: t("createMemberForm.companyName"),
          companyContactName: t("createMemberForm.companyContactName"),
          companyDescription: t("createMemberForm.companyDescription"),
          companyContactPhone: t("createMemberForm.companyContactPhone"),
          companyWebsite: t("createMemberForm.companyWebsite"),
          companyContactEmail: t("createMemberForm.companyContactEmail"),
          googleReviewUrl: t("createMemberForm.googleReviewUrl"),
          logo: t("createMemberForm.logo"),
          logoHelperText: t("createMemberForm.logoHelperText"),
          chamberPerksCategories: t("createMemberForm.chamberPerksCategories"),
          categoriesHelperText: t("createMemberForm.categoriesHelperText"),
          idaHelperText: t("createMemberForm.idaHelperText"),
          idaDescriptionRegenerateHelper: t("createMemberForm.idaDescriptionRegenerateHelper"),
          idaRegenerate: t("createMemberForm.idaRegenerate"),
          idaCompanyDescriptionLabel: t("createMemberForm.idaCompanyDescriptionLabel"),
          categoryDescriptionHelper: t("createMemberForm.categoryDescriptionHelper"),
          extensionNo: t("createMemberForm.extensionNo"),
          extensionPlaceholder: t("createMemberForm.extensionPlaceholder"),
          doNotIncludeInDirectory: t("createMemberForm.doNotIncludeInDirectory"),
          companyAddress: t("createMemberForm.companyAddress"),
          companyAddressTitle: t("createMemberForm.companyAddressTitle"),
          companyAddressDesc: t("createMemberForm.companyAddressDesc"),
          hideAddressCheckbox: t("createMemberForm.hideAddressCheckbox"),
          streetAddress: t("createMemberForm.streetAddress"),
          provinceState: t("createMemberForm.provinceState"),
          city: t("createMemberForm.city"),
          postalZipCode: t("createMemberForm.postalZipCode"),
          linksToSocials: t("createMemberForm.linksToSocials"),
          socialsDesc: t("createMemberForm.socialsDesc"),
          facebookUrl: t("createMemberForm.facebookUrl"),
          instagramUrl: t("createMemberForm.instagramUrl"),
          threadsUrl: t("createMemberForm.threadsUrl"),
          linkedinUrl: t("createMemberForm.linkedinUrl"),
          xTwitterUrl: t("createMemberForm.xTwitterUrl"),
          tiktokUrl: t("createMemberForm.tiktokUrl"),
          youtubeUrl: t("createMemberForm.youtubeUrl"),
          companyPrimaryContact: t("createMemberForm.companyPrimaryContact"),
          primaryContactDesc: t("createMemberForm.primaryContactDesc"),
          primaryContactDescLine1Start: t("createMemberForm.primaryContactDescLine1Start"),
          primaryContactDescLine1Bold: t("createMemberForm.primaryContactDescLine1Bold"),
          primaryContactDescLine2Start: t("createMemberForm.primaryContactDescLine2Start"),
          primaryContactDescLine2Bold: t("createMemberForm.primaryContactDescLine2Bold"),
          primaryContactDescLine2End: t("createMemberForm.primaryContactDescLine2End"),
          primaryContactName: t("createMemberForm.primaryContactName"),
          title: t("createMemberForm.title"),
          primaryContactEmail: t("createMemberForm.primaryContactEmail"),
          emailUsernameNote: t("createMemberForm.emailUsernameNote"),
          primaryContactPhone: t("createMemberForm.primaryContactPhone"),
          chamberPerksAdminContact: t("createMemberForm.chamberPerksAdminContact"),
          adminContactAutoAssignDesc: t("createMemberForm.adminContactAutoAssignDesc"),
          adminContactDesc: t("createMemberForm.adminContactDesc"),
          adminContactDescStart: t("createMemberForm.adminContactDescStart"),
          adminContactDescBold: t("createMemberForm.adminContactDescBold"),
          adminContactNotAppear: t("createMemberForm.adminContactNotAppear"),
          sameAsPrimaryContact: t("createMemberForm.sameAsPrimaryContact"),
          contactName: t("createMemberForm.contactName"),
          contactEmail: t("createMemberForm.contactEmail"),
          adminEmailNote: t("createMemberForm.adminEmailNote"),
          contactPhone: t("createMemberForm.contactPhone"),
          employeeAdoptionContact: t("createMemberForm.employeeAdoptionContact"),
          employeeAdoptionDesc: t("createMemberForm.employeeAdoptionDesc"),
          employeeAdoptionNotAppear: t("createMemberForm.employeeAdoptionNotAppear"),
          employeeAdoptionDescEdit: t("createMemberForm.employeeAdoptionDescEdit"),
          employeeContactName: t("createMemberForm.employeeContactName"),
          employeeContactEmail: t("createMemberForm.employeeContactEmail"),
          employeeContactPhone: t("createMemberForm.employeeContactPhone"),
          sameAsCompanyContact: t("createMemberForm.sameAsCompanyContact"),
          membershipStatusTitle: t("createMemberForm.membershipStatusTitle"),
          featuredMemberBannersTitle: t("createMemberForm.featuredMemberBannersTitle"),
          featuredMemberBannersDesc: t("createMemberForm.featuredMemberBannersDesc"),
          featuredMemberNotUpgraded: t("createMemberForm.featuredMemberNotUpgraded"),
          uploadBannerLabel: t("createMemberForm.uploadBannerLabel"),
          bannerLabel: t("createMemberForm.bannerLabel"),
          bannerPreviewFooter: t("createMemberForm.bannerPreviewFooter"),
          previewBannersButton: t("createMemberForm.previewBannersButton"),
          contactChamberTitle: t("createMemberForm.contactChamberTitle"),
          contactChamberDesc: t("createMemberForm.contactChamberDesc"),
          contactChamberSendRequest: t("createMemberForm.contactChamberSendRequest"),
          contactChamberRequestSent: t("createMemberForm.contactChamberRequestSent"),
          contactChamberEmailSubject: t("createMemberForm.contactChamberEmailSubject"),
          contactChamberEmailBody: t("createMemberForm.contactChamberEmailBody"),
          notUpgradedBold: t("createMemberForm.notUpgradedBold"),
          clickHereLink: t("createMemberForm.clickHereLink"),
          memberProfileBanners: t("createMemberForm.memberProfileBanners"),
          bannerUploadDesc: t("createMemberForm.bannerUploadDesc"),
          updateMember: t("createMemberForm.updateMember"),
          deleteMember: t("createMemberForm.deleteMember"),
          noMemberSelected: t("createMemberForm.noMemberSelected"),
          backToMembersList: t("createMemberForm.backToMembersList"),
          memberUpdatedSuccess: t("createMemberForm.memberUpdatedSuccess"),
          memberDeletedSuccess: t("createMemberForm.memberDeletedSuccess"),
          failedToUpdateMember: t("createMemberForm.failedToUpdateMember"),
          failedToDeleteMember: t("createMemberForm.failedToDeleteMember"),
          failedToResetPassword: t("createMemberForm.failedToResetPassword"),
          emailAlreadyInUse: t("createMemberForm.emailAlreadyInUse"),
          emailAlreadyInUseDesc: t("createMemberForm.emailAlreadyInUseDesc"),
          emailAlreadyInUseDesc2: t("createMemberForm.emailAlreadyInUseDesc2"),
          emailAlreadyInUseDesc3: t("createMemberForm.emailAlreadyInUseDesc3"),
          useAlternative: t("createMemberForm.useAlternative"),
          ok: t("createMemberForm.ok"),
          characters: t("createMemberForm.characters"),
          primaryContactTitle: t("createMemberForm.primaryContactTitle"),
          primaryContactPhoneTooltip: t("createMemberForm.primaryContactPhoneTooltip"),
          contactTitleTooltip: t("createMemberForm.contactTitleTooltip"),
          contactEmailTooltip: t("createMemberForm.contactEmailTooltip"),
          contactPhoneTooltip: t("createMemberForm.contactPhoneTooltip"),
          contactNameTooltip: t("createMemberForm.contactNameTooltip"),
          companyProfileInformation: t("createMemberForm.companyProfileInformation"),
          companyProfileInfoDescMember: t("createMemberForm.companyProfileInfoDescMember"),
          companyDescriptionHelper: t("createMemberForm.companyDescriptionHelper"),
          companyAddressDescMember: t("createMemberForm.companyAddressDescMember"),
          socialsDescMember: t("createMemberForm.socialsDescMember"),
          socialsDescMember2: t("createMemberForm.socialsDescMember2"),
          adminContactDescMember: t("createMemberForm.adminContactDescMember"),
          employeeAdoptionDescMember: t("createMemberForm.employeeAdoptionDescMember"),
          adminContactImportantTitle: t("createMemberForm.adminContactImportantTitle"),
          adminContactImportantDesc: t("createMemberForm.adminContactImportantDesc"),
          mainCompanyContactName: t("createMemberForm.mainCompanyContactName"),
          mainCompanyContactEmail: t("createMemberForm.mainCompanyContactEmail"),
          placeholders: {
            abcPlumbing: t("createMemberForm.placeholders.abcPlumbing"),
            mainContactNamePlaceholder: t("createMemberForm.placeholders.mainContactNamePlaceholder"),
            enterCompanyContactName: t("createMemberForm.placeholders.enterCompanyContactName"),
            enterCompanyDescription: t("createMemberForm.placeholders.enterCompanyDescription"),
            enterCompanyContactPhone: t("createMemberForm.placeholders.enterCompanyContactPhone"),
            enterCompanyWebsite: t("createMemberForm.placeholders.enterCompanyWebsite"),
            enterCompanyContactEmail: t("createMemberForm.placeholders.enterCompanyContactEmail"),
            enterGoogleReviewUrl: t("createMemberForm.placeholders.enterGoogleReviewUrl"),
            enterStreetAddress: t("createMemberForm.placeholders.enterStreetAddress"),
            enterCity: t("createMemberForm.placeholders.enterCity"),
            enterPostalCode: t("createMemberForm.placeholders.enterPostalCode"),
            enterFacebookUrl: t("createMemberForm.placeholders.enterFacebookUrl"),
            enterInstagramUrl: t("createMemberForm.placeholders.enterInstagramUrl"),
            enterThreadsUrl: t("createMemberForm.placeholders.enterThreadsUrl"),
            enterLinkedinUrl: t("createMemberForm.placeholders.enterLinkedinUrl"),
            enterXTwitterUrl: t("createMemberForm.placeholders.enterXTwitterUrl"),
            enterTiktokUrl: t("createMemberForm.placeholders.enterTiktokUrl"),
            enterYoutubeUrl: t("createMemberForm.placeholders.enterYoutubeUrl"),
            enterPrimaryContactName: t("createMemberForm.placeholders.enterPrimaryContactName"),
            enterContactTitle: t("createMemberForm.placeholders.enterContactTitle"),
            enterPrimaryContactEmail: t("createMemberForm.placeholders.enterPrimaryContactEmail"),
            enterPrimaryContactPhone: t("createMemberForm.placeholders.enterPrimaryContactPhone"),
            enterContactName: t("createMemberForm.placeholders.enterContactName"),
            enterContactEmail: t("createMemberForm.placeholders.enterContactEmail"),
            enterContactPhone: t("createMemberForm.placeholders.enterContactPhone"),
            enterEmployeeContactName: t("createMemberForm.placeholders.enterEmployeeContactName"),
            enterEmployeeContactEmail: t("createMemberForm.placeholders.enterEmployeeContactEmail"),
            enterEmployeeContactPhone: t("createMemberForm.placeholders.enterEmployeeContactPhone"),
          },
          successModalTitle: t("createMemberForm.successModalTitle"),
          successModalDesc: t("createMemberForm.successModalDesc"),
          successModalManualTitle: t("createMemberForm.successModalManualTitle"),
          successModalManualDesc: t("createMemberForm.successModalManualDesc"),
          successModalClickHere: t("createMemberForm.successModalClickHere"),
          successModalManualDesc2: t("createMemberForm.successModalManualDesc2"),
          successModalDownloadCredentials: t("createMemberForm.successModalDownloadCredentials"),
          successModalAutoTitle: t("createMemberForm.successModalAutoTitle"),
          successModalAutoDesc: t("createMemberForm.successModalAutoDesc"),
          successModalSendEmail: t("createMemberForm.successModalSendEmail"),
          successModalPreviewEmail: t("createMemberForm.successModalPreviewEmail"),
          credentialsDownloadedTitle: t("createMemberForm.credentialsDownloadedTitle"),
          credentialsDownloadedDesc: t("createMemberForm.credentialsDownloadedDesc"),
          credentialsDownloadedButton: t("createMemberForm.credentialsDownloadedButton"),
          emailSentTitle: t("createMemberForm.emailSentTitle"),
          previewEmailTitle: t("createMemberForm.previewEmailTitle"),
          createMemberButton: t("createMemberForm.createMemberButton"),
        },
        editMemberForm: {
          updateMemberTitle: t("editMemberForm.updateMemberTitle"),
          updateMemberDesc: t("editMemberForm.updateMemberDesc"),
          updateMemberButton: t("editMemberForm.updateMemberButton"),
          memberUpdatedSuccess: t("editMemberForm.memberUpdatedSuccess"),
          emailAlreadyInUse: t("editMemberForm.emailAlreadyInUse"),
          emailAlreadyInUseDesc: t("editMemberForm.emailAlreadyInUseDesc"),
          emailAlreadyInUseDesc2: t("editMemberForm.emailAlreadyInUseDesc2"),
          emailAlreadyInUseDesc3: t("editMemberForm.emailAlreadyInUseDesc3"),
          emailAlreadyInUseUseAlt: t("editMemberForm.emailAlreadyInUseUseAlt"),
          useAlternative: t("editMemberForm.useAlternative"),
          back: t("editMemberForm.back"),
          updateFailed: t("editMemberForm.updateFailed"),
          updateFailedDesc: t("editMemberForm.updateFailedDesc"),
          featuredMemberActivatedTitle: t("editMemberForm.featuredMemberActivatedTitle"),
          featuredMemberNotActivatedTitle: t("editMemberForm.featuredMemberNotActivatedTitle"),
          featuredMemberNotActivatedDesc: t("editMemberForm.featuredMemberNotActivatedDesc"),
          perkBannersActivatedTitle: t("editMemberForm.perkBannersActivatedTitle"),
          perkBannersNotActivatedTitle: t("editMemberForm.perkBannersNotActivatedTitle"),
          perkBannersNotActivatedDesc: t("editMemberForm.perkBannersNotActivatedDesc"),
          featuredMemberUpgradedDesc: t("editMemberForm.featuredMemberUpgradedDesc"),
          featuredMemberNotUpgradedDesc: t("editMemberForm.featuredMemberNotUpgradedDesc"),
          bannerLabel: t("editMemberForm.bannerLabel"),
          watchVideo: t("editMemberForm.watchVideo"),
          uploadBanner: t("editMemberForm.uploadBanner"),
          bannerPreviewFooter: t("editMemberForm.bannerPreviewFooter"),
          bannerPreviewFooterCreate: t("editMemberForm.bannerPreviewFooterCreate"),
          primaryContactDesc1: t("editMemberForm.primaryContactDesc1"),
          primaryContactDesc2: t("editMemberForm.primaryContactDesc2"),
          perkBannersNotActivatedIntro: t("editMemberForm.perkBannersNotActivatedIntro"),
          resetPasswordPrompt: t("editMemberForm.resetPasswordPrompt"),
        },
        toastMessages: {
          profileBannersUpdated: t("toastMessages.profileBannersUpdated"),
          failedToUpdateProfileBanners: t("toastMessages.failedToUpdateProfileBanners"),
          perkBannersUpdated: t("toastMessages.perkBannersUpdated"),
          failedToUpdatePerkBanners: t("toastMessages.failedToUpdatePerkBanners"),
          memberActivatedSuccess: t("toastMessages.memberActivatedSuccess"),
          failedToActivateMember: t("toastMessages.failedToActivateMember"),
          selectMemberAndDate: t("toastMessages.selectMemberAndDate"),
          memberUpdatedSuccess: t("toastMessages.memberUpdatedSuccess"),
          failedToUpdateMember: t("toastMessages.failedToUpdateMember"),
          memberDeletedSuccess: t("toastMessages.memberDeletedSuccess"),
          failedToDeleteMember: t("toastMessages.failedToDeleteMember"),
          passwordResetSuccess: t("toastMessages.passwordResetSuccess"),
          failedToResetPassword: t("toastMessages.failedToResetPassword"),
          profileUpdatedSuccess: t("toastMessages.profileUpdatedSuccess"),
          failedToUpdateProfile: t("toastMessages.failedToUpdateProfile"),
          unableToUpdateMember: t("toastMessages.unableToUpdateMember"),
          passwordCopied: t("toastMessages.passwordCopied"),
          memberSuspendedSuccess: t("toastMessages.memberSuspendedSuccess"),
          memberAccountActivatedSuccess: t("toastMessages.memberAccountActivatedSuccess"),
          failedToUpdateMembershipStatus: t("toastMessages.failedToUpdateMembershipStatus"),
          missingMemberInfo: t("toastMessages.missingMemberInfo"),
          failedToSendEmail: t("toastMessages.failedToSendEmail"),
          failedToGeneratePassword: t("toastMessages.failedToGeneratePassword"),
          credentialsDownloaded: t("toastMessages.credentialsDownloaded"),
          memberPerkCreatedSuccess: t("toastMessages.memberPerkCreatedSuccess"),
          memberPerkUpdatedSuccess: t("toastMessages.memberPerkUpdatedSuccess"),
          idaGeneratedSuccess: t("toastMessages.idaGeneratedSuccess"),
          memberPerkDeletedSuccess: t("toastMessages.memberPerkDeletedSuccess"),
        },
        dialogs: {
          resetPassword: t("dialogs.resetPassword"),
          resetPasswordDesc: t("dialogs.resetPasswordDesc"),
          resetDashboardPassword: t("dialogs.resetDashboardPassword"),
          resetDashboardPasswordDesc: t("dialogs.resetDashboardPasswordDesc"),
          areYouSureContinue: t("dialogs.areYouSureContinue"),
          yes: t("dialogs.yes"),
          no: t("dialogs.no"),
          deleteMember: t("dialogs.deleteMember"),
          areYouSureDelete: t("dialogs.areYouSureDelete"),
          areYouSureDeleteMember: t("dialogs.areYouSureDeleteMember"),
          cannotUndo: t("dialogs.cannotUndo"),
          delete: t("dialogs.delete"),
          cancel: t("dialogs.cancel"),
          close: t("dialogs.close"),
          newPasswordGenerated: t("dialogs.newPasswordGenerated"),
          newPasswordFor: t("dialogs.newPasswordFor"),
          is: t("dialogs.is"),
          savePasswordSecurely: t("dialogs.savePasswordSecurely"),
          copyPassword: t("dialogs.copyPassword"),
          bannerPreview: t("dialogs.bannerPreview"),
          videoPreview: t("dialogs.videoPreview"),
          downloadCredentials: t("dialogs.downloadCredentials"),
          sendEmail: t("dialogs.sendEmail"),
          previewEmail: t("dialogs.previewEmail"),
          editAdministratorContact: t("dialogs.editAdministratorContact"),
          sendLoginCredentialsTitle: t("dialogs.sendLoginCredentialsTitle"),
          sendLoginCredentialsDesc: t("dialogs.sendLoginCredentialsDesc"),
          deleteLogo: t("dialogs.deleteLogo"),
          areYouSureDeleteLogo: t("dialogs.areYouSureDeleteLogo"),
        },
        idaGenerate: {
          speechBubble: t("idaGenerate.speechBubble"),
          title: t("idaGenerate.title"),
          subtitle: t("idaGenerate.subtitle"),
          subtitleLine2: t("idaGenerate.subtitleLine2"),
          placeholder: t("idaGenerate.placeholder"),
          generateButton: t("idaGenerate.generateButton"),
          overwriteTitle: t("idaGenerate.overwriteTitle"),
          overwriteDesc: t("idaGenerate.overwriteDesc"),
          generatedLabel: t("idaGenerate.generatedLabel"),
          reGenerateButton: t("idaGenerate.reGenerateButton"),
          overwriteButton: t("idaGenerate.overwriteButton"),
          successTitle: t("idaGenerate.successTitle"),
          successTitle2: t("idaGenerate.successTitle2"),
          generating: t("idaGenerate.generating"),
          chamberDescriptionHelper: t("idaGenerate.chamberDescriptionHelper"),
          chamberDescriptionRegenerateHelper: t("idaGenerate.chamberDescriptionRegenerateHelper"),
          idaChamberDescriptionLabel: t("idaGenerate.idaChamberDescriptionLabel"),
          idaRegenerate: t("idaGenerate.idaRegenerate"),
          chamberOverwriteTitle: t("idaGenerate.chamberOverwriteTitle"),
          chamberOverwriteDesc: t("idaGenerate.chamberOverwriteDesc"),
          chamberGeneratedLabel: t("idaGenerate.chamberGeneratedLabel"),
          chamberSuccessTitle: t("idaGenerate.chamberSuccessTitle"),
          companyDescriptionLabel: t("idaGenerate.companyDescriptionLabel"),
          companyDescriptionSubLabel: t("idaGenerate.companyDescriptionSubLabel"),
        },
        manageCompanyProfile: {
          confirmUpdateTitle: t("manageCompanyProfile.confirmUpdateTitle"),
          confirmUpdateDescription: t("manageCompanyProfile.confirmUpdateDescription"),
          iAcceptButton: t("manageCompanyProfile.iAcceptButton"),
          updateSuccessTitle: t("manageCompanyProfile.updateSuccessTitle"),
          updateSuccessDescription: t("manageCompanyProfile.updateSuccessDescription"),
          credentialsUpdatedLogout: t("manageCompanyProfile.credentialsUpdatedLogout"),
          checkSpamFolder: t("manageCompanyProfile.checkSpamFolder"),
        },
        filters: {
          filterByCategory: t("filters.filterByCategory"),
          filterByStatus: t("filters.filterByStatus"),
          filterByActivationStatus: t("filters.filterByActivationStatus"),
          all: t("filters.all"),
          active: t("filters.active"),
          inactive: t("filters.inactive"),
          searchByMemberName: t("filters.searchByMemberName"),
          clearFilter: t("filters.clearFilter"),
          clearCategory: t("filters.clearCategory"),
          clearStatus: t("filters.clearStatus"),
        },
        bannerUpload: {
          uploadImage: t("bannerUpload.uploadImage"),
          imageLink: t("bannerUpload.imageLink"),
          videoLinkPlaceholder: t("bannerUpload.videoLinkPlaceholder"),
          uploadBanner: t("bannerUpload.uploadBanner"),
          previewBanners: t("bannerUpload.previewBanners"),
          imageTab: t("bannerUpload.imageTab"),
          videoTab: t("bannerUpload.videoTab"),
          deleteVideoWarning: t("bannerUpload.deleteVideoWarning"),
          deleteImageWarning: t("bannerUpload.deleteImageWarning"),
          whereRedirect: t("bannerUpload.whereRedirect"),
          changeImage: t("bannerUpload.changeImage"),
          deleteImage: t("bannerUpload.deleteImage"),
          enterVideoLink: t("bannerUpload.enterVideoLink"),
          watchVideo: t("bannerUpload.watchVideo"),
          saveBanner: t("bannerUpload.saveBanner"),
          footerText: t("bannerUpload.footerText"),
          deleteBannerTitle: t("bannerUpload.deleteBannerTitle"),
          deleteBannerConfirm: t("bannerUpload.deleteBannerConfirm"),
          deleteBannerDescription: t("bannerUpload.deleteBannerDescription"),
          bannerDeletedSuccessfully: t("bannerUpload.bannerDeletedSuccessfully"),
          delete: t("bannerUpload.delete"),
          uploadImageBanner: t("bannerUpload.uploadImageBanner"),
          uploadVideoBanner: t("bannerUpload.uploadVideoBanner"),
          qualityRecommendation: t("bannerUpload.qualityRecommendation"),
        },
        memberProfileBanners: {
          aspectRatioText: t("memberProfileBanners.aspectRatioText"),
          recommendedSizeSmall: t("memberProfileBanners.recommendedSizeSmall"),
          recommendedSizeLarge: t("memberProfileBanners.recommendedSizeLarge"),
          bannerPosition: t("memberProfileBanners.bannerPosition"),
          uploadPrompt: t("memberProfileBanners.uploadPrompt"),
        },
      };
    },
    [t]
  );
};

export default enumStrings;
